import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Button, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import axios from 'axios';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import WonderTradeLoader from '../Shared/WonderTradeLoader';
import apiService from '../../services/api';
import { useApp } from '../../contexts/AppContext';
import './ComercialCalendar.css';
import './ComercialCalendarMobile.css';
import './ComercialManagement.css';
import RequestCard from './RequestCard';
import { getClaimForPropostaId } from '../../utils/proposalClaimLookup';
import { canViewComercialTeamHierarchy, isComercialGestaoHierarchyRole } from '../../utils/comercialGestaoRoles';

const { Text } = Typography;

type ViewMode = 'daily' | 'weekly' | 'monthly';


export interface CalendarEvent {
  id: string;
  title: string;
  time: string;
  duration: string;
  type: 'call' | 'meeting' | 'training' | 'break' | 'marcacao' | 'fecho' | 'bloqueio' | 'rota' | 'other';
  description?: string;
  attendees?: string[];
  location?: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled' | 'blocked';
  date: Date;
  product?: 'bairro-solar' | 'pap' | 'gas' | 'eletricidade' | 'bloqueio';
  formData?: any; // Complete form data from Nova Marcação modal
}

interface ComercialUser {
  id: string;
  name: string;
  color: string;
  visible: boolean;
  especialidade?: string[]; // Array of specialties: ['bairro-solar', 'pap', 'gas', 'eletricidade']
}

interface StatusEvent {
  id: string;
  userId: string;
  date: Date;
  text: string; // Free text for scenario content (matches CallCenterCalendar)
}

export interface ComercialCalendarModalOnlyProps {
  /** When true, only the event detail modal is rendered (used by Gestão das Marcações) */
  modalOnly?: boolean;
  /** Event to show in the modal (must have formData, title, time, duration, date, etc.) */
  initialEvent?: CalendarEvent | null;
  /** Called when the user closes the modal */
  onCloseModal?: () => void;
}

type LaidOutCalendarEvent = { event: CalendarEvent; col: number; cols: number };

/** Parse HH:MM[/SS] into minutes from midnight. */
function parseEventMinutes(value: unknown): number | null {
  if (value == null) return null;
  const parts = String(value).trim().split(':');
  if (parts.length < 2) return null;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return hour * 60 + minute;
}

function getEventRangeMinutes(event: CalendarEvent): { start: number; end: number } | null {
  const start = parseEventMinutes(event.formData?.horaInicio ?? event.time);
  const end = parseEventMinutes(event.formData?.horaFim ?? event.duration);
  if (start == null || end == null || end <= start) return null;
  return { start, end };
}

/**
 * Split overlapping timed events into side-by-side columns (same algorithm as GRH calendar).
 * Events without a valid time range stay full-width (col=0, cols=1).
 */
function layoutCalendarEventsInDay(events: CalendarEvent[]): LaidOutCalendarEvent[] {
  const timed: Array<{ event: CalendarEvent; start: number; end: number }> = [];
  const untimed: LaidOutCalendarEvent[] = [];

  events.forEach((event) => {
    const range = getEventRangeMinutes(event);
    if (!range) {
      untimed.push({ event, col: 0, cols: 1 });
      return;
    }
    timed.push({ event, start: range.start, end: range.end });
  });

  timed.sort((a, b) => a.start - b.start || a.end - b.end);

  const clusters: Array<typeof timed> = [];
  let current: typeof timed = [];
  let currentEnd = 0;

  timed.forEach((item) => {
    if (current.length && item.start >= currentEnd) {
      clusters.push(current);
      current = [];
      currentEnd = 0;
    }
    current.push(item);
    currentEnd = Math.max(currentEnd, item.end);
  });
  if (current.length) clusters.push(current);

  const result: LaidOutCalendarEvent[] = [...untimed];
  clusters.forEach((cluster) => {
    const columnEnds: number[] = [];
    const assignments = new Map<string, number>();
    cluster.forEach((item) => {
      let col = columnEnds.findIndex((endTime) => endTime <= item.start);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(item.end);
      } else {
        columnEnds[col] = item.end;
      }
      assignments.set(item.event.id, col);
    });
    const cols = columnEnds.length;
    cluster.forEach((item) => {
      result.push({ event: item.event, col: assignments.get(item.event.id) ?? 0, cols });
    });
  });

  return result;
}

const ComercialCalendar: React.FC<ComercialCalendarModalOnlyProps> = ({ modalOnly = false, initialEvent = null, onCloseModal }) => {
  const { refreshCalendarData, onCalendarDataRefresh } = useApp();
  
  // Calculate Monday of current week (for weekly view - work days)
  const getMondayOfCurrentWeek = () => {
    // Get current date/time
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate Monday of current week
    // If Sunday (0), go back 6 days to get Monday
    // If Monday (1), go back 0 days (it's already Monday)
    // etc.
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysToSubtract);
    monday.setHours(0, 0, 0, 0);
    
    return monday;
  };

  const [viewMode, setViewMode] = useState<ViewMode>('weekly');
  const [displayMode, setDisplayMode] = useState<'calendar' | 'table'>('calendar');
  const [currentDate, setCurrentDate] = useState(getMondayOfCurrentWeek());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedStatusEvent, setSelectedStatusEvent] = useState<StatusEvent | null>(null);
  const [showStatusEventDetailModal, setShowStatusEventDetailModal] = useState(false);
  const [selectedRotaEvent, setSelectedRotaEvent] = useState<any>(null);
  const [showRotaEventDetailModal, setShowRotaEventDetailModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Touch-capable devices (iPad/tablets) should behave like mobile layouts.
  const [isMobileOrTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    const hasTouch =
      (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0)
      || 'ontouchstart' in window;
    // iPads/tablets can be wider than 768px, so allow a larger cutoff.
    const isTabletWidth = window.innerWidth <= 1100;
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
    return hasTouch && (isTabletWidth || isSmallScreen);
  });
  const [showStatusEventModal, setShowStatusEventModal] = useState(false);
  const [selectedUserForStatus, setSelectedUserForStatus] = useState<ComercialUser | null>(null);
  const [selectedUser, setSelectedUser] = useState<ComercialUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null); // Only set when Procurar is clicked
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [scenariosMode, setScenariosMode] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<Date[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEspecialidadeModal, setShowEspecialidadeModal] = useState(false);
  const [selectedUserForEspecialidade, setSelectedUserForEspecialidade] = useState<ComercialUser | null>(null);
  const [selectedEspecialidades, setSelectedEspecialidades] = useState<string[]>([]);
  const [showMarcacaoModal, setShowMarcacaoModal] = useState(false);
  const [currentMarcacaoStep, setCurrentMarcacaoStep] = useState(1);
  const [marcacaoFormData, setMarcacaoFormData] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [marcacaoNotification, setMarcacaoNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [savingMarcacao, setSavingMarcacao] = useState(false);
  const [isCallCenterInfoExpanded, setIsCallCenterInfoExpanded] = useState(true);
  // Marcação detail modal state
  const [selectedMarcacaoEvent, setSelectedMarcacaoEvent] = useState<CalendarEvent | null>(null);
  const [showMarcacaoEventDetailModal, setShowMarcacaoEventDetailModal] = useState(false);
  const [isCallCenterInfoCollapsed, setIsCallCenterInfoCollapsed] = useState(false);
  const [isComercialCurrentCollapsed, setIsComercialCurrentCollapsed] = useState(false);
  const [isRescheduleReasonCollapsed, setIsRescheduleReasonCollapsed] = useState(false);
  const [isNotOkCallCenterFeedbackCollapsed, setIsNotOkCallCenterFeedbackCollapsed] = useState(false);
  // Marcação edit mode state
  const [isEditingMarcacao, setIsEditingMarcacao] = useState(false);
  const [editMarcacaoFormData, setEditMarcacaoFormData] = useState<any>({});
  const [editMarcacaoValidationErrors, setEditMarcacaoValidationErrors] = useState<any>({});
  const [editMarcacaoNotification, setEditMarcacaoNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Marcação delete state
  const [showMarcacaoDeleteConfirm, setShowMarcacaoDeleteConfirm] = useState(false);
  const [deletingMarcacao, setDeletingMarcacao] = useState(false);
  // Ver Propostas (Solicitar Propostas) modal - proposals associated with the marcação
  const [showPropostasMarcacaoModal, setShowPropostasMarcacaoModal] = useState(false);
  // When modalOnly + initialEvent (from Gestão), open the exact same modal
  useEffect(() => {
    if (modalOnly && initialEvent) {
      setSelectedMarcacaoEvent(initialEvent);
      setShowMarcacaoEventDetailModal(true);
    }
  }, [modalOnly, initialEvent]);

  // Mobile only: scroll weekly view to keep current day in view when opening
  useEffect(() => {
    if (viewMode !== 'weekly' || !weeklyScrollRef.current) return;
    // Treat tablets/iPads like "mobile" based on touch capabilities (not only width).
    const isMobile = window.matchMedia('(pointer: coarse) and (hover: none)').matches
      || window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return;
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    const todayIndex = dayOfWeek >= 1 && dayOfWeek <= 5 ? dayOfWeek - 1 : 2; // Mon=0..Fri=4, else Wed=2
    const MOBILE_DAY_WIDTH = 165;
    const scrollToCurrentDay = () => {
      const el = weeklyScrollRef.current;
      if (!el || el.clientWidth === 0) return;
      const containerWidth = el.clientWidth;
      const scrollToCenter = todayIndex * MOBILE_DAY_WIDTH - containerWidth / 2 + MOBILE_DAY_WIDTH / 2;
      el.scrollLeft = Math.max(0, Math.min(scrollToCenter, el.scrollWidth - containerWidth));
    };
    requestAnimationFrame(() => {
      scrollToCurrentDay();
      setTimeout(scrollToCurrentDay, 100); // Retry after layout (e.g. when switching to weekly)
    });
  }, [viewMode, currentDate]);

  // Fecho: create from Marcação
  const [showFechoModal, setShowFechoModal] = useState(false);
  const [currentFechoStep, setCurrentFechoStep] = useState(1);
  const [fechoFormData, setFechoFormData] = useState<any>({});
  const [fechoValidationErrors, setFechoValidationErrors] = useState<any>({});
  const [fechoNotification, setFechoNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [savingFecho, setSavingFecho] = useState(false);
  const [selectedMarcacaoForFecho, setSelectedMarcacaoForFecho] = useState<CalendarEvent | null>(null);
  // Fecho detail modal (view/edit/delete)
  const [selectedFechoEvent, setSelectedFechoEvent] = useState<CalendarEvent | null>(null);
  const [showFechoEventDetailModal, setShowFechoEventDetailModal] = useState(false);
  const [isEditingFecho, setIsEditingFecho] = useState(false);
  const [editFechoFormData, setEditFechoFormData] = useState<any>({});
  const [editFechoValidationErrors, setEditFechoValidationErrors] = useState<any>({});
  const [editFechoNotification, setEditFechoNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showFechoDeleteConfirm, setShowFechoDeleteConfirm] = useState(false);
  const [deletingFecho, setDeletingFecho] = useState(false);
  const [showRotaModal, setShowRotaModal] = useState(false);
  const [rotaFormData, setRotaFormData] = useState<any>({});
  const [showBloqueioModal, setShowBloqueioModal] = useState(false);
  const [bloqueioFormData, setBloqueioFormData] = useState<any>({});
  const [bloqueioValidationErrors, setBloqueioValidationErrors] = useState<any>({});
  const [rotaValidationErrors, setRotaValidationErrors] = useState<any>({});
  // Scenario edit state
  const [isEditingScenario, setIsEditingScenario] = useState(false);
  const [editScenarioDate, setEditScenarioDate] = useState<string>('');
  const [editScenarioText, setEditScenarioText] = useState<string>('');
  const [editScenarioAssignedTo, setEditScenarioAssignedTo] = useState<string[]>([]);
  const [savingScenario, setSavingScenario] = useState(false);
  const [scenarioNotification, setScenarioNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editScenarioValidationErrors, setEditScenarioValidationErrors] = useState<any>({});
  const [showScenarioDeleteConfirm, setShowScenarioDeleteConfirm] = useState(false);
  const [deletingScenario, setDeletingScenario] = useState(false);
  // Rota edit state
  const [isEditingRota, setIsEditingRota] = useState(false);
  const [editRotaDate, setEditRotaDate] = useState<string>('');
  const [editRotaComerciais, setEditRotaComerciais] = useState<string[]>([]);
  const [editRotaLinkGoogleMaps, setEditRotaLinkGoogleMaps] = useState<string>(''); // Legacy, kept for backward compatibility
  const [editRotaLinks, setEditRotaLinks] = useState<Array<{ url: string; label: string }>>([]);
  const [savingRota, setSavingRota] = useState(false);
  const [rotaNotification, setRotaNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editRotaValidationErrors, setEditRotaValidationErrors] = useState<any>({});
  const [showRotaDeleteConfirm, setShowRotaDeleteConfirm] = useState(false);
  const [deletingRota, setDeletingRota] = useState(false);
  const [fetchedEvents, setFetchedEvents] = useState<CalendarEvent[]>([]); // Events loaded from backend (activities + marcações)
  const [fetchedScenarioEvents, setFetchedScenarioEvents] = useState<StatusEvent[]>([]); // Scenario events loaded from backend
  const [isLoadingEvents, setIsLoadingEvents] = useState(false); // Loading state for events
  const weeklyScrollRef = useRef<HTMLDivElement>(null); // Mobile: scroll to current day in weekly view
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null); // Track when loading started
  const [bloqueioEvents, setBloqueioEvents] = useState<any[]>([
    // October 1, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio1',
      title: 'BLOQUEIO',
      time: '08:00',
      duration: '09:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['João Silva', 'Maria Santos', 'Ana Costa'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 1),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-001',
        marcacaoUsers: ['1', '2', '5'],
        marcacaoData: '01/10/2025',
        horaInicio: '08:00',
        horaFim: '09:00',
        marcacaoDescription: 'Período bloqueado - reunião matinal'
      }
    },
    // October 2, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio2',
      title: 'BLOQUEIO',
      time: '12:00',
      duration: '13:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['João Silva', 'Maria Santos', 'Sofia Lima', 'Carlos Mendes'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 2),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-002',
        marcacaoUsers: ['1', '2', '4', '6'],
        marcacaoData: '02/10/2025',
        horaInicio: '12:00',
        horaFim: '13:00',
        marcacaoDescription: 'Período bloqueado - pausa almoço'
      }
    },
    // October 3, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio3',
      title: 'BLOQUEIO',
      time: '17:00',
      duration: '18:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['João Silva', 'Pedro Alves', 'Ana Costa'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 3),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-003',
        marcacaoUsers: ['1', '3', '5'],
        marcacaoData: '03/10/2025',
        horaInicio: '17:00',
        horaFim: '18:00',
        marcacaoDescription: 'Período bloqueado - reunião final do dia'
      }
    },
    // October 4, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio4',
      title: 'BLOQUEIO',
      time: '10:00',
      duration: '11:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['Maria Santos', 'Sofia Lima', 'Carlos Mendes'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 4),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-004',
        marcacaoUsers: ['2', '4', '6'],
        marcacaoData: '04/10/2025',
        horaInicio: '10:00',
        horaFim: '11:00',
        marcacaoDescription: 'Período bloqueado - formação interna'
      }
    },
    // October 5, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio5',
      title: 'BLOQUEIO',
      time: '14:00',
      duration: '15:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['Pedro Alves', 'Ana Costa'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 5),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-005',
        marcacaoUsers: ['3', '5'],
        marcacaoData: '05/10/2025',
        horaInicio: '14:00',
        horaFim: '15:00',
        marcacaoDescription: 'Período bloqueado - reunião de equipa'
      }
    },
    // October 6, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio6',
      title: 'BLOQUEIO',
      time: '16:00',
      duration: '17:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['Sofia Lima', 'Carlos Mendes'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 6),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-006',
        marcacaoUsers: ['4', '6'],
        marcacaoData: '06/10/2025',
        horaInicio: '16:00',
        horaFim: '17:00',
        marcacaoDescription: 'Período bloqueado - análise de relatórios'
      }
    },
    // October 7, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio7',
      title: 'BLOQUEIO',
      time: '09:00',
      duration: '10:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['João Silva', 'Maria Santos'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 7),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-007',
        marcacaoUsers: ['1', '2'],
        marcacaoData: '07/10/2025',
        horaInicio: '09:00',
        horaFim: '10:00',
        marcacaoDescription: 'Período bloqueado - briefing matinal'
      }
    },
    // October 10, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio8',
      title: 'BLOQUEIO',
      time: '11:00',
      duration: '12:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['Pedro Alves', 'Sofia Lima'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 10),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-008',
        marcacaoUsers: ['3', '4'],
        marcacaoData: '10/10/2025',
        horaInicio: '11:00',
        horaFim: '12:00',
        marcacaoDescription: 'Período bloqueado - reunião de coordenação'
      }
    },
    // October 15, 2025 - Bloqueio for events on this day
    {
      id: 'bloqueio9',
      title: 'BLOQUEIO',
      time: '13:00',
      duration: '14:00',
      type: 'bloqueio',
      description: 'Período bloqueado para eventos',
      attendees: ['Ana Costa', 'Carlos Mendes'],
      location: 'Bloqueio de horário',
      status: 'blocked',
      date: new Date(2025, 9, 15),
      product: 'bloqueio',
      formData: {
        marcacaoId: 'BLOQ-009',
        marcacaoUsers: ['5', '6'],
        marcacaoData: '15/10/2025',
        horaInicio: '13:00',
        horaFim: '14:00',
        marcacaoDescription: 'Período bloqueado - avaliação de performance'
      }
    }
  ]);
  const [rotaEvents, setRotaEvents] = useState<any[]>([
    // October 1, 2025 - Rota for events on this day
    {
      id: 'rota1',
      date: new Date(2025, 9, 1),
      userIds: ['1', '2', '5'], // João Silva, Maria Santos, Ana Costa
      linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7',
      links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }],
      title: 'Rota - Lisboa/Coimbra',
      description: 'Rota para visitas em Lisboa e Coimbra'
    },
    // October 2, 2025 - Rota for events on this day
    {
      id: 'rota2',
      date: new Date(2025, 9, 2),
      userIds: ['1', '2', '4', '6'],
      linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7',
      links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }],
      title: 'Rota - Lisboa/Coimbra',
      description: 'Rota para visitas em Lisboa e Coimbra'
    },
    { id: 'rota3', date: new Date(2025, 9, 3), userIds: ['1', '3', '5'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Coimbra/Lisboa', description: 'Rota para visitas em Coimbra e Lisboa' },
    { id: 'rota4', date: new Date(2025, 9, 4), userIds: ['2', '4', '6'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Lisboa/Coimbra', description: 'Rota para visitas em Lisboa e Coimbra' },
    { id: 'rota5', date: new Date(2025, 9, 5), userIds: ['3', '5'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Coimbra/Lisboa', description: 'Rota para visitas em Coimbra e Lisboa' },
    { id: 'rota6', date: new Date(2025, 9, 6), userIds: ['4', '6'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Lisboa/Coimbra', description: 'Rota para visitas em Lisboa e Coimbra' },
    { id: 'rota7', date: new Date(2025, 9, 7), userIds: ['1', '2'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Aveiro/Viseu', description: 'Rota para visitas em Aveiro e Viseu' },
    { id: 'rota8', date: new Date(2025, 9, 10), userIds: ['3', '4'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Faro/Leiria', description: 'Rota para visitas em Faro e Leiria' },
    { id: 'rota9', date: new Date(2025, 9, 15), userIds: ['5', '6'], linkGoogleMaps: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', links: [{ url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Início da Rota' }, { url: 'https://maps.app.goo.gl/gaMzES5BkacGXt5V7', label: 'Continuação' }], title: 'Rota - Setúbal/Santarém', description: 'Rota para visitas em Setúbal e Santarém' }
  ]);

  // Search helpers
  const normalizedIncludes = (haystack: string | undefined, needle: string) => {
    if (!haystack) return false;
    return haystack.toLowerCase().includes(needle);
  };

  // Lisbon timezone conversion helper
  const getLisbonDateString = (date: Date) => {
    // Convert the date to Lisbon timezone and return as YYYY-MM-DD format
    const lisbonDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Lisbon"}));
    const year = lisbonDate.getFullYear();
    const month = String(lisbonDate.getMonth() + 1).padStart(2, '0');
    const day = String(lisbonDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Same visible range as marcações fetch — keeps bloqueios/rotas in sync when a week spans two months */
  const getFetchRangeForView = (anchor: Date, mode: ViewMode): { dateFrom: Date; dateTo: Date } => {
    const today = anchor || new Date();
    let dateFrom: Date;
    let dateTo: Date;
    if (mode === 'weekly') {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      dateFrom = new Date(startOfWeek);
      dateFrom.setDate(startOfWeek.getDate() - 7);
      dateTo = new Date(endOfWeek);
      dateTo.setDate(endOfWeek.getDate() + 7);
    } else if (mode === 'daily') {
      dateFrom = new Date(today);
      dateFrom.setDate(today.getDate() - 7);
      dateFrom.setHours(0, 0, 0, 0);
      dateTo = new Date(today);
      dateTo.setDate(today.getDate() + 7);
      dateTo.setHours(23, 59, 59, 999);
    } else {
      dateFrom = new Date(today.getFullYear(), today.getMonth(), 1);
      dateFrom.setDate(dateFrom.getDate() - 7);
      dateTo = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      dateTo.setDate(dateTo.getDate() + 7);
      dateTo.setHours(23, 59, 59, 999);
    }
    return { dateFrom, dateTo };
  };

  const getEventPostalCode = (event: CalendarEvent) => {
    const p1 = event.formData?.codigoPostal1 || '';
    const p2 = event.formData?.codigoPostal2 || '';
    if (!p1 && !p2) return '';
    return `${String(p1)}-${String(p2)}`;
  };

  const isEventMatch = (event: CalendarEvent, queryRaw: string) => {
    const q = queryRaw.trim().toLowerCase();
    if (!q) return false;
    const id = event.formData?.marcacaoId || '';
    const empresa = event.formData?.nomeEmpresa || '';
    const contacto = event.formData?.contactoCliente || '';
    const cp = getEventPostalCode(event);
    return (
      normalizedIncludes(String(id), q) ||
      normalizedIncludes(String(empresa), q) ||
      normalizedIncludes(String(contacto), q) ||
      normalizedIncludes(cp, q)
    );
  };

  // Get current user role from localStorage or context
  const [currentUserRole, setCurrentUserRole] = useState<string>('Comercial');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserData, setCurrentUserData] = useState<any>(null);
  
  // Helper function to get Monday of the week
  const getMondayOfWeek = (date: Date): Date => {
    const monday = new Date(date);
    const dayOfWeek = monday.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // If Sunday (0), go back 6 days to get Monday. If Monday (1), go back 0 days, etc.
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    monday.setDate(monday.getDate() - daysToSubtract);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const isAdminUser = () => {
    const role = String(currentUserData?.role || localStorage.getItem('userRole') || '').toLowerCase();
    const tenant = String(
      currentUserData?.tenant || localStorage.getItem('user_tenant') || ''
    ).toLowerCase();
    return role.includes('admin') || tenant === 'admin';
  };

  /** Admin, Gerente, Gestor, Assistente de Gestor, Instrutor — team calendar sidebar */
  const isAdminOrGerenteComercial = () => {
    try {
      if (isAdminUser()) return true;
      const tenant =
        currentUserData?.tenant ||
        localStorage.getItem('user_tenant') ||
        '';
      const role = currentUserData?.role || localStorage.getItem('userRole') || '';
      if (canViewComercialTeamHierarchy({ role, tenant, isAdmin: false })) {
        return true;
      }
      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        const user = JSON.parse(cognitoUserRaw);
        if (
          canViewComercialTeamHierarchy({
            role: user?.role || '',
            tenant: user?.tenant || '',
            isAdmin: false,
          })
        ) {
          return true;
        }
        if (Array.isArray(user?.UserAttributes)) {
          const attrs = user.UserAttributes.reduce((acc: any, a: any) => {
            acc[a.Name] = a.Value;
            return acc;
          }, {});
          if (
            canViewComercialTeamHierarchy({
              role: attrs['custom:role'] || '',
              tenant: attrs['custom:tenant'] || '',
              isAdmin: false,
            })
          ) {
            return true;
          }
        }
      }
    } catch (e) {
      // ignore
    }
    return false;
  };

  // Comercial users data
  const [comercialUsers, setComercialUsers] = useState<ComercialUser[]>([
    { id: '1', name: 'João Silva', color: '#3b82f6', visible: false },
    { id: '2', name: 'Maria Santos', color: '#10b981', visible: false },
    { id: '3', name: 'Pedro Alves', color: '#f59e0b', visible: false },
    { id: '4', name: 'Sofia Lima', color: '#8b5cf6', visible: false },
    { id: '5', name: 'Ana Costa', color: '#ef4444', visible: false },
    { id: '6', name: 'Carlos Mendes', color: '#06b6d4', visible: false }
  ]);

  // Fetch comercial users from backend
  useEffect(() => {
    const fetchComercialUsers = async () => {
      try {
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        if (!token) {
          // If no token, keep example users
          return;
        }

        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const url = `${backendUrl}/api/callcenter/comercial-users`;
        
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data && response.data.success && response.data.comercial_users) {
          // Map backend users to ComercialUser interface
          const backendUsers: ComercialUser[] = response.data.comercial_users.map((user: any) => {
            // Generate color based on user ID or use a default color
            const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#6366f1'];
            const colorIndex = (user.id || 0) % colors.length;
            
            // Parse especialidade - handle both string (SQLite JSON) and array
            let especialidade = user.especialidade || [];
            if (typeof especialidade === 'string') {
              try {
                especialidade = JSON.parse(especialidade);
              } catch (e) {
                especialidade = [];
              }
            }
            if (!Array.isArray(especialidade)) {
              especialidade = [];
            }
            
            return {
              id: String(user.id || ''),
              name: user.name || user.username || 'Unknown User',
              color: colors[colorIndex],
              visible: false,
              especialidade: especialidade
            };
          });

          // Sort alphabetically by name
          const sortedUsers = [...backendUsers].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'pt-PT', { sensitivity: 'base' })
          );

          setComercialUsers(sortedUsers);
        } else {
          // Check if response.data.comercial_users exists but is empty
          if (response.data && Array.isArray(response.data.comercial_users)) {
            setComercialUsers([]);
          }
        }
      } catch (error: any) {
        // On error, keep example users as fallback
      }
    };

    fetchComercialUsers();
  }, []);

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (user && user.id) {
          setCurrentUserId(String(user.id));
          
          // Also fetch team information from comercial users list
          try {
            const token = localStorage.getItem('jwt_token');
            if (token) {
              const response = await axios.get(`${DEPLOYMENT_CONFIG.getBackendUrl()}/api/callcenter/comercial-users`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (response.data && response.data.success && response.data.comercial_users) {
                const currentUserFromList = response.data.comercial_users.find((u: any) => u.id === user.id);
                if (currentUserFromList) {
                  setCurrentUserData({
                    ...user,
                    team: currentUserFromList.team || currentUserFromList.team_name || '',
                    team_name: currentUserFromList.team_name || currentUserFromList.team || '',
                    tenant: user.tenant || localStorage.getItem('user_tenant') || 'comercial',
                    role: user.role || currentUserFromList.role || '',
                  });
                } else {
                  setCurrentUserData({
                    ...user,
                    tenant: user.tenant || localStorage.getItem('user_tenant') || 'comercial',
                  });
                }
              } else {
                setCurrentUserData(user);
              }
            } else {
              setCurrentUserData(user);
            }
          } catch (e) {
            setCurrentUserData(user);
          }
        } else {
          setCurrentUserData(user);
        }
        
        // Get user role
        const userRole = localStorage.getItem('userRole') || user?.role || 'Comercial';
        setCurrentUserRole(userRole);
      } catch (error) {
        // Fallback to localStorage
        const userRole = localStorage.getItem('userRole') || 'Comercial';
        setCurrentUserRole(userRole);
        
        // Try to get user ID from localStorage
        try {
          const cognitoUserRaw = localStorage.getItem('cognito_user');
          if (cognitoUserRaw) {
            const cognitoUser = JSON.parse(cognitoUserRaw);
            if (cognitoUser?.id) {
              setCurrentUserId(String(cognitoUser.id));
            } else if (cognitoUser?.UserAttributes) {
              const attrs = cognitoUser.UserAttributes.reduce((acc: any, a: any) => { 
                acc[a.Name] = a.Value; 
                return acc; 
              }, {});
              if (attrs['custom:user_id']) {
                setCurrentUserId(String(attrs['custom:user_id']));
              }
            }
          }
        } catch (e) {
        }
      }
    };
    
    fetchCurrentUser();
  }, []);

  // Auto-select calendar: regular users → self; Gestão hierarchy → self on first load
  useEffect(() => {
    if (!currentUserId || comercialUsers.length === 0) return;
    const currentUser = comercialUsers.find((u) => String(u.id) === String(currentUserId));
    if (!currentUser) return;
    if (!isAdminOrGerenteComercial()) {
      setSelectedUser(currentUser);
      return;
    }
    if (!selectedUser) {
      setSelectedUser({ ...currentUser });
    }
  }, [comercialUsers, currentUserId, selectedUser]);

  // Sync edit values when selectedStatusEvent changes
  useEffect(() => {
    if (selectedStatusEvent && !isEditingScenario) {
      const scenarioDate = selectedStatusEvent.date;
      if (scenarioDate) {
        let dateStr: string;
        if (scenarioDate instanceof Date) {
          const year = scenarioDate.getFullYear();
          const month = String(scenarioDate.getMonth() + 1).padStart(2, '0');
          const day = String(scenarioDate.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        } else {
          dateStr = scenarioDate;
        }
        setEditScenarioDate(dateStr);
      }
      setEditScenarioText(selectedStatusEvent.text || '');
      // Get assigned users - check if there are multiple users linked
      const userId = selectedStatusEvent.userId;
      setEditScenarioAssignedTo(userId ? [userId] : []);
    }
  }, [selectedStatusEvent, isEditingScenario]);

  // Sync edit values when selectedRotaEvent changes
  useEffect(() => {
    if (selectedRotaEvent && !isEditingRota) {
      // Handle both sample data structure and backend data structure
      const rotaDate = selectedRotaEvent.date || (selectedRotaEvent.formData?.data ? new Date(selectedRotaEvent.formData.data) : null) || (selectedRotaEvent.data ? new Date(selectedRotaEvent.data) : null);
      
      if (rotaDate) {
        let dateStr: string;
        if (rotaDate instanceof Date) {
          const year = rotaDate.getFullYear();
          const month = String(rotaDate.getMonth() + 1).padStart(2, '0');
          const day = String(rotaDate.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        } else {
          dateStr = rotaDate;
        }
        setEditRotaDate(dateStr);
      }
      const userIds = selectedRotaEvent.userIds || selectedRotaEvent.formData?.comerciais || [];
      setEditRotaComerciais(userIds.map((id: string | number) => String(id)));
      const linkGoogleMapsVal = selectedRotaEvent.linkGoogleMaps || selectedRotaEvent.formData?.linkGoogleMaps || '';
      setEditRotaLinkGoogleMaps(linkGoogleMapsVal);
      const links = selectedRotaEvent.links || selectedRotaEvent.formData?.links || [];
      const labels = ['Início da Rota', 'Continuação'];
      const finalLinks = links.length > 0 ? links : (linkGoogleMapsVal ? [{ url: linkGoogleMapsVal, label: 'Google Maps' }] : []);
      const editLinks: Array<{ url: string; label: string }> = [0, 1].map((i) => finalLinks[i] || { url: '', label: labels[i] });
      setEditRotaLinks(editLinks);
    }
  }, [selectedRotaEvent, isEditingRota]);

  // Function to load bloqueio events from backend
  const loadBloqueioEvents = useCallback(async (): Promise<CalendarEvent[]> => {
    if (!selectedUser) return [];
    
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    try {
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = new URL(`${backendUrl}/api/callcenter/calendar/bloqueios`);
      url.searchParams.set('user_id', String(selectedUser.id));
      
      const today = currentDate || new Date();
      const { dateFrom, dateTo } = getFetchRangeForView(today, viewMode);
      url.searchParams.set('date_from', getLisbonDateString(dateFrom));
      url.searchParams.set('date_to', getLisbonDateString(dateTo));
      
      const res = await fetch(url.toString(), {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      
      if (!res.ok) {
        return [];
      }
      
      const json = await res.json();
      const bloqueios = Array.isArray(json?.bloqueios) ? json.bloqueios : [];
      
      // Map backend bloqueio events to CalendarEvent format (following example data structure)
      const mapped: CalendarEvent[] = bloqueios.map((b: any) => {
        // Parse date from ISO format (YYYY-MM-DD)
        const dateParts = b.date.split('-');
        const bloqueioDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        
        const horaInicio = b.hora_inicio || '09:00';
        const horaFim = b.hora_fim || '10:00';
        
        // Calculate duration string (matching example data format like "1h", "30min")
        const [startHour, startMin] = horaInicio.split(':').map(Number);
        const [endHour, endMin] = horaFim.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = Math.floor(durationMinutes / 60);
        const remainingMinutes = durationMinutes % 60;
        const durationStr = durationHours > 0 
          ? `${durationHours}h${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`
          : `${remainingMinutes}min`;
        
        // Get user IDs from form_data.marcacaoUsers (can be array of numbers or strings)
        const userIds = (b.form_data?.marcacaoUsers || []).map((uid: string | number) => String(uid));
        
        // Map user IDs to user names for attendees
        const attendees = userIds.map((uid: string) => 
          comercialUsers.find((u: ComercialUser) => u.id === uid)?.name || ''
        ).filter(Boolean);
        
        // Get description from backend or form_data
        const description = b.description || b.form_data?.marcacaoDescription || 'Período bloqueado para eventos';
        
        return {
          id: `bloqueio-${b.id}`,
          title: 'BLOQUEIO DE HORÁRIO',
          time: horaInicio,
          duration: durationStr,
          type: 'bloqueio' as const,
          description: description,
          attendees: attendees.length > 0 ? attendees : undefined,
          location: 'Bloqueio de horário',
          status: 'blocked' as const,
          date: bloqueioDate,
          product: 'bloqueio' as const,
          formData: {
            marcacaoId: b.bloqueio_id || `BLOQ-${b.id}`,
            marcacaoUsers: userIds,
            marcacaoData: b.form_data?.marcacaoData || b.date,
            horaInicio: horaInicio,
            horaFim: horaFim,
            marcacaoDescription: description,
            bloqueioEventId: String(b.id)
          }
        } as CalendarEvent;
      });
      
      return mapped;
    } catch (e) {
      return [];
    }
  }, [selectedUser, comercialUsers, currentDate, viewMode]);

  // Function to load marcação events from backend
  const loadMarcacaoEvents = useCallback(async (): Promise<CalendarEvent[]> => {
    // Load ALL marcações for the date range (not filtered by user_id)
    // This allows shared marcações (with multiple users) to appear in all relevant calendars
    // Filtering by selected user happens client-side in getEventsForDate
    
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    try {
      // Try comercial endpoint first, fallback to callcenter endpoint
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      // Ensure backendUrl doesn't have trailing slash
      const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
      
      const today = currentDate || new Date();
      const { dateFrom, dateTo } = getFetchRangeForView(today, viewMode);
      
      // Format dates as YYYY-MM-DD (without timezone issues)
      const formatDateForAPI = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Build URL with query parameters
      let url = `${baseUrl}/api/callcenter/calendar/marcacoes?date_from=${encodeURIComponent(formatDateForAPI(dateFrom))}&date_to=${encodeURIComponent(formatDateForAPI(dateTo))}`;
      
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      
      if (!res.ok) {
        return [];
      }

      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return [];
      }
      
      const json = await res.json();
      const marcacoes = Array.isArray(json?.marcacoes) ? json.marcacoes : [];
      
      // Map backend marcação events to CalendarEvent format
      const mapped: CalendarEvent[] = marcacoes.map((m: any) => {
        const dateParts = m.date.split('-');
        const marcacaoDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        const horaInicio = m.hora_inicio || '09:00';
        const horaFim = m.hora_fim || '10:00';
        
        // Calculate duration string
        const [startHour, startMin] = horaInicio.split(':').map(Number);
        const [endHour, endMin] = horaFim.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = Math.floor(durationMinutes / 60);
        const remainingMinutes = durationMinutes % 60;
        const durationStr = durationHours > 0 
          ? `${durationHours}h${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`
          : `${remainingMinutes}min`;
        
        // Build title based on produto(s) and nome_empresa
        const produtoTitles: Record<string, string> = {
          'bairro-solar': 'Bairro Solar',
          'pap': 'PAP',
          'gas': 'Gás',
          'eletricidade': 'Eletricidade'
        };
        
        // Extract produtos array from form_data (for multi-product marcações)
        const produtosArray = m.form_data?.produtos || (m.produto ? [m.produto] : []);
        const produtos = Array.isArray(produtosArray) ? produtosArray : [produtosArray].filter(Boolean);
        
        // Build product title(s) - if multiple products, join them with " + "
        const produtoTitleParts = produtos.map((p: string) => produtoTitles[p] || p);
        const produtoTitle = produtoTitleParts.length > 0 ? produtoTitleParts.join(' + ') : (produtoTitles[m.produto] || m.produto);
        
        const isRescheduled = m.gestao_substage === 'agendamentos-remarcacao';
        const isNotOkCallCenter = m.gestao_substage === 'feedback-pendencia-not-ok-callcenter';
        const isNotOkCliente = m.gestao_substage === 'feedback-pendencia-not-ok-cliente';
        const isFechoOk = m.gestao_substage === 'resultado-fecho-ok';
        const isFechoNotOk = m.gestao_substage === 'resultado-fecho-not-ok';
        const isCancelado = m.gestao_substage === 'resultado-cancelado';
        const isPendente = m.gestao_substage === 'feedback-pendencia-pendente';
        const isConcluidoSemProposta = m.gestao_substage === 'feedback-pendencia-concluido-sem-proposta';
        // Visita Válida: Concluido Com Proposta, Fecho Ok, Fecho Not Ok, Aguardar, Cancelado
        const visitaValidaSubstages = [
          'feedback-pendencia-concluido-com-proposta',
          'resultado-fecho-ok',
          'resultado-fecho-not-ok',
          'resultado-aguardar',
          'resultado-cancelado'
        ];
        const isVisitaValida = m.gestao_substage && visitaValidaSubstages.includes(m.gestao_substage);
        // Fecho = new event type: identify by marcacao_id prefix FECHO- or form_data.eventType
        const isFecho = (m.marcacao_id && String(m.marcacao_id).startsWith('FECHO-')) || m.form_data?.eventType === 'fecho';
        const baseTitle = `${produtoTitle} - ${m.nome_empresa}`;
        let title = baseTitle;
        if (isFecho) {
          title = `[Fecho] ${baseTitle}`;
        } else if (isRescheduled) {
          title = `Reagendamento ${baseTitle}`;
        } else if (isNotOkCallCenter) {
          title = `Not Ok Callcenter ${baseTitle}`;
        } else if (isNotOkCliente) {
          title = `Not Ok Cliente ${baseTitle}`;
        } else if (isFechoOk) {
          title = `Fecho Ok ${baseTitle}`;
        } else if (isFechoNotOk) {
          title = `Fecho Not Ok ${baseTitle}`;
        } else if (isCancelado) {
          title = `Cancelado ${baseTitle}`;
        } else if (isPendente) {
          title = `Pendente ${baseTitle}`;
        } else if (isConcluidoSemProposta) {
          title = `Concluído Sem Proposta ${baseTitle}`;
        } else if (isVisitaValida) {
          title = `Visita Válida ${baseTitle}`;
        }
        
        // Build description from product details
        let description = '';
        if (m.product_details) {
          const details = m.product_details;
          if (m.produto === 'bairro-solar') {
            description = `Visita técnica para instalação de painéis solares residenciais - ${details.tipoTelhado || ''}, ${details.jaTemPaineis === 'nao' ? 'sem' : 'com'} painéis existentes, potência ${details.potencia1035 === 'sim' ? '13.80Kw' : 'outra'}, ${details.trifasico === 'sim' ? 'trifásico' : 'monofásico'}`;
          } else if (m.produto === 'pap') {
            description = `Visita para instalação de sistema PAY AS PRODUCED - ${details.tipoTelhadoPAP || ''}, ${details.jaTemPaineisPAP === 'nao' ? 'sem' : 'com'} painéis existentes, potência ${details.potencia69 === 'sim' ? '6.9Kw' : 'outra'}, ${details.pagaMais250 === 'sim' ? 'paga mais de 250€' : 'não paga mais de 250€'}`;
          } else if (m.produto === 'gas') {
            description = `Visita para mudança de comercializadora de gás - ${details.gasNatural ? 'Gás natural canalizado' : 'Outro tipo'}, comercializadora atual ${details.comercializadoraGas || ''}, valor fatura ${details.valorFaturaGas || ''}`;
          } else if (m.produto === 'eletricidade') {
            description = `Visita para mudança de comercializadora de eletricidade - Comercializadora atual ${details.comercializadoraEletricidade || ''}, valor fatura ${details.valorFaturaEletricidade || ''}`;
          }
        } else if (m.marcacao_description) {
          description = m.marcacao_description;
        }
        
        // Extract and normalize user IDs - ensure they're strings for consistent filtering
        // Handle both integer arrays (from backend) and string arrays (already converted)
        const rawUserIds = m.form_data?.marcacaoUsers || [];
        const userIds = Array.isArray(rawUserIds) 
          ? rawUserIds.map((uid: any) => String(uid)).filter((uid: string) => uid && uid !== 'null' && uid !== 'undefined')
          : [];
        const attendees = userIds.map((uid: string) => 
          comercialUsers.find((u: ComercialUser) => u.id === uid)?.name || ''
        ).filter(Boolean);
        
        const eventType: 'fecho' | 'marcacao' = isFecho ? 'fecho' : 'marcacao';
        return {
          id: `marcacao-${m.id}`,
          title: title,
          time: horaInicio,
          duration: durationStr,
          type: eventType,
          description: description || m.marcacao_description,
          attendees: attendees.length > 0 ? attendees : undefined,
          location: m.morada,
          status: 'scheduled' as const,
          date: marcacaoDate,
          product: m.produto as 'bairro-solar' | 'pap' | 'gas' | 'eletricidade',
            formData: {
            marcacaoData: m.form_data?.marcacaoData || m.date,
            horaInicio: m.hora_inicio,
            horaFim: m.hora_fim,
            marcacaoDescription: m.marcacao_description,
            produto: m.produto,
            gestao_substage: m.gestao_substage || 'agendamentos-agendamentos',
            feedback: m.form_data?.feedback || m.feedback || null,
            ...m.product_details,
            nomeEmpresa: m.nome_empresa,
            nomeResponsavel: m.nome_responsavel,
            morada: m.morada,
            contactoCliente: m.contacto_cliente,
            codigoPostal1: m.codigo_postal1,
            codigoPostal2: m.codigo_postal2,
            linkGoogleMaps: m.link_google_maps,
            // Spread form_data first
            ...m.form_data,
            // Ensure these computed values are not overwritten by spread
            // IMPORTANT: marcacaoUsers must be set AFTER spread to ensure it's a string array (not integers from form_data)
            marcacaoUser: userIds[0] || String(selectedUser?.id || ''),
            marcacaoUsers: userIds,
            // IMPORTANT: produtos must be set AFTER spread to ensure it's properly extracted (not overwritten by form_data)
            produtos: produtos, // Include produtos array for multi-product marcações
            isRescheduled: isRescheduled,
            isNotOkCallCenter: isNotOkCallCenter,
            isNotOkCliente: isNotOkCliente,
            isFechoOk: isFechoOk,
            isFechoNotOk: isFechoNotOk,
            isCancelado: isCancelado,
            isPendente: isPendente,
            isConcluidoSemProposta: isConcluidoSemProposta,
            isVisitaValida: isVisitaValida,
            marcacaoEventId: String(m.id),
            createdBy: m.created_by,
            createdAt: m.created_at,
            // Ensure marcacaoId is preserved (from m.marcacao_id, not from form_data)
            marcacaoId: m.marcacao_id || m.form_data?.marcacaoId || m.form_data?.marcacao_id,
            // Ensure source is preserved (from form_data or default to 'callcenter')
            // Must be after spread to preserve source from form_data if it exists
            source: m.form_data?.source || 'callcenter',
            eventType: m.form_data?.eventType || 'marcacao',
            sourceMarcacaoId: m.form_data?.sourceMarcacaoId
          }
        } as CalendarEvent;
      });
      
      // Group only marcações (not Fecho) that share the same date, time, and client (same logic as CallCenterCalendar)
      const groupedMap = new Map<string, CalendarEvent[]>();
      mapped.forEach((event) => {
        const isFechoEvent = event.type === 'fecho' || event.formData?.marcacaoId?.toString().startsWith('FECHO-');
        if (event.type === 'marcacao' && event.formData && !isFechoEvent) {
          const key = `${event.date.toISOString().split('T')[0]}_${event.formData.horaInicio}_${event.formData.nomeEmpresa}`;
          if (!groupedMap.has(key)) {
            groupedMap.set(key, []);
          }
          groupedMap.get(key)!.push(event);
        }
      });
      
      // Process grouped events: combine into single events with multiple products
      const finalEvents: CalendarEvent[] = [];
      groupedMap.forEach((group, key) => {
        if (group.length === 1) {
          // Single product - keep as is, but ensure produtos array is set
          const event = group[0];
          // If produtos is not already set or is empty, use single produto
          if (!event.formData?.produtos || event.formData.produtos.length === 0) {
            const produtos = event.product ? [event.product] : [];
            event.formData = {
              ...event.formData,
              produtos: produtos
            };
          }
          finalEvents.push(event);
        } else {
          // Multiple products - combine into one event
          const firstEvent = group[0];
          const produtos = group.map(e => e.product).filter(Boolean) as string[];
          
          // Build title with all products
          const produtoTitles: Record<string, string> = {
            'bairro-solar': 'Bairro Solar',
            'pap': 'PAP',
            'gas': 'Gás',
            'eletricidade': 'Eletricidade'
          };
          const productNames = produtos.map((p: string) => produtoTitles[p] || p).join(' + ');
          const baseTitle = `${productNames} - ${firstEvent.formData?.nomeEmpresa || ''}`;
          
          // Determine title prefix based on status (use first event's status)
          let title = baseTitle;
          if (firstEvent.formData?.isRescheduled) {
            title = `Reagendamento ${baseTitle}`;
          } else if (firstEvent.formData?.isNotOkCallCenter) {
            title = `Not Ok Callcenter ${baseTitle}`;
          } else if (firstEvent.formData?.isNotOkCliente) {
            title = `Not Ok Cliente ${baseTitle}`;
          } else if (firstEvent.formData?.isFechoOk) {
            title = `Fecho Ok ${baseTitle}`;
          } else if (firstEvent.formData?.isFechoNotOk) {
            title = `Fecho Not Ok ${baseTitle}`;
          } else if (firstEvent.formData?.isCancelado) {
            title = `Cancelado ${baseTitle}`;
          } else if (firstEvent.formData?.isPendente) {
            title = `Pendente ${baseTitle}`;
          } else if (firstEvent.formData?.isConcluidoSemProposta) {
            title = `Concluído Sem Proposta ${baseTitle}`;
          } else if (firstEvent.formData?.isVisitaValida) {
            title = `Visita Válida ${baseTitle}`;
          }
          
          // Merge formData from all events so each product's fields (comercializadoraGas, comercializadoraEletricidade, etc.) are present
          const mergedFormData = group.reduce<Record<string, any>>((acc, e) => ({ ...acc, ...(e.formData || {}) }), {});
          const mergedOriginalCallcenter = group.reduce<Record<string, any>>((acc, e) => ({
            ...acc,
            ...(e.formData?.original_callcenter || {})
          }), {});
          const mergedComercialOverrides = group.reduce<Record<string, any>>((acc, e) => ({
            ...acc,
            ...(e.formData?.comercial_overrides || {})
          }), {});
          const combinedFormData = {
            ...mergedFormData,
            original_callcenter: Object.keys(mergedOriginalCallcenter).length > 0 ? mergedOriginalCallcenter : mergedFormData.original_callcenter,
            comercial_overrides: Object.keys(mergedComercialOverrides).length > 0 ? mergedComercialOverrides : mergedFormData.comercial_overrides,
            produtos,
            groupedMarcacoes: group.map(e => e.formData?.marcacaoEventId).filter(Boolean)
          };
          // Create combined event
          const combinedEvent: CalendarEvent = {
            ...firstEvent,
            id: `marcacao-group-${key}`,
            title: title,
            product: produtos[0] as any, // Use first product for backward compatibility
            formData: combinedFormData
          };
          finalEvents.push(combinedEvent);
        }
      });
      
      // Add non-marcação events (bloqueios, rotas, etc.) - though ComercialCalendar filters these out
      mapped.forEach((event) => {
        if (event.type !== 'marcacao') {
          finalEvents.push(event);
        }
      });
      
      return finalEvents;
    } catch (e) {
      return [];
    }
  }, [comercialUsers, currentDate, viewMode, currentUserId, selectedUser]); // Include currentUserId and selectedUser for logging

  // Function to load all events from backend (activities + marcações + bloqueios + scenarios)
  const loadEvents = useCallback(async () => {
    if (!selectedUser) { 
      setFetchedEvents([]);
      setFetchedScenarioEvents([]);
      setBloqueioEvents([]);
      return; 
    }
    
    setIsLoadingEvents(true);
    const startTime = Date.now();
    setLoadingStartTime(startTime);
    
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    try {
      // Fetch activities
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = new URL(`${backendUrl}/api/activities`);
      url.searchParams.set('user_id', String(selectedUser.id));
      
      const res = await fetch(url.toString(), {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      
      if (!res.ok) {
        setFetchedEvents([]);
        setFetchedScenarioEvents([]);
        setBloqueioEvents([]);
        return;
      }
      
      const json = await res.json();
      const activities = Array.isArray(json?.activities) ? json.activities : [];
      
      // Separate scenario activities from regular activities
      const scenarioActivities = activities.filter((a: any) => 
        a && a.due_date && (a.activity_type || '').toLowerCase() === 'scenario'
      );
      
      const regularActivities = activities.filter((a: any) => 
        a && a.due_date && (a.activity_type || '').toLowerCase() !== 'scenario'
      );
      
      // Map scenario activities to StatusEvent format (matching CallCenterCalendar)
      const mappedScenarios: StatusEvent[] = scenarioActivities.map((a: any) => {
        // Handle date parsing - backend returns ISO format
        let startDt: Date;
        if (a.due_date) {
          if (typeof a.due_date === 'string') {
            // Replace Z with +00:00 for proper parsing, or use as-is
            startDt = new Date(a.due_date.includes('Z') ? a.due_date.replace('Z', '+00:00') : a.due_date);
          } else {
            startDt = new Date(a.due_date);
          }
        } else {
          return null;
        }
        
        if (isNaN(startDt.getTime())) {
          return null;
        }
        
        // Extract scenario text (matching CallCenterCalendar logic)
        // Scenarios are now free text fields filled by CallCenter users
        let scenarioText = 'Cenário';
        if (a.title) {
          if (a.title.includes(':')) {
            const titleParts = a.title.split(':');
            if (titleParts.length > 1) {
              const afterColon = titleParts[1].trim();
              if (afterColon.includes(' - ')) {
                scenarioText = afterColon.split(' - ')[0].trim();
              } else {
                scenarioText = afterColon;
              }
            } else {
              scenarioText = a.title;
            }
          } else {
            scenarioText = a.title;
          }
        } else if (a.description) {
          if (a.description.includes("'") && a.description.includes(' em ')) {
            const match = a.description.match(/'([^']+)'/);
            if (match && match[1]) {
              scenarioText = match[1];
            } else {
              scenarioText = a.description;
            }
          } else {
            scenarioText = a.description;
          }
        }
        
        return {
          id: a.scenario_id || `scenario-${a.id}`,
          userId: String(a.assigned_to || selectedUser.id),
          date: startDt,
          text: scenarioText
        } as StatusEvent;
      }).filter((event: StatusEvent | null): event is StatusEvent => event !== null);
      
      setFetchedScenarioEvents(mappedScenarios);
      
      // Load marcação events
      const marcacaoEvents = await loadMarcacaoEvents();
      
      // Load bloqueio events
      const bloqueioEventsFromBackend = await loadBloqueioEvents();
      
      // Set bloqueio events from backend (replace examples with backend data)
      setBloqueioEvents(bloqueioEventsFromBackend);
      
      // Only include marcação events in fetchedEvents (bloqueio events are kept separate)
      setFetchedEvents(marcacaoEvents);
      
      // Ensure minimum 2 seconds display time
      const elapsedTime = Date.now() - startTime;
      const minimumDisplayTime = 2000; // 2 seconds
      const remainingTime = minimumDisplayTime - elapsedTime;
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } catch (e) {
      setFetchedEvents([]);
      setFetchedScenarioEvents([]);
      setBloqueioEvents([]);
      
      // Still ensure minimum display time even on error
      const elapsedTime = Date.now() - startTime;
      const minimumDisplayTime = 2000;
      const remainingTime = minimumDisplayTime - elapsedTime;
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } finally {
      setIsLoadingEvents(false);
      setLoadingStartTime(null);
    }
  }, [selectedUser, loadMarcacaoEvents, loadBloqueioEvents, currentDate, comercialUsers, viewMode]);

  // Function to load rota events from backend
  const loadRotaEvents = useCallback(async () => {
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    try {
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const url = new URL(`${backendUrl}/api/callcenter/calendar/rotas`);
      if (selectedUser) {
        url.searchParams.set('user_id', String(selectedUser.id));
      }
      const today = currentDate || new Date();
      const { dateFrom, dateTo } = getFetchRangeForView(today, viewMode);
      url.searchParams.set('date_from', getLisbonDateString(dateFrom));
      url.searchParams.set('date_to', getLisbonDateString(dateTo));
      
      const res = await fetch(url.toString(), {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      
      if (!res.ok) {
        return;
      }
      
      const json = await res.json();
      const rotas = Array.isArray(json?.rotas) ? json.rotas : [];
      
      // Map backend rota events (matching CallCenterCalendar): { id, date, userIds, links, linkGoogleMaps, title, description }
      const mapped = rotas.map((r: any) => {
        const dateParts = r.date.split('-');
        const rotaDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        const userIds = (r.form_data?.comerciais || []).map((uid: number) => String(uid));
        // Get links from backend - support both new format (links array) and legacy (linkGoogleMaps)
        const links = r.links || r.form_data?.links || [];
        const linkGoogleMaps = r.link_google_maps || r.form_data?.linkGoogleMaps || '';
        const finalLinks = links.length > 0 ? links : (linkGoogleMaps ? [{ url: linkGoogleMaps, label: 'Google Maps' }] : []);
        return {
          id: `rota-${r.id}`,
          rotaId: r.rota_id || r.rotaId,
          date: rotaDate,
          userIds: userIds,
          links: finalLinks,
          linkGoogleMaps: linkGoogleMaps,
          title: r.form_data?.title || r.title || 'Rota',
          description: r.form_data?.description || r.description || '',
          formData: {
            ...r.form_data,
            rotaId: r.rota_id || r.rotaId,
            links: finalLinks,
            linkGoogleMaps: linkGoogleMaps
          }
        };
      });
      
      setRotaEvents(mapped);
    } catch (e) {
      // Error handling - silently fail
    }
  }, [selectedUser, currentDate, viewMode]);

  // Load events when selectedUser or currentDate changes
  // IMPORTANT: Wait for selectedUser to be set before loading events
  // This ensures it works correctly in production (single run) where React StrictMode doesn't double-invoke
  useEffect(() => {
    // Don't load events if selectedUser is not set yet
    // This prevents race conditions in production where effects only run once
    if (!selectedUser) {
      return;
    }
    loadEvents();
    loadRotaEvents();
  }, [loadEvents, loadRotaEvents, selectedUser, viewMode]);

  // Sync edit values when selectedStatusEvent changes
  useEffect(() => {
    if (selectedStatusEvent && !isEditingScenario) {
      // Initialize with single userId (scenarios can be linked to multiple users, but we start with the one shown)
      const linkedUserIds: string[] = [selectedStatusEvent.userId];
      
      // Set date
      if (selectedStatusEvent.date) {
        const dateObj = selectedStatusEvent.date instanceof Date ? selectedStatusEvent.date : new Date(selectedStatusEvent.date);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        setEditScenarioDate(`${year}-${month}-${day}`);
      }
      
      setEditScenarioText(selectedStatusEvent.text || '');
      setEditScenarioAssignedTo(linkedUserIds);
    }
  }, [selectedStatusEvent, isEditingScenario]);

  // Sync edit values when selectedRotaEvent changes
  useEffect(() => {
    if (selectedRotaEvent && !isEditingRota) {
      const rotaDate = selectedRotaEvent.date || (selectedRotaEvent.formData?.data ? new Date(selectedRotaEvent.formData.data) : null) || (selectedRotaEvent.data ? new Date(selectedRotaEvent.data) : null);
      
      if (rotaDate) {
        let dateStr: string;
        if (rotaDate instanceof Date) {
          const year = rotaDate.getFullYear();
          const month = String(rotaDate.getMonth() + 1).padStart(2, '0');
          const day = String(rotaDate.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        } else {
          dateStr = rotaDate;
        }
        setEditRotaDate(dateStr);
      }
      
      const userIds = selectedRotaEvent.userIds || selectedRotaEvent.formData?.comerciais || [];
      setEditRotaComerciais(userIds.map((id: string | number) => String(id)));
      const linkGoogleMapsVal = selectedRotaEvent.linkGoogleMaps || selectedRotaEvent.formData?.linkGoogleMaps || '';
      setEditRotaLinkGoogleMaps(linkGoogleMapsVal);
      const links = selectedRotaEvent.links || selectedRotaEvent.formData?.links || [];
      const labels = ['Início da Rota', 'Continuação'];
      const finalLinks = links.length > 0 ? links : (linkGoogleMapsVal ? [{ url: linkGoogleMapsVal, label: 'Google Maps' }] : []);
      const editLinks: Array<{ url: string; label: string }> = [0, 1].map((i) => finalLinks[i] || { url: '', label: labels[i] });
      setEditRotaLinks(editLinks);
    }
  }, [selectedRotaEvent, isEditingRota]);

  // Register callback to refresh calendar when marcações are created elsewhere
  useEffect(() => {
    const unregister = onCalendarDataRefresh(() => {
      if (selectedUser) {
        loadEvents();
        loadRotaEvents();
      }
    });
    return unregister;
  }, [onCalendarDataRefresh, selectedUser, loadEvents, loadRotaEvents]);

  // Status events data - Examples spread across users
  const [statusEvents, setStatusEvents] = useState<StatusEvent[]>([
    // João Silva (User 1) - Transportes examples
    {
      id: 'status1',
      userId: '1',
      date: new Date(2025, 9, 1), // Oct 1
      text: 'Transportes - Coimbra'
    },
    {
      id: 'status2',
      userId: '1',
      date: new Date(2025, 9, 2), // Oct 2
      text: 'Transportes - Lisboa'
    },
    {
      id: 'status3',
      userId: '1',
      date: new Date(2025, 9, 3), // Oct 3
      text: 'Transportes - Coimbra'
    },
    
    // Maria Santos (User 2) - Carro examples
    {
      id: 'status4',
      userId: '2',
      date: new Date(2025, 9, 1), // Oct 1
      text: 'Carro - Lisboa'
    },
    {
      id: 'status5',
      userId: '2',
      date: new Date(2025, 9, 2), // Oct 2
      text: 'Carro - Coimbra'
    },
    {
      id: 'status6',
      userId: '2',
      date: new Date(2025, 9, 4), // Oct 4
      text: 'Carro - Lisboa'
    },
    
    // Pedro Alves (User 3) - Longe examples
    {
      id: 'status7',
      userId: '3',
      date: new Date(2025, 9, 1), // Oct 1
      text: 'Longe - Coimbra'
    },
    {
      id: 'status8',
      userId: '3',
      date: new Date(2025, 9, 3), // Oct 3
      text: 'Longe - Lisboa'
    },
    {
      id: 'status9',
      userId: '3',
      date: new Date(2025, 9, 5), // Oct 5
      text: 'Longe - Coimbra'
    },
    
    // Sofia Lima (User 4) - RoadTrip examples
    {
      id: 'status10',
      userId: '4',
      date: new Date(2025, 9, 2), // Oct 2
      text: 'RoadTrip - Lisboa'
    },
    {
      id: 'status11',
      userId: '4',
      date: new Date(2025, 9, 4), // Oct 4
      text: 'RoadTrip - Coimbra'
    },
    {
      id: 'status12',
      userId: '4',
      date: new Date(2025, 9, 6), // Oct 6
      text: 'RoadTrip - Lisboa'
    },
    
    // Ana Costa (User 5) - Mixed examples
    {
      id: 'status13',
      userId: '5',
      date: new Date(2025, 9, 1), // Oct 1
      text: 'Transportes - Lisboa'
    },
    {
      id: 'status14',
      userId: '5',
      date: new Date(2025, 9, 3), // Oct 3
      text: 'Carro - Coimbra'
    },
    {
      id: 'status15',
      userId: '5',
      date: new Date(2025, 9, 5), // Oct 5
      text: 'Longe - Lisboa'
    },
    
    // Carlos Mendes (User 6) - Mixed examples
    {
      id: 'status16',
      userId: '6',
      date: new Date(2025, 9, 2), // Oct 2
      text: 'RoadTrip - Coimbra'
    },
    {
      id: 'status17',
      userId: '6',
      date: new Date(2025, 9, 4), // Oct 4
      text: 'Transportes - Lisboa'
    },
    {
      id: 'status18',
      userId: '6',
      date: new Date(2025, 9, 6), // Oct 6
      text: 'Carro - Coimbra'
    }
  ]);

  // Sample events data - Only for October 2025
  // COMPLETE BACKEND INTEGRATION TEMPLATE
  // This template includes ALL fields from the Nova Marcação modal
  // Use this structure when implementing backend integration
  /*
  interface MarcacaoFormData {
    // Step 1: Agendamento
    marcacaoId?: string;                    // Auto-generated by backend
    marcacaoUsers: string[];                // Array of commercial user IDs
    marcacaoData: string;                   // Date in DD/MM/YYYY format
    horaInicio: string;                     // Start time in HH:MM format
    horaFim: string;                        // End time in HH:MM format
    marcacaoDescription?: string;           // Optional observations
    
    // Step 2: Produto
    produto: 'bairro-solar' | 'pap' | 'gas' | 'eletricidade';
    
    // Step 3: Detalhes (Product-specific fields)
    // Bairro Solar fields
    jaTemPaineis?: 'sim' | 'nao';
    potencia1035?: 'sim' | 'nao' | 'nao_sabe';
    trifasico?: 'sim' | 'nao' | 'nao_sabe';
    tipoTelhado?: string;
    faturaPresenteBairro?: 'sim' | 'nao';
    
    // PAP fields
    jaTemPaineisPAP?: 'sim' | 'nao';
    potencia69?: 'sim' | 'nao' | 'nao_sabe';
    tipoTelhadoPAP?: string;
    pagaMais250?: 'sim' | 'nao';
    faturaPresentePAP?: 'sim' | 'nao';
    
    // Gás fields
    gasNatural?: boolean;                   // Checkbox value
    comercializadoraGas?: string;
    valorFaturaGas?: string;
    faturaPresenteGas?: 'sim' | 'nao';
    
    // Eletricidade fields
    comercializadoraEletricidade?: string;
    valorFaturaEletricidade?: string;
    faturaPresenteEletricidade?: 'sim' | 'nao';
    
    // Step 4: Cliente
    nomeEmpresa: string;
    nomeResponsavel: string;
    morada: string;
    contactoCliente: string;
    codigoPostal1?: string;                 // First 4 digits
    codigoPostal2?: string;                 // Last 3 digits
    linkGoogleMaps?: string;                // Google Maps URL
  }
  */

  const sampleEvents: CalendarEvent[] = [
    // October 1, 2025 - Bairro Solar marcações
    {
      id: '1',
      title: 'Bairro Solar - Residência Silva',
      time: '09:00',
      duration: '1h30min',
      type: 'marcacao',
      description: 'Visita técnica para instalação de painéis solares residenciais - Telha cerâmica, sem painéis existentes, potência 13.80Kw, trifásico',
      attendees: ['João Silva', 'Maria Santos'],
      location: 'Rua das Flores, 123, Lisboa',
      status: 'scheduled',
      date: new Date(2025, 9, 1),
      product: 'bairro-solar',
      // Form data simulation
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-001',
        marcacaoUsers: ['1', '2'],
        marcacaoData: '01/10/2025',
        horaInicio: '09:00',
        horaFim: '10:30',
        marcacaoDescription: 'Cliente interessado em instalação completa de painéis solares',
        
        // Step 2: Produto
        produto: 'bairro-solar',
        
        // Step 3: Detalhes - Bairro Solar
        jaTemPaineis: 'nao',
        potencia1035: 'sim',
        trifasico: 'sim',
        tipoTelhado: 'Telha cerâmica',
        faturaPresenteBairro: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Residência Silva',
        nomeResponsavel: 'Carlos Silva',
        morada: 'Rua das Flores, 123, Lisboa',
        contactoCliente: '912345678',
        codigoPostal1: '1000',
        codigoPostal2: '001',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    {
      id: '2',
      title: 'Bairro Solar - Empresa TechCorp',
      time: '14:00',
      duration: '2h',
      type: 'marcacao',
      description: 'Avaliação técnica para instalação industrial de painéis solares - Telha metálica, sem painéis existentes, potência 13.80Kw, monofásico',
      attendees: ['Ana Costa', 'Pedro Alves'],
      location: 'Zona Industrial, Lote 45, Porto',
      status: 'scheduled',
      date: new Date(2025, 9, 1),
      product: 'pap',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-002',
        marcacaoUsers: ['5'],
        marcacaoData: '01/10/2025',
        horaInicio: '14:00',
        horaFim: '16:00',
        marcacaoDescription: 'Empresa industrial interessada em redução de custos energéticos',
        
        // Step 2: Produto
        produto: 'pap',
        
        // Step 3: Detalhes - PAP
        jaTemPaineisPAP: 'nao',
        potencia69: 'sim',
        tipoTelhadoPAP: 'Telha metálica',
        pagaMais250: 'sim',
        faturaPresentePAP: 'nao',
        
        // Step 4: Cliente
        nomeEmpresa: 'TechCorp Lda',
        nomeResponsavel: 'Maria Santos',
        morada: 'Zona Industrial, Lote 45, Porto',
        contactoCliente: '912987654',
        codigoPostal1: '4200',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    // October 3, 2025 - PAP marcações
    {
      id: '3',
      title: 'Gás - Residência Alves',
      time: '10:00',
      duration: '1h30min',
      type: 'marcacao',
      description: 'Visita para mudança de comercializadora de gás - Gás natural canalizado, comercializadora atual EDP, valor fatura 45€',
      attendees: ['Carlos Mendes', 'Sofia Rodrigues'],
      location: 'Avenida Central, 456, Braga',
      status: 'scheduled',
      date: new Date(2025, 9, 3),
      product: 'gas',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-003',
        marcacaoUsers: ['6'],
        marcacaoData: '03/10/2025',
        horaInicio: '10:00',
        horaFim: '11:30',
        marcacaoDescription: 'Cliente residencial interessado em mudança de comercializadora de gás',
        
        // Step 2: Produto
        produto: 'gas',
        
        // Step 3: Detalhes - Gás
        gasNatural: true,
        comercializadoraGas: 'EDP Comercial',
        valorFaturaGas: '45€',
        faturaPresenteGas: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Residência Alves',
        nomeResponsavel: 'Pedro Alves',
        morada: 'Avenida Central, 456, Braga',
        contactoCliente: '912345123',
        codigoPostal1: '4700',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    {
      id: '4',
      title: 'Eletricidade - Empresa Verde',
      time: '15:30',
      duration: '2h',
      type: 'marcacao',
      description: 'Visita para mudança de comercializadora de eletricidade - Comercializadora atual Galp, valor fatura 120€',
      attendees: ['Ana Costa', 'João Silva'],
      location: 'Rua do Comércio, 789, Coimbra',
      status: 'scheduled',
      date: new Date(2025, 9, 3),
      product: 'eletricidade',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-004',
        marcacaoUsers: ['5'],
        marcacaoData: '03/10/2025',
        horaInicio: '15:30',
        horaFim: '17:30',
        marcacaoDescription: 'Empresa comercial interessada em mudança de comercializadora de eletricidade',
        
        // Step 2: Produto
        produto: 'eletricidade',
        
        // Step 3: Detalhes - Eletricidade
        comercializadoraEletricidade: 'Galp Energia',
        valorFaturaEletricidade: '120€',
        faturaPresenteEletricidade: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Empresa Verde Lda',
        nomeResponsavel: 'Carlos Verde',
        morada: 'Rua do Comércio, 789, Coimbra',
        contactoCliente: '912987123',
        codigoPostal1: '3000',
        codigoPostal2: '456',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    // October 7, 2025 - Gás marcações
    {
      id: '5',
      title: 'Bairro Solar - Residência Rodrigues',
      time: '12:30',
      duration: '1h',
      type: 'marcacao',
      description: 'Visita para instalação de painéis solares - Telha cerâmica, sem painéis existentes, potência 13.80Kw, trifásico',
      attendees: ['João Silva', 'Maria Santos'],
      location: 'Rua da Paz, 321, Aveiro',
      status: 'scheduled',
      date: new Date(2025, 9, 7),
      product: 'bairro-solar',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-005',
        marcacaoUsers: ['1'],
        marcacaoData: '07/10/2025',
        horaInicio: '12:30',
        horaFim: '13:30',
        marcacaoDescription: 'Cliente residencial interessado em instalação de painéis solares',
        
        // Step 2: Produto
        produto: 'bairro-solar',
        
        // Step 3: Detalhes - Bairro Solar
        jaTemPaineis: 'nao',
        potencia1035: 'sim',
        trifasico: 'nao',
        tipoTelhado: 'Telha cerâmica',
        faturaPresenteBairro: 'nao',
        
        // Step 4: Cliente
        nomeEmpresa: 'Residência Rodrigues',
        nomeResponsavel: 'Sofia Rodrigues',
        morada: 'Rua da Paz, 321, Aveiro',
        contactoCliente: '912345456',
        codigoPostal1: '3800',
        codigoPostal2: '789',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    {
      id: '6',
      title: 'PAP - Restaurante O Sabor',
      time: '16:00',
      duration: '1h30min',
      type: 'marcacao',
      description: 'Visita para instalação de sistema PAY AS PRODUCED - Telha cerâmica, sem painéis existentes, potência 6.9Kw, paga mais de 250€',
      attendees: ['Maria Santos', 'Carlos Mendes'],
      location: 'Praça Principal, 12, Viseu',
      status: 'scheduled',
      date: new Date(2025, 9, 7),
      product: 'pap',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-006',
        marcacaoUsers: ['2'],
        marcacaoData: '07/10/2025',
        horaInicio: '16:00',
        horaFim: '17:30',
        marcacaoDescription: 'Restaurante interessado em sistema PAP para redução de custos energéticos',
        
        // Step 2: Produto
        produto: 'pap',
        
        // Step 3: Detalhes - PAP
        jaTemPaineisPAP: 'nao',
        potencia69: 'sim',
        tipoTelhadoPAP: 'Telha cerâmica',
        pagaMais250: 'sim',
        faturaPresentePAP: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Restaurante O Sabor',
        nomeResponsavel: 'António Sabor',
        morada: 'Praça Principal, 12, Viseu',
        contactoCliente: '912987456',
        codigoPostal1: '3500',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    // October 10, 2025 - Eletricidade marcações
    {
      id: '7',
      title: 'Eletricidade - Hotel Central',
      time: '11:00',
      duration: '2h',
      type: 'marcacao',
      description: 'Proposta de mudança de comercializadora de eletricidade - EDP Comercial, fatura 125.30€, fatura presente',
      attendees: ['Carlos Mendes', 'Ana Costa'],
      location: 'Avenida do Turismo, 100, Faro',
      status: 'scheduled',
      date: new Date(2025, 9, 10),
      product: 'eletricidade',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-007',
        marcacaoUsers: ['6'],
        marcacaoData: '10/10/2025',
        horaInicio: '11:00',
        horaFim: '13:00',
        marcacaoDescription: 'Hotel interessado em otimização de tarifa elétrica',
        
        // Step 2: Produto
        produto: 'eletricidade',
        
        // Step 3: Detalhes - Eletricidade
        comercializadoraEletricidade: 'EDP Comercial',
        valorFaturaEletricidade: '125.30€',
        faturaPresenteEletricidade: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Hotel Central',
        nomeResponsavel: 'Manuel Central',
        morada: 'Avenida do Turismo, 100, Faro',
        contactoCliente: '912345789',
        codigoPostal1: '8000',
        codigoPostal2: '456',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    {
      id: '8',
      title: 'Gás - Clínica Saúde',
      time: '14:30',
      duration: '1h30min',
      type: 'marcacao',
      description: 'Visita para mudança de comercializadora de gás - Endesa, fatura 89.75€, fatura presente',
      attendees: ['Ana Costa', 'João Silva'],
      location: 'Rua da Saúde, 55, Leiria',
      status: 'scheduled',
      date: new Date(2025, 9, 10),
      product: 'gas',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-008',
        marcacaoUsers: ['5'],
        marcacaoData: '10/10/2025',
        horaInicio: '14:30',
        horaFim: '16:00',
        marcacaoDescription: 'Clínica interessada em redução de custos de gás',
        
        // Step 2: Produto
        produto: 'gas',
        
        // Step 3: Detalhes - Gás
        gasNatural: true,
        comercializadoraGas: 'Endesa',
        valorFaturaGas: '89.75€',
        faturaPresenteGas: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Clínica Saúde',
        nomeResponsavel: 'Dr. Carlos Saúde',
        morada: 'Rua da Saúde, 55, Leiria',
        contactoCliente: '912987789',
        codigoPostal1: '2400',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    // October 15, 2025 - Mixed marcações
    {
      id: '9',
      title: 'Bairro Solar - Residencial Sol',
      time: '09:30',
      duration: '2h',
      type: 'marcacao',
      description: 'Instalação de painéis solares em condomínio residencial - Telha cerâmica, sem painéis existentes, potência 13.80Kw, trifásico',
      attendees: ['João Silva', 'Pedro Alves'],
      location: 'Urbanização Sol Nascente, Lote 15, Setúbal',
      status: 'scheduled',
      date: new Date(2025, 9, 15),
      product: 'bairro-solar',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-009',
        marcacaoUsers: ['1'],
        marcacaoData: '15/10/2025',
        horaInicio: '09:30',
        horaFim: '11:30',
        marcacaoDescription: 'Condomínio residencial interessado em instalação coletiva',
        
        // Step 2: Produto
        produto: 'bairro-solar',
        
        // Step 3: Detalhes - Bairro Solar
        jaTemPaineis: 'nao',
        potencia1035: 'sim',
        trifasico: 'sim',
        tipoTelhado: 'Telha cerâmica',
        faturaPresenteBairro: 'nao',
        
        // Step 4: Cliente
        nomeEmpresa: 'Residencial Sol',
        nomeResponsavel: 'Administrador do Condomínio',
        morada: 'Urbanização Sol Nascente, Lote 15, Setúbal',
        contactoCliente: '912345000',
        codigoPostal1: '2900',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    },
    {
      id: '10',
      title: 'Gás - Padaria Tradicional',
      time: '13:00',
      duration: '1h',
      type: 'marcacao',
      description: 'Proposta de gás natural para padaria - EDP Comercial, fatura 156.20€, fatura presente',
      attendees: ['Maria Santos', 'Sofia Rodrigues'],
      location: 'Rua do Pão, 88, Santarém',
      status: 'scheduled',
      date: new Date(2025, 9, 15),
      product: 'gas',
      formData: {
        // Step 1: Agendamento
        marcacaoId: 'MAR-2025-010',
        marcacaoUsers: ['2'],
        marcacaoData: '15/10/2025',
        horaInicio: '13:00',
        horaFim: '14:00',
        marcacaoDescription: 'Padaria interessada em redução de custos de gás',
        
        // Step 2: Produto
        produto: 'gas',
        
        // Step 3: Detalhes - Gás
        gasNatural: true,
        comercializadoraGas: 'EDP Comercial',
        valorFaturaGas: '156.20€',
        faturaPresenteGas: 'sim',
        
        // Step 4: Cliente
        nomeEmpresa: 'Padaria Tradicional',
        nomeResponsavel: 'José Pão',
        morada: 'Rua do Pão, 88, Santarém',
        contactoCliente: '912987000',
        codigoPostal1: '2000',
        codigoPostal2: '123',
        linkGoogleMaps: 'https://maps.app.goo.gl/AigYE4MR8wqPTZS6A'
      }
    }
  ];

  const getCurrentDateString = () => {
    return currentDate.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCurrentWeekString = () => {
    // For work days view, use Monday to Friday
    const startOfWeek = getMondayOfWeek(currentDate);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 4); // Monday + 4 days = Friday

    return `${startOfWeek.toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const getCurrentMonthString = () => {
    return currentDate.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long'
    });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case 'daily':
        newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'weekly':
        newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'monthly':
        newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentDate(date);
    setViewMode('daily');
  };

  const handleEventClick = (event: CalendarEvent) => {
    if (event.type === 'marcacao' || event.type === 'fecho') {
      setSelectedMarcacaoEvent(event);
      setShowMarcacaoEventDetailModal(true);
    } else if (event.type === 'rota') {
      setSelectedRotaEvent(event);
      setShowRotaEventDetailModal(true);
    } else {
      setSelectedEvent(event);
      setShowEventModal(true);
    }
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Sync edit values when selectedMarcacaoEvent changes
  useEffect(() => {
    if (selectedMarcacaoEvent && !isEditingMarcacao) {
      const formData = selectedMarcacaoEvent.formData || {};
      // Merge comercial_overrides so the edit modal shows the correct (effective) value for each field
      const co = formData.comercial_overrides || {};
      const effective = { ...formData, ...co };
      
      // Handle date
      let marcacaoDateStr = '';
      let marcacaoDate: Date | null = null;
      if (selectedMarcacaoEvent.date) {
        marcacaoDate = selectedMarcacaoEvent.date;
      } else if (effective.marcacaoData) {
        // Try parsing DD/MM/YYYY format
        const dateParts = effective.marcacaoData.split('/');
        if (dateParts.length === 3) {
          marcacaoDate = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
        } else {
          // Try YYYY-MM-DD format
          marcacaoDate = new Date(effective.marcacaoData);
        }
      }
      
      if (marcacaoDate && !isNaN(marcacaoDate.getTime())) {
        const year = marcacaoDate.getFullYear();
        const month = String(marcacaoDate.getMonth() + 1).padStart(2, '0');
        const day = String(marcacaoDate.getDate()).padStart(2, '0');
        marcacaoDateStr = `${year}-${month}-${day}`;
      } else {
        marcacaoDateStr = effective.marcacaoData || '';
      }
      
      // Set all fields at once. Support multi-product: include produtos array and all product-specific fields from effective (merged with comercial_overrides).
      const normalizedUsers = (effective.marcacaoUsers || []).map((uid: any) => String(uid));
      const norm = (p: string) => String(p || '').toLowerCase().replace(/\s+/g, '-').replace(/á/g, 'a').replace(/ã/g, 'a');
      const editProdutos: string[] = (effective.produtos && Array.isArray(effective.produtos))
        ? (effective.produtos as string[]).map((p: string) => norm(p) || p)
        : (effective.produto ? [norm(effective.produto) || effective.produto] : []);
      setEditMarcacaoFormData({
        marcacaoData: marcacaoDateStr,
        horaInicio: effective.horaInicio || selectedMarcacaoEvent.time || '',
        horaFim: effective.horaFim || selectedMarcacaoEvent.duration || '',
        marcacaoUsers: normalizedUsers,
        marcacaoDescription: effective.marcacaoDescription || '',
        produto: effective.produto || selectedMarcacaoEvent.product || '',
        produtos: editProdutos,
        nomeEmpresa: effective.nomeEmpresa || '',
        nomeResponsavel: effective.nomeResponsavel || '',
        morada: effective.morada || selectedMarcacaoEvent.location || '',
        contactoCliente: effective.contactoCliente || '',
        codigoPostal1: effective.codigoPostal1 || '',
        codigoPostal2: effective.codigoPostal2 || '',
        linkGoogleMaps: effective.linkGoogleMaps || '',
        // All product-specific fields (effective = base + comercial_overrides so correct option per product)
        jaTemPaineis: effective.jaTemPaineis ?? '',
        potencia1035: effective.potencia1035 ?? '',
        trifasico: effective.trifasico ?? '',
        tipoTelhado: effective.tipoTelhado ?? '',
        faturaPresenteBairro: effective.faturaPresenteBairro ?? '',
        jaTemPaineisPAP: effective.jaTemPaineisPAP ?? '',
        potencia69: effective.potencia69 ?? '',
        tipoTelhadoPAP: effective.tipoTelhadoPAP ?? '',
        pagaMais250: effective.pagaMais250 ?? '',
        faturaPresentePAP: effective.faturaPresentePAP ?? '',
        gasNatural: effective.gasNatural === true,
        comercializadoraGas: effective.comercializadoraGas ?? '',
        valorFaturaGas: effective.valorFaturaGas ?? '',
        faturaPresenteGas: effective.faturaPresenteGas ?? '',
        comercializadoraEletricidade: effective.comercializadoraEletricidade ?? '',
        valorFaturaEletricidade: effective.valorFaturaEletricidade ?? '',
        faturaPresenteEletricidade: effective.faturaPresenteEletricidade ?? ''
      });
    }
  }, [selectedMarcacaoEvent, isEditingMarcacao]);

  // Sync edit Fecho form when selectedFechoEvent changes
  useEffect(() => {
    if (selectedFechoEvent && !isEditingFecho) {
      const fd = selectedFechoEvent.formData || {};
      let dateStr = '';
      if (selectedFechoEvent.date) {
        const d = selectedFechoEvent.date;
        dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else if (fd.marcacaoData) {
        const parts = String(fd.marcacaoData).split('/');
        if (parts.length === 3) dateStr = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        else dateStr = fd.marcacaoData;
      }
      setEditFechoFormData({
        marcacaoData: dateStr,
        horaInicio: fd.horaInicio || selectedFechoEvent.time || '',
        horaFim: fd.horaFim || selectedFechoEvent.duration || '',
        marcacaoDescription: fd.marcacaoDescription || '',
        produto: fd.produto || selectedFechoEvent.product || '',
        nomeEmpresa: fd.nomeEmpresa || '',
        nomeResponsavel: fd.nomeResponsavel || '',
        morada: fd.morada || selectedFechoEvent.location || '',
        contactoCliente: fd.contactoCliente || '',
        codigoPostal1: fd.codigoPostal1 || '',
        codigoPostal2: fd.codigoPostal2 || '',
        linkGoogleMaps: fd.linkGoogleMaps || '',
        marcacaoUsers: Array.isArray(fd.marcacaoUsers) ? fd.marcacaoUsers.map((u: any) => String(u)) : []
      });
    }
  }, [selectedFechoEvent, isEditingFecho]);

  // Validate marcação edit form
  const validateMarcacaoEditForm = () => {
    const errors: any = {};
    const formData = editMarcacaoFormData || {};
    
    // Validate date (mandatory)
    if (!formData.marcacaoData || formData.marcacaoData.trim() === '') {
      errors.marcacaoData = 'Data é obrigatória';
    }
    
    // Validate times (mandatory)
    if (!formData.horaInicio || formData.horaInicio.trim() === '') {
      errors.horaInicio = 'Hora de início é obrigatória';
    }
    
    if (!formData.horaFim || formData.horaFim.trim() === '') {
      errors.horaFim = 'Hora de fim é obrigatória';
    }
    
    // Validate hora_fim > hora_inicio
    if (formData.horaInicio && formData.horaFim) {
      const horaInicio = formData.horaInicio.split(':');
      const horaFim = formData.horaFim.split(':');
      if (horaInicio.length === 2 && horaFim.length === 2) {
        const inicioMinutes = parseInt(horaInicio[0]) * 60 + parseInt(horaInicio[1]);
        const fimMinutes = parseInt(horaFim[0]) * 60 + parseInt(horaFim[1]);
        if (fimMinutes <= inicioMinutes) {
          errors.horaFim = 'Hora de fim deve ser posterior à hora de início';
        }
      }
    }
    
    // Validate users (mandatory - at least one)
    if (!formData.marcacaoUsers || formData.marcacaoUsers.length === 0) {
      errors.marcacaoUsers = 'Comercial / Comerciais é obrigatório';
    }
    
    // Validate produto(s) (at least one)
    const formProdutos: string[] = (formData.produtos && Array.isArray(formData.produtos))
      ? (formData.produtos as string[])
      : (formData.produto ? [formData.produto] : []);
    if (formProdutos.length === 0 || (!formData.produto && !formData.produtos?.length)) {
      errors.produto = 'Pelo menos um produto é obrigatório';
    }
    
    // Validate client fields (mandatory)
    if (!formData.nomeEmpresa || formData.nomeEmpresa.trim() === '') {
      errors.nomeEmpresa = 'Nome da empresa é obrigatório';
    }
    
    if (!formData.nomeResponsavel || formData.nomeResponsavel.trim() === '') {
      errors.nomeResponsavel = 'Nome do responsável é obrigatório';
    }
    
    if (!formData.morada || formData.morada.trim() === '') {
      errors.morada = 'Morada é obrigatória';
    }
    
    if (!formData.contactoCliente || formData.contactoCliente.trim() === '') {
      errors.contactoCliente = 'Contacto do cliente é obrigatório';
    }
    
    // Product-specific validation: validate fields for each product in the marcação
    formProdutos.forEach((p: string) => {
      if (p === 'bairro-solar') {
        if (!formData.jaTemPaineis) errors.jaTemPaineis = 'Já tem painéis é obrigatório';
        if (!formData.potencia1035) errors.potencia1035 = 'Potência 13.80Kw é obrigatória';
        if (!formData.trifasico) errors.trifasico = 'Trifásico é obrigatório';
        if (!formData.tipoTelhado) errors.tipoTelhado = 'Tipo de telhado é obrigatório';
        if (!formData.faturaPresenteBairro) errors.faturaPresenteBairro = 'Fatura presente é obrigatória';
      } else if (p === 'pap') {
        if (!formData.jaTemPaineisPAP) errors.jaTemPaineisPAP = 'Já tem painéis é obrigatório';
        if (!formData.potencia69) errors.potencia69 = 'Potência 6.9Kw é obrigatória';
        if (!formData.tipoTelhadoPAP) errors.tipoTelhadoPAP = 'Tipo de telhado é obrigatório';
        if (!formData.pagaMais250) errors.pagaMais250 = 'Paga mais que 250€ é obrigatório';
        if (!formData.faturaPresentePAP) errors.faturaPresentePAP = 'Fatura presente é obrigatória';
      } else if (p === 'gas') {
        if (formData.gasNatural !== true) errors.gasNatural = 'Gás natural é obrigatório';
        if (!formData.comercializadoraGas) errors.comercializadoraGas = 'Comercializadora é obrigatória';
        if (!formData.valorFaturaGas) errors.valorFaturaGas = 'Valor da fatura é obrigatório';
        if (!formData.faturaPresenteGas) errors.faturaPresenteGas = 'Fatura presente é obrigatória';
      } else if (p === 'eletricidade') {
        if (!formData.comercializadoraEletricidade) errors.comercializadoraEletricidade = 'Comercializadora é obrigatória';
        if (!formData.valorFaturaEletricidade) errors.valorFaturaEletricidade = 'Valor da fatura é obrigatório';
        if (!formData.faturaPresenteEletricidade) errors.faturaPresenteEletricidade = 'Fatura presente é obrigatória';
      }
    });
    
    return errors;
  };

  // Handle save Marcação changes
  const handleSaveMarcacao = async () => {
    if (!selectedMarcacaoEvent) return;
    
    // Validate mandatory fields first
    const validationErrors = validateMarcacaoEditForm();
    if (Object.keys(validationErrors).length > 0) {
      setEditMarcacaoValidationErrors(validationErrors);
      setEditMarcacaoNotification({ 
        message: `Por favor, corrija os seguintes erros: ${Object.values(validationErrors).join(', ')}`, 
        type: 'error' 
      });
      setTimeout(() => setEditMarcacaoNotification(null), 5000);
      return;
    }
    
    // Clear validation errors if validation passes
    setEditMarcacaoValidationErrors({});
    
    setSavingMarcacao(true);
    try {
      // Extract marcação ID from selected event
      const formData = selectedMarcacaoEvent.formData || {};
      const marcacaoId = formData.marcacaoId || selectedMarcacaoEvent.id?.replace('marcacao-', '');
      
      if (!marcacaoId) {
        setSavingMarcacao(false);
        setEditMarcacaoNotification({ message: 'ID da marcação não encontrado.', type: 'error' });
        setTimeout(() => setEditMarcacaoNotification(null), 4000);
        return;
      }
      
      // Build payload - include all fields from editMarcacaoFormData
      // Ensure marcacaoUsers is an array of strings (backend will convert to ints)
      const normalizedUserIds = (editMarcacaoFormData.marcacaoUsers || []).map((uid: any) => String(uid));
      const editProdutosList: string[] = (editMarcacaoFormData.produtos && Array.isArray(editMarcacaoFormData.produtos))
        ? (editMarcacaoFormData.produtos as string[])
        : (editMarcacaoFormData.produto ? [editMarcacaoFormData.produto] : []);
      const payload: any = {
        marcacaoData: editMarcacaoFormData.marcacaoData,
        horaInicio: editMarcacaoFormData.horaInicio,
        horaFim: editMarcacaoFormData.horaFim,
        marcacaoUsers: normalizedUserIds,
        marcacaoDescription: editMarcacaoFormData.marcacaoDescription || undefined,
        produto: editMarcacaoFormData.produto,
        produtos: editProdutosList,
        nomeEmpresa: editMarcacaoFormData.nomeEmpresa,
        nomeResponsavel: editMarcacaoFormData.nomeResponsavel,
        morada: editMarcacaoFormData.morada,
        contactoCliente: editMarcacaoFormData.contactoCliente,
        codigoPostal1: editMarcacaoFormData.codigoPostal1 || undefined,
        codigoPostal2: editMarcacaoFormData.codigoPostal2 || undefined,
        linkGoogleMaps: editMarcacaoFormData.linkGoogleMaps || undefined
      };
      // Add product-specific fields for every product in the marcação
      if (editProdutosList.includes('bairro-solar')) {
        payload.jaTemPaineis = editMarcacaoFormData.jaTemPaineis;
        payload.potencia1035 = editMarcacaoFormData.potencia1035;
        payload.trifasico = editMarcacaoFormData.trifasico;
        payload.tipoTelhado = editMarcacaoFormData.tipoTelhado;
        payload.faturaPresenteBairro = editMarcacaoFormData.faturaPresenteBairro;
      }
      if (editProdutosList.includes('pap')) {
        payload.jaTemPaineisPAP = editMarcacaoFormData.jaTemPaineisPAP;
        payload.potencia69 = editMarcacaoFormData.potencia69;
        payload.tipoTelhadoPAP = editMarcacaoFormData.tipoTelhadoPAP;
        payload.pagaMais250 = editMarcacaoFormData.pagaMais250;
        payload.faturaPresentePAP = editMarcacaoFormData.faturaPresentePAP;
      }
      if (editProdutosList.includes('gas')) {
        payload.gasNatural = editMarcacaoFormData.gasNatural;
        payload.comercializadoraGas = editMarcacaoFormData.comercializadoraGas;
        payload.valorFaturaGas = editMarcacaoFormData.valorFaturaGas;
        payload.faturaPresenteGas = editMarcacaoFormData.faturaPresenteGas;
      }
      if (editProdutosList.includes('eletricidade')) {
        payload.comercializadoraEletricidade = editMarcacaoFormData.comercializadoraEletricidade;
        payload.valorFaturaEletricidade = editMarcacaoFormData.valorFaturaEletricidade;
        payload.faturaPresenteEletricidade = editMarcacaoFormData.faturaPresenteEletricidade;
      }
      
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const res = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      const json = await res.json().catch(() => ({} as any));
      
      if (!res.ok || !json?.success) {
        setSavingMarcacao(false);
        setEditMarcacaoNotification({ message: `Erro ao atualizar marcação: ${json?.message || 'Tente novamente.'}`, type: 'error' });
        setTimeout(() => setEditMarcacaoNotification(null), 4000);
        return;
      }
      
      // Refresh events from backend
      await loadEvents();
      
      setIsEditingMarcacao(false);
      setSavingMarcacao(false);
      setEditMarcacaoNotification({ message: 'Marcação atualizada com sucesso!', type: 'success' });
      setTimeout(() => setEditMarcacaoNotification(null), 4000);
    } catch (error) {
      setSavingMarcacao(false);
      setEditMarcacaoNotification({ message: 'Erro de rede: Não foi possível atualizar a marcação.', type: 'error' });
      setTimeout(() => setEditMarcacaoNotification(null), 4000);
    }
  };

  // Handle delete Marcação
  const handleDeleteMarcacao = async () => {
    if (!selectedMarcacaoEvent) return;
    
    setDeletingMarcacao(true);
    try {
      // Extract marcação ID from selected event
      const formData = selectedMarcacaoEvent.formData || {};
      const marcacaoId = formData.marcacaoId || selectedMarcacaoEvent.id?.replace('marcacao-', '');
      
      if (!marcacaoId) {
        setDeletingMarcacao(false);
        setEditMarcacaoNotification({ message: 'ID da marcação não encontrado.', type: 'error' });
        setTimeout(() => setEditMarcacaoNotification(null), 4000);
        return;
      }

      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const response = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro ao eliminar marcação: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Close modals
      setShowMarcacaoDeleteConfirm(false);
      setShowMarcacaoEventDetailModal(false);
      setSelectedMarcacaoEvent(null);
      setIsEditingMarcacao(false);
      setEditMarcacaoNotification(null);
      
      // Reload events to reflect deletion
      if (selectedUser) {
        await loadEvents();
      }
      
      // Show success message
      setMarcacaoNotification({ 
        message: 'Marcação eliminada e arquivada com sucesso!', 
        type: 'success' 
      });
      setTimeout(() => setMarcacaoNotification(null), 3000);
      
    } catch (error: any) {
      setEditMarcacaoNotification({ 
        message: `Erro ao eliminar marcação: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
      setTimeout(() => setEditMarcacaoNotification(null), 4000);
    } finally {
      setDeletingMarcacao(false);
    }
  };

  const handleSaveFecho = async () => {
    if (!selectedFechoEvent) return;
    const formData = selectedFechoEvent.formData || {};
    const marcacaoId = formData.marcacaoId || selectedFechoEvent.id?.replace('marcacao-', '');
    if (!marcacaoId) return;
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    if (!token) return;
    setSavingMarcacao(true);
    try {
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const payload = {
        marcacaoData: editFechoFormData.marcacaoData?.includes('/') ? (() => { const p = editFechoFormData.marcacaoData.split('/'); return `${p[2]}-${p[1].padStart(2, '0')}-${p[0].padStart(2, '0')}`; })() : editFechoFormData.marcacaoData,
        horaInicio: editFechoFormData.horaInicio,
        horaFim: editFechoFormData.horaFim,
        marcacaoUsers: (editFechoFormData.marcacaoUsers || []).map((u: any) => String(u)),
        nomeEmpresa: editFechoFormData.nomeEmpresa,
        nomeResponsavel: editFechoFormData.nomeResponsavel,
        morada: editFechoFormData.morada,
        contactoCliente: editFechoFormData.contactoCliente,
        codigoPostal1: editFechoFormData.codigoPostal1,
        codigoPostal2: editFechoFormData.codigoPostal2,
        linkGoogleMaps: editFechoFormData.linkGoogleMaps,
        produto: editFechoFormData.produto,
        marcacaoDescription: editFechoFormData.marcacaoDescription
      };
      const res = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const result = await res.json().catch(() => ({} as any));
      if (!res.ok || !result.success) {
        setEditFechoNotification({ message: result.message || result.error || 'Erro ao guardar', type: 'error' });
        return;
      }
      setIsEditingFecho(false);
      setEditFechoNotification({ message: 'Fecho atualizado com sucesso', type: 'success' });
      if (selectedUser) await loadEvents();
    } catch (e: any) {
      setEditFechoNotification({ message: e.message || 'Erro ao guardar', type: 'error' });
    } finally {
      setSavingMarcacao(false);
    }
  };

  const handleDeleteFecho = async () => {
    if (!selectedFechoEvent) return;
    const formData = selectedFechoEvent.formData || {};
    const marcacaoId = formData.marcacaoId || selectedFechoEvent.id?.replace('marcacao-', '');
    if (!marcacaoId) return;
    setDeletingFecho(true);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const res = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes/${marcacaoId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      if (!res.ok) throw new Error('Erro ao eliminar');
      setShowFechoDeleteConfirm(false);
      setShowFechoEventDetailModal(false);
      setSelectedFechoEvent(null);
      if (selectedUser) await loadEvents();
    } catch (e: any) {
      setEditFechoNotification({ message: e.message || 'Erro ao eliminar', type: 'error' });
    } finally {
      setDeletingFecho(false);
    }
  };

  const goToDate = (year: number, month: number, day?: number) => {
    const newDate = new Date(year, month, day || 1);
    setCurrentDate(newDate);
    if (day) {
      setSelectedDate(newDate);
      setViewMode('daily');
    }
    setShowDatePicker(false);
    setShowYearPicker(false);
  };

  // Function to handle event search and navigate to matching event
  const handleEventSearch = async (searchTerm: string) => {
    if (!searchTerm || !searchTerm.trim()) {
      setActiveSearchTerm(null);
      return;
    }
    
    // Only navigate in calendar view, not table view
    if (displayMode !== 'calendar') {
      setActiveSearchTerm(null);
      return;
    }
    
    const trimmedTerm = searchTerm.trim();
    
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    let matchingEvent: CalendarEvent | null = null;
    let eventUserId: string | null = null;
    
    try {
      // Search directly in ALL events from database without any filters
      // Step 1: Search in all marcação events (no user_id, no date filters)
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const marcaçõesUrl = `${backendUrl}/api/callcenter/calendar/marcacoes`;
      // No user_id parameter - get all events across all users
      // No date_from and date_to parameters - get all events regardless of date
      
      const marcaçõesRes = await fetch(marcaçõesUrl, {
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      });
      
      if (marcaçõesRes.ok) {
        const marcaçõesJson = await marcaçõesRes.json();
        const allMarcações = Array.isArray(marcaçõesJson?.marcacoes) ? marcaçõesJson.marcacoes : [];
        
        // Map and search marcação events
        for (const m of allMarcações) {
          const userIds = (m.form_data?.marcacaoUsers || [m.form_data?.marcacaoUser] || []).filter(Boolean);
          const userIdsStr = userIds.map((uid: any) => String(uid));
          
          // Parse date - API returns YYYY-MM-DD format in 'date' field or 'marcacaoData' in form_data
          const marcacaoDateStr = m.date || m.form_data?.marcacaoData || m.marcacao_data || '';
          let marcacaoDate: Date | null = null;
          
          if (marcacaoDateStr) {
            // Try YYYY-MM-DD format first (ISO format from API)
            if (marcacaoDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              marcacaoDate = new Date(marcacaoDateStr + 'T00:00:00');
            } 
            // Try DD/MM/YYYY format (legacy format)
            else {
              const parts = marcacaoDateStr.split('/');
              if (parts.length === 3) {
                marcacaoDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              }
            }
          }
          
          if (!marcacaoDate || isNaN(marcacaoDate.getTime())) {
            continue;
          }
          
          // Get marcacaoId from root level (marcacao_id) or form_data
          const marcacaoId = m.marcacao_id || 
                            m.marcacaoId || 
                            m.form_data?.marcacaoId || 
                            m.form_data?.marcacao_id || '';
          
          // Format date as DD/MM/YYYY for display consistency
          const formattedDateStr = marcacaoDate.toLocaleDateString('pt-PT').replace(/\//g, '/');
          const dateParts = formattedDateStr.split('/');
          const ddmmYYYYDate = dateParts.length === 3 ? `${dateParts[0]}/${dateParts[1]}/${dateParts[2]}` : marcacaoDateStr;
          
          const formData = {
            ...m.form_data,
            marcacaoUsers: userIdsStr,
            marcacaoUser: userIdsStr[0] || '',
            marcacaoId: marcacaoId,
            marcacaoData: ddmmYYYYDate,
            horaInicio: m.form_data?.horaInicio || m.hora_inicio || '',
            horaFim: m.form_data?.horaFim || m.hora_fim || ''
          };
          
          const event: CalendarEvent = {
            id: `marcacao-${m.id}`,
            date: marcacaoDate,
            title: `Marcação ${marcacaoId || m.id}`,
            type: 'marcacao' as const,
            time: formData.horaInicio || '00:00',
            duration: formData.horaFim || '00:00',
            status: 'scheduled' as const,
            product: formData.produto || 'unknown',
            formData: formData
          };
          
          // Test the match function
          const matchResult = isEventMatch(event, trimmedTerm);
          
          if (matchResult) {
            matchingEvent = event;
            eventUserId = userIdsStr[0] || null;
            break;
          }
        }
      }
      
      // Step 2: If not found in marcações, search in bloqueios
      if (!matchingEvent) {
        const bloqueiosUrl = `${backendUrl}/api/callcenter/calendar/bloqueios`;
        
        const bloqueiosRes = await fetch(bloqueiosUrl, {
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
        });
        
        if (bloqueiosRes.ok) {
          const bloqueiosJson = await bloqueiosRes.json();
          const allBloqueios = Array.isArray(bloqueiosJson?.bloqueios) ? bloqueiosJson.bloqueios : [];
          
          for (const b of allBloqueios) {
            const bloqueioDateStr = b.form_data?.bloqueioData || b.bloqueio_data || '';
            let bloqueioDate: Date | null = null;
            if (bloqueioDateStr) {
              const parts = bloqueioDateStr.split('/');
              if (parts.length === 3) {
                bloqueioDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              }
            }
            
            if (!bloqueioDate) continue;
            
            const event: CalendarEvent = {
              id: `bloqueio-${b.id}`,
              date: bloqueioDate,
              title: `Bloqueio ${b.id}`,
              type: 'bloqueio' as const,
              time: b.form_data?.horaInicio || '00:00',
              duration: b.form_data?.horaFim || '00:00',
              status: 'blocked' as const,
              formData: {
                ...b.form_data,
                bloqueioId: b.bloqueio_id || b.form_data?.bloqueioId || '',
                bloqueioData: bloqueioDateStr,
                horaInicio: b.form_data?.horaInicio || '',
                horaFim: b.form_data?.horaFim || ''
              }
            };
            
            if (isEventMatch(event, trimmedTerm)) {
              matchingEvent = event;
              // For bloqueios, get user from form_data if available
              eventUserId = b.form_data?.marcacaoUser ? String(b.form_data.marcacaoUser) : null;
              break;
            }
          }
        }
      }
      
      // Step 3: If still not found, search in rotas
      if (!matchingEvent) {
        const rotasUrl = `${backendUrl}/api/callcenter/calendar/rotas`;
        
        const rotasRes = await fetch(rotasUrl, {
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
        });
        
        if (rotasRes.ok) {
          const rotasJson = await rotasRes.json();
          const allRotas = Array.isArray(rotasJson?.rotas) ? rotasJson.rotas : [];
          
          for (const r of allRotas) {
            const dateParts = r.date.split('-');
            const rotaDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            
            const event: CalendarEvent = {
              id: `rota-${r.id}`,
              date: rotaDate,
              title: r.title || r.form_data?.title || 'Rota',
              type: 'rota' as const,
              time: '00:00',
              duration: '00:00',
              status: 'scheduled' as const,
              formData: {
                ...r.form_data,
                data: r.date,
                rotaId: r.rota_id
              }
            };
            
            if (isEventMatch(event, trimmedTerm)) {
              matchingEvent = event;
              // For rotas, get first comercial from comerciais array
              const comerciais = r.form_data?.comerciais || [];
              eventUserId = comerciais.length > 0 ? String(comerciais[0]) : null;
              break;
            }
          }
        }
      }
    } catch (error) {
      setActiveSearchTerm(null);
      setSelectedDate(null);
      return;
    }
    
    // Step 4: Navigate to the found event and switch to the owning user
    if (matchingEvent && matchingEvent.date) {
      const eventDate = new Date(matchingEvent.date);
      
      // Switch to the user who owns the event if found
      if (eventUserId) {
        const eventUser = comercialUsers.find(u => String(u.id) === eventUserId);
        if (eventUser) {
          if (!selectedUser || String(selectedUser.id) !== eventUserId) {
            setSelectedUser(eventUser);
          }
        }
      }
      
      // Navigate to the date containing the event based on current view mode
      if (viewMode === 'monthly') {
        // Navigate to the month containing the event
        const newDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1);
        setCurrentDate(newDate);
        setSelectedDate(eventDate);
      } else if (viewMode === 'weekly') {
        // Navigate to the week containing the event
        // Get the Sunday of the week containing the event (week starts on Sunday)
        const dayOfWeek = eventDate.getDay();
        const sunday = new Date(eventDate);
        sunday.setDate(eventDate.getDate() - dayOfWeek); // Subtract dayOfWeek to get Sunday
        sunday.setHours(0, 0, 0, 0);
        setCurrentDate(sunday);
        setSelectedDate(eventDate);
      } else if (viewMode === 'daily') {
        // Navigate to the day containing the event
        setCurrentDate(eventDate);
        setSelectedDate(eventDate);
      }
      
      // Set active search term for highlighting
      setActiveSearchTerm(trimmedTerm);
    } else {
      // Clear active search term if no event found
      setActiveSearchTerm(null);
      setSelectedDate(null);
    }
  };

  const runEventSearchWithNotification = async (term: string) => {
    const trimmedTerm = term?.trim();
    if (!trimmedTerm) {
      setActiveSearchTerm(null);
      setSelectedDate(null);
      return;
    }

    setSearchInProgress(true);
    setActiveSearchTerm(trimmedTerm);
    try {
      await handleEventSearch(trimmedTerm);
    } catch (error) {
      setActiveSearchTerm(null);
    } finally {
      setSearchInProgress(false);
    }
  };

  const getEventsForDate = (date: Date) => {
    const allEvents = [...sampleEvents, ...fetchedEvents, ...bloqueioEvents];
    
    const filteredEvents = allEvents.filter(event => {
      if (!event.date) return false;
      if (event.date.toDateString() !== date.toDateString()) return false;
      
      // For Bloqueio events: include if user is in marcacaoUsers (or selected user filter)
      if (event.type === 'bloqueio') {
        if (!isAdminOrGerenteComercial() && currentUserId) {
          const userIdStr = String(currentUserId);
          const userIds = event.formData?.marcacaoUsers?.map((id: any) => String(id)) || [];
          if (userIds.length && !userIds.includes(userIdStr)) return false;
        } else if (selectedUser) {
          const selectedUserIdStr = String(selectedUser.id);
          const userIds = event.formData?.marcacaoUsers?.map((id: any) => String(id)) || [];
          if (userIds.length && !userIds.includes(selectedUserIdStr)) return false;
        }
        return true;
      }
      
      // For regular Comercial users (not Admin/Gerente), only show their own events
      if (!isAdminOrGerenteComercial() && currentUserId) {
        const userIdStr = String(currentUserId);
        // Check marcacaoUsers array (for multi-user marcações/shared events)
        if (event.formData?.marcacaoUsers && Array.isArray(event.formData.marcacaoUsers)) {
          const userIds = event.formData.marcacaoUsers.map((id: any) => String(id));
          if (!userIds.includes(userIdStr)) {
            return false;
          }
        }
        // Check single marcacaoUser (for backward compatibility)
        else if (event.formData?.marcacaoUser) {
          if (String(event.formData.marcacaoUser) !== userIdStr) {
            return false;
          }
        }
        // For marcação/fecho events, if no user assignment found, exclude it
        else if (event.type === 'marcacao' || event.type === 'fecho' || event.formData?.marcacaoId) {
          return false;
        }
      }
      // For Admin/Gerente users, filter by selected user if one is selected
      else if (selectedUser) {
        // Check marcacaoUsers array (for multi-user marcações/shared events)
        if (event.formData?.marcacaoUsers && Array.isArray(event.formData.marcacaoUsers)) {
          const userIds = event.formData.marcacaoUsers.map((id: any) => String(id));
          const selectedUserIdStr = String(selectedUser.id);
          if (!userIds.includes(selectedUserIdStr)) {
            return false;
          }
        }
        // Check single marcacaoUser (for backward compatibility)
        else if (event.formData?.marcacaoUser) {
          if (String(event.formData.marcacaoUser) !== String(selectedUser.id)) {
            return false;
          }
        }
        // For marcação/fecho events, if no user assignment found, exclude it
        else if (event.type === 'marcacao' || event.type === 'fecho' || event.formData?.marcacaoId) {
          return false;
        }
      }
      
      return true;
    });

    // Sort events by start time (earlier events first)
    return filteredEvents.sort((a, b) => {
      const timeA = a.formData?.horaInicio || a.time || '00:00';
      const timeB = b.formData?.horaInicio || b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // Validation functions for Bloqueio form
  const validateBloqueioForm = () => {
    const errors: any = {};
    
    if (!bloqueioFormData.marcacaoData) errors.marcacaoData = 'Data é obrigatória';
    if (!bloqueioFormData.horaInicio) errors.horaInicio = 'Hora de início é obrigatória';
    if (!bloqueioFormData.horaFim) errors.horaFim = 'Hora de fim é obrigatória';
    if (!bloqueioFormData.marcacaoUsers || bloqueioFormData.marcacaoUsers.length === 0) {
      errors.marcacaoUsers = 'Comercial / Comerciais é obrigatório';
    }
    
    return errors;
  };

  // Validation functions for Rota form
  const validateRotaForm = () => {
    const errors: any = {};
    
    if (!rotaFormData.data) errors.data = 'Data é obrigatória';
    if (!rotaFormData.comerciais || rotaFormData.comerciais.length === 0) {
      errors.comerciais = 'Comercial / Comerciais é obrigatório';
    }
    if (!rotaFormData.linkGoogleMaps) errors.linkGoogleMaps = 'Link da Rota no Google Maps é obrigatório';
    
    return errors;
  };

  // Validation functions for marcação form
  const validateStep = (step: number) => {
    const errors: any = {};
    
    if (step === 1) {
      // Agendamento step
      if (!marcacaoFormData.marcacaoData) errors.marcacaoData = 'Data é obrigatória';
      if (!marcacaoFormData.horaInicio) errors.horaInicio = 'Hora de início é obrigatória';
      if (!marcacaoFormData.horaFim) errors.horaFim = 'Hora de fim é obrigatória';
      // marcacaoUsers is auto-filled based on user role and selected calendar, no need to validate
    } else if (step === 2) {
      // Produto step
      if (!marcacaoFormData.produto) errors.produto = 'Produto é obrigatório';
    } else if (step === 3) {
      // Cliente step
      if (!marcacaoFormData.nomeEmpresa) errors.nomeEmpresa = 'Nome da empresa é obrigatório';
      if (!marcacaoFormData.nomeResponsavel) errors.nomeResponsavel = 'Nome do responsável é obrigatório';
      if (!marcacaoFormData.morada) errors.morada = 'Morada é obrigatória';
      if (!marcacaoFormData.contactoCliente) errors.contactoCliente = 'Contacto do cliente é obrigatório';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentMarcacaoStep)) {
      setCurrentMarcacaoStep(currentMarcacaoStep + 1);
      setValidationErrors({}); // Clear errors when moving to next step
    }
  };

  const handlePrevStep = () => {
    setCurrentMarcacaoStep(Math.max(1, currentMarcacaoStep - 1));
    setValidationErrors({}); // Clear errors when going back
  };

  // Handle marcação form submission
  const handleMarcacaoSubmit = async () => {
    setSavingMarcacao(true);
    setMarcacaoNotification(null);
    
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      if (!token) {
        setMarcacaoNotification({ message: 'Erro: Token de autenticação não encontrado', type: 'error' });
        setTimeout(() => setMarcacaoNotification(null), 4000);
        setSavingMarcacao(false);
        return;
      }

      // Prepare data for backend API - convert date format if needed
      let marcacaoData = marcacaoFormData.marcacaoData;
      
      // Convert DD/MM/YYYY to YYYY-MM-DD if needed
      if (marcacaoData && marcacaoData.includes('/')) {
        const parts = marcacaoData.split('/');
        if (parts.length === 3) {
          marcacaoData = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }

      // Ensure marcacaoUsers is set based on user role and selected calendar
      // If not set, auto-fill it now
      let userIds = marcacaoFormData.marcacaoUsers || [];
      if (!userIds || userIds.length === 0) {
        if (isAdminOrGerenteComercial()) {
          // Coordenador/Gerente: use selectedUser
          if (selectedUser) {
            userIds = [selectedUser.id];
          }
        } else {
          // Regular user: use currentUserId
          if (currentUserId) {
            userIds = [currentUserId];
          }
        }
      }
      
      // Prepare submission data
      // Normalize marcacaoUsers to strings (backend will convert to ints)
      const normalizedUserIds = userIds.map((uid: any) => String(uid));
      const submissionData = {
        // Step 1: Agendamento
        marcacaoData: marcacaoData,
        horaInicio: marcacaoFormData.horaInicio,
        horaFim: marcacaoFormData.horaFim,
        marcacaoUsers: normalizedUserIds,
        marcacaoDescription: marcacaoFormData.marcacaoDescription || '',
        
        // Step 2: Produto
        produto: marcacaoFormData.produto,
        
        // Step 3: Cliente
        nomeEmpresa: marcacaoFormData.nomeEmpresa,
        nomeResponsavel: marcacaoFormData.nomeResponsavel,
        morada: marcacaoFormData.morada,
        contactoCliente: marcacaoFormData.contactoCliente,
        codigoPostal1: marcacaoFormData.codigoPostal1 || '',
        codigoPostal2: marcacaoFormData.codigoPostal2 || '',
        localidade: marcacaoFormData.localidade || '',
        linkGoogleMaps: marcacaoFormData.linkGoogleMaps || '',
        
        // Source: indicate this is from Comercial calendar
        source: 'comercial'
      };

      // Call backend API to create marcação event
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const apiUrl = `${backendUrl}/api/callcenter/calendar/marcacoes`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json().catch(() => ({} as any));

      if (!response.ok || !result.success) {
        setMarcacaoNotification({ 
          message: `Erro ao criar marcação: ${result.message || result.error || 'Tente novamente.'}`, 
          type: 'error' 
        });
        setTimeout(() => setMarcacaoNotification(null), 4000);
        setSavingMarcacao(false);
        return;
      }

      // Reload events from backend
      if (selectedUser) {
        setIsLoadingEvents(true);
        try {
          await Promise.all([loadEvents(), loadRotaEvents()]);
        } catch (error) {
        } finally {
          setIsLoadingEvents(false);
        }
      }
      
      // Close modal and reset form
      setShowMarcacaoModal(false);
      setCurrentMarcacaoStep(1);
      setMarcacaoFormData({});
      setValidationErrors({});
      
      // Show success message
      setMarcacaoNotification({ message: 'Marcação criada com sucesso!', type: 'success' });
      setTimeout(() => setMarcacaoNotification(null), 3000);
    } catch (error: any) {
      setMarcacaoNotification({ 
        message: `Erro ao criar marcação: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
      setTimeout(() => setMarcacaoNotification(null), 4000);
    } finally {
      setSavingMarcacao(false);
    }
  };

  // Fecho modal: validate step 1 Agendamento, step 2 Cliente (no Produto step)
  const validateFechoStep = (step: number) => {
    const errors: any = {};
    if (step === 1) {
      if (!fechoFormData.marcacaoData) errors.marcacaoData = 'Data é obrigatória';
      if (!fechoFormData.horaInicio) errors.horaInicio = 'Hora de início é obrigatória';
      if (!fechoFormData.horaFim) errors.horaFim = 'Hora de fim é obrigatória';
      if (fechoFormData.horaInicio && fechoFormData.horaFim && fechoFormData.horaInicio >= fechoFormData.horaFim) {
        errors.horaFim = 'Hora de fim deve ser posterior à hora de início';
      }
    } else if (step === 2) {
      if (!fechoFormData.nomeEmpresa) errors.nomeEmpresa = 'Nome da empresa é obrigatório';
      if (!fechoFormData.nomeResponsavel) errors.nomeResponsavel = 'Nome do responsável é obrigatório';
      if (!fechoFormData.morada) errors.morada = 'Morada é obrigatória';
      if (!fechoFormData.contactoCliente) errors.contactoCliente = 'Contacto do cliente é obrigatório';
    }
    setFechoValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFechoSubmit = async () => {
    if (!selectedMarcacaoForFecho?.formData?.marcacaoId) {
      setFechoNotification({ message: 'Marcação de origem não encontrada', type: 'error' });
      return;
    }
    setSavingFecho(true);
    setFechoNotification(null);
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      if (!token) {
        setFechoNotification({ message: 'Erro: Token de autenticação não encontrado', type: 'error' });
        setSavingFecho(false);
        return;
      }
      let marcacaoData = fechoFormData.marcacaoData;
      if (marcacaoData && marcacaoData.includes('/')) {
        const parts = marcacaoData.split('/');
        if (parts.length === 3) {
          marcacaoData = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      let userIds = fechoFormData.marcacaoUsers || [];
      if (!userIds.length && selectedUser) userIds = [selectedUser.id];
      if (!userIds.length && currentUserId) userIds = [String(currentUserId)];
      // Build payload: spread fechoFormData first so eventType/sourceMarcacaoId are never overwritten
      const submissionData = {
        ...fechoFormData,
        marcacaoData,
        horaInicio: fechoFormData.horaInicio,
        horaFim: fechoFormData.horaFim,
        marcacaoUsers: userIds.map((uid: any) => String(uid)),
        marcacaoDescription: fechoFormData.marcacaoDescription || '',
        produto: fechoFormData.produto,
        nomeEmpresa: fechoFormData.nomeEmpresa,
        nomeResponsavel: fechoFormData.nomeResponsavel,
        morada: fechoFormData.morada,
        contactoCliente: fechoFormData.contactoCliente,
        codigoPostal1: fechoFormData.codigoPostal1 || '',
        codigoPostal2: fechoFormData.codigoPostal2 || '',
        localidade: fechoFormData.localidade || '',
        linkGoogleMaps: fechoFormData.linkGoogleMaps || '',
        source: 'comercial',
        eventType: 'fecho',
        sourceMarcacaoId: selectedMarcacaoForFecho.formData.marcacaoId
      };
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const response = await fetch(`${backendUrl}/api/callcenter/calendar/marcacoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(submissionData)
      });
      const result = await response.json().catch(() => ({} as any));
      if (!response.ok || !result.success) {
        setFechoNotification({ message: result.message || result.error || 'Erro ao criar Fecho', type: 'error' });
        setSavingFecho(false);
        return;
      }
      if (selectedUser) {
        setIsLoadingEvents(true);
        try {
          await Promise.all([loadEvents(), loadRotaEvents()]);
        } catch (_) {}
        setIsLoadingEvents(false);
      }
      setShowFechoModal(false);
      setCurrentFechoStep(1);
      setFechoFormData({});
      setSelectedMarcacaoForFecho(null);
      setFechoValidationErrors({});
      setFechoNotification({ message: 'Fecho criado com sucesso!', type: 'success' });
      setTimeout(() => setFechoNotification(null), 3000);
    } catch (error: any) {
      setFechoNotification({ message: error.message || 'Erro ao criar Fecho', type: 'error' });
    } finally {
      setSavingFecho(false);
    }
  };

  const getEventsForTimeSlot = (hour: number) => {
    // Only show events for October 2025
    const isOctober2025 = currentDate.getFullYear() === 2025 && currentDate.getMonth() === 9;
    if (!isOctober2025) return [];
    
    return sampleEvents.filter(event => {
      if (!event.date) return false;
      const eventDate = event.date;
      const currentDateStr = currentDate.toDateString();
      const eventDateStr = eventDate.toDateString();
      
      if (currentDateStr !== eventDateStr) return false;
      
      const eventHour = parseInt(event.time.split(':')[0]);
      if (eventHour !== hour) return false;
      
      // Filter by selected user if one is selected
      if (selectedUser && event.formData?.marcacaoUsers) {
        return event.formData.marcacaoUsers.includes(selectedUser.id);
      }
      if (selectedUser && event.formData?.marcacaoUser) {
        return event.formData.marcacaoUser === selectedUser.id;
      }
      
      return true;
    });
  };

  // Calculate event positioning and height based on start and end times
  const getEventStyle = (event: CalendarEvent) => {
    if (!event.formData?.horaInicio || !event.formData?.horaFim) {
      return {};
    }

    const startTime = event.formData.horaInicio;
    const endTime = event.formData.horaFim;
    
    // Parse start and end times
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    // Calculate position and height in pixels
    const slotHeight = 60; // Each hour slot is 60px high
    const minutesPerPixel = 60 / slotHeight; // 1 pixel = 1 minute
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    const durationMinutes = endMinutes - startMinutes;
    
    const top = (startMinutes % 60) * minutesPerPixel;
    const height = durationMinutes * minutesPerPixel;
    
    return {
      top: `${top}px`,
      height: `${height}px`,
      position: 'absolute' as const,
      left: '0',
      right: '0',
      margin: '0',
      zIndex: 2
    };
  };

  // Calculate absolute positioning for the full-day overlay in daily/weekly view
  const getEventStyleForDay = (
    event: CalendarEvent,
    firstDisplayedHour: number = 0,
    slotHeight: number = 60,
    col: number = 0,
    cols: number = 1,
  ): React.CSSProperties | {} => {
    const range = getEventRangeMinutes(event);
    if (!range) {
      return {};
    }

    const pixelsPerMinute = slotHeight / 60;
    const firstDisplayedMinutes = firstDisplayedHour * 60;
    const durationMinutes = Math.max(0, range.end - range.start);
    const adjustedTop = Math.max(0, range.start - firstDisplayedMinutes);
    const topStr = `${adjustedTop * pixelsPerMinute}px`;
    const heightStr = `${durationMinutes * pixelsPerMinute}px`;

    const safeCols = Math.max(1, cols);
    const safeCol = Math.min(Math.max(0, col), safeCols - 1);

    const style: React.CSSProperties = {
      position: 'absolute',
      top: topStr,
      height: heightStr,
      margin: '0',
      zIndex: 2,
      // CSS vars: weekly/mobile !important rules read these for top/height/columns
      ['--event-top' as any]: topStr,
      ['--event-height' as any]: heightStr,
      ['--event-col' as any]: String(safeCol),
      ['--event-cols' as any]: String(safeCols),
      // Desktop daily: share the content area after the 80px time-label gutter
      left: `calc(80px + (100% - 80px) * ${safeCol} / ${safeCols} + 2px)`,
      width: `calc((100% - 80px) / ${safeCols} - 4px)`,
      right: 'auto',
    };

    return style;
  };


  const selectUser = (userId: string) => {
    const user = comercialUsers.find(u => u.id === userId);
    if (user) {
      // Create a new object reference to ensure React detects the change
      setSelectedUser({ ...user });
      setComercialUsers(users => 
        users.map(u => ({ ...u, visible: u.id === userId }))
      );
    }
  };

  const filteredUsers = comercialUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleScenariosMode = () => {
    setScenariosMode(!scenariosMode);
    if (scenariosMode) {
      // Reset selections when exiting scenarios mode
      setSelectedStatus('');
      setSelectedLocation('');
      setSelectedDays([]);
    }
  };

  const handleDayClick = (date: Date) => {
    if (!scenariosMode || !selectedUser) return;
    
    const dateString = date.toDateString();
    const isSelected = selectedDays.some(day => day.toDateString() === dateString);
    
    if (isSelected) {
      setSelectedDays(prev => prev.filter(day => day.toDateString() !== dateString));
    } else {
      setSelectedDays(prev => [...prev, date]);
    }
  };

  const applyScenarios = async () => {
    if (!selectedUser || selectedDays.length === 0 || !selectedStatus || !selectedLocation) return;

    const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
    const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
    
    try {
      // Format days as YYYY-MM-DD strings
      const daysFormatted = selectedDays.map(day => {
        const year = day.getFullYear();
        const month = String(day.getMonth() + 1).padStart(2, '0');
        const dayNum = String(day.getDate()).padStart(2, '0');
        return `${year}-${month}-${dayNum}`;
      });

      const response = await fetch(`${backendUrl}/api/callcenter/calendar/scenarios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          status: selectedStatus,
          location: selectedLocation,
          days: daysFormatted,
          hour: '09:00',
          duration_minutes: 480
        })
      });

      const json = await response.json().catch(() => ({} as any));

      if (!response.ok || !json?.success) {
        alert(`Erro ao criar cenários: ${json?.message || 'Erro desconhecido'}`);
        return;
      }

      // Reload events to show the new scenarios
      await loadEvents();

      // Reset after applying
      setSelectedDays([]);
      setSelectedStatus('');
      setSelectedLocation('');
      setScenariosMode(false);

      // Show success message
      alert(`Cenários criados com sucesso para ${daysFormatted.length} dia(s)!`);
    } catch (error: any) {
      alert(`Erro ao criar cenários: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const isDaySelected = (date: Date) => {
    return selectedDays.some(day => day.toDateString() === date.toDateString());
  };

  // Check if user can edit/delete events (Cenário, Rota, and all event modals)
  // Allows Coordenador, Supervisor, Admin, and Gerente Comercial
  const canEditEvents = () => {
    try {
      // Helper function to check if a role string matches Coordenador/Supervisor patterns
      // This matches the logic from CallCenterCalendar.tsx exactly
      const isCoordinatorOrSupervisorRole = (roleStr: string, tenantStr: string = ''): boolean => {
        if (!roleStr || roleStr.trim() === '') return false;
        
        const roleLower = roleStr.toLowerCase().trim();
        const tenantLower = tenantStr.toLowerCase().trim();
        
        // Check for coordenador/coordinator (Portuguese or English)
        if (roleLower.includes('coordenador') || roleLower.includes('coordinator')) {
          return true;
        }
        
        // Check for supervisor
        if (roleLower.includes('supervisor')) {
          return true;
        }
        
        // Check for Call Center Manager (Coordenador equivalent) - backend stores this
        if (roleLower.includes('call center manager')) {
          return true;
        }
        
        // Check for Call Center Team Lead (Supervisor equivalent)
        if (roleLower.includes('call center team lead') || roleLower.includes('team lead')) {
          return true;
        }
        
        // If tenant is CallCenter, check for manager role (Call Center Manager)
        if (tenantLower === 'callcenter' || tenantLower === 'call center') {
          if (roleLower.includes('manager')) {
            return true;
          }
        }
        
        return false;
      };
      
      // FIRST: Check cognito_user from localStorage (most reliable source)
      const cognitoUser = localStorage.getItem('cognito_user');
      if (cognitoUser) {
        try {
          const user = JSON.parse(cognitoUser);
          
          // Check UserAttributes if present (Cognito custom attributes)
          if (Array.isArray(user?.UserAttributes)) {
            const attrs = user.UserAttributes.reduce((acc: any, a: any) => { 
              acc[a.Name] = a.Value; 
              return acc; 
            }, {});
            const attrTenant = String(attrs['custom:tenant'] || '').toLowerCase();
            const attrRole = String(attrs['custom:role'] || '').toLowerCase();
            
            // Check if Admin from attributes
            if (attrTenant === 'admin' || attrRole.includes('admin')) return true;
            
            // Check if CallCenter with specific roles: Coordenador or Supervisor (matches CallCenterCalendar exactly)
            if (attrTenant === 'callcenter' || attrTenant === 'call center') {
              // Match CallCenterCalendar exactly - only check coordenador and supervisor in UserAttributes
              if (attrRole.includes('coordenador') || attrRole.includes('supervisor')) return true;
            }
            
            // Check if Comercial Gerente from attributes
            if (attrTenant === 'comercial' || attrTenant === 'commercial') {
              if (attrRole.includes('gerente') || attrRole.includes('gestor') || 
                  attrRole.includes('director') || attrRole.includes('commercial director')) return true;
            }
          }
          
          const role = String(user?.role || '').toLowerCase();
          const tenant = String(user?.tenant || '').toLowerCase();
          
          if (role.includes('admin') || tenant === 'admin') return true;
          
          // Check if CallCenter with specific roles: Coordenador or Supervisor (matches CallCenterCalendar)
          if (tenant === 'callcenter' || tenant === 'call center') {
            // Get role from UserAttributes if available, otherwise use direct role (matches CallCenterCalendar)
            let attrsForRole: any = {};
            if (Array.isArray(user?.UserAttributes)) {
              user.UserAttributes.forEach((a: any) => { attrsForRole[a.Name] = a.Value; });
            }
            const userRoleFromAttr = String(user?.role || attrsForRole?.['custom:role'] || role || '').toLowerCase();
            // Match CallCenterCalendar exactly - check coordenador and supervisor
            // Also check for manager (Call Center Manager = Coordenador)
            if (userRoleFromAttr.includes('coordenador') || userRoleFromAttr.includes('supervisor') || 
                userRoleFromAttr.includes('manager')) {
              return true;
            }
          }
          
          // Check if Comercial Gerente
          if (tenant === 'comercial' || tenant === 'commercial') {
            if (role.includes('gerente') || role.includes('gestor') || 
                role.includes('director') || role.includes('commercial director')) {
              return true;
            }
          }
        } catch (e) {
          console.error('[canEditEvents] Error parsing cognito_user:', e);
          // Continue to other checks if cognito_user parse fails
        }
      }
      
      // Check localStorage for basic tenant/role info
      // Try multiple sources: userRole/userTenant (legacy), user object, user_tenant, cognito_user
      let userRole = localStorage.getItem('userRole') || '';
      let userTenant = localStorage.getItem('userTenant') || '';
      
      // Try to get from 'user' object
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userObj = JSON.parse(storedUser);
          if (!userRole && userObj?.role) userRole = userObj.role;
          if (!userTenant && userObj?.tenant) userTenant = userObj.tenant;
        } catch (e) {
          console.error('[canEditEvents] Error parsing user object:', e);
        }
      }
      
      // Try to get from 'user_tenant' (correct key)
      if (!userTenant) {
        const userTenantStored = localStorage.getItem('user_tenant');
        if (userTenantStored) userTenant = userTenantStored;
      }
      
      const roleLower = userRole.toLowerCase();
      const tenantLower = userTenant.toLowerCase();
      
      // Check if Admin from localStorage
      if (roleLower.includes('admin') || tenantLower === 'admin') {
        return true;
      }
      
      // Check if Coordenador or Supervisor from localStorage (any tenant)
      const localStorageCheck = isCoordinatorOrSupervisorRole(userRole, userTenant);
      if (localStorageCheck) {
        return true;
      }
      
      // Check currentUserData (from API)
      if (currentUserData) {
        const apiUserRole = String(currentUserData.role || '');
        const apiUserTenant = String(currentUserData.tenant || currentUserData.business_area || '');
        const apiUserRoleLower = apiUserRole.toLowerCase();
        const apiUserTenantLower = apiUserTenant.toLowerCase();
        
        if (apiUserRoleLower.includes('admin') || apiUserTenantLower === 'admin') return true;
        
        const apiCheck = isCoordinatorOrSupervisorRole(apiUserRole, apiUserTenant);
        if (apiCheck) return true;
        
        if (apiUserTenantLower === 'comercial' || apiUserTenantLower === 'commercial') {
          if (apiUserRoleLower.includes('gerente') || apiUserRoleLower.includes('gestor') || 
              apiUserRoleLower.includes('director') || apiUserRoleLower.includes('commercial director')) return true;
        }
      }
      
      if (tenantLower === 'callcenter' || tenantLower === 'call center') {
        if (isCoordinatorOrSupervisorRole(userRole, userTenant)) return true;
      }
      
      if (tenantLower === 'comercial' || tenantLower === 'commercial') {
        if (roleLower.includes('gerente') || roleLower.includes('gestor') || 
            roleLower.includes('director') || roleLower.includes('commercial director')) return true;
      }
      
      return false;
    } catch (e) {
      console.error('[canEditEvents] Exception:', e);
      return false;
    }
  };

  // Check if user can edit Marcação events - only Gerente in comercial tenant (or Admin)
  const canEditMarcacaoEvents = () => {
    try {
      // Check currentUserData first (from API)
      if (currentUserData) {
        const userRole = String(currentUserData.role || '').toLowerCase();
        const userTenant = String(currentUserData.tenant || currentUserData.business_area || '').toLowerCase();
        
        // Check if Admin
        if (userRole.includes('admin') || userTenant === 'admin' || userRole.includes('administrator')) {
          return true;
        }
        
        // Check if Gerente in comercial tenant
        if (userTenant === 'comercial' || userTenant === 'commercial') {
          // Only allow Gerente role (not other comercial roles)
          if (userRole.includes('gerente') || userRole.includes('gestor')) {
            return true;
          }
        }
      }
      
      // Check localStorage for basic tenant/role info
      const userRole = localStorage.getItem('userRole') || '';
      const tenant = localStorage.getItem('user_tenant') || '';
      const tenantLower = tenant.toLowerCase();
      const roleLower = userRole.toLowerCase();
      
      // Check if Admin from localStorage
      if (roleLower.includes('admin') || tenantLower === 'admin') {
        return true;
      }
      
      // Check if Gerente in comercial tenant from localStorage
      if (tenantLower === 'comercial' || tenantLower === 'commercial') {
        // Only allow Gerente role (not other comercial roles)
        if (roleLower.includes('gerente') || roleLower.includes('gestor')) {
          return true;
        }
      }
      
      // Check cognito_user from localStorage
      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        const user = JSON.parse(cognitoUserRaw);
        const role = String(user?.role || '').toLowerCase();
        const tenant = String(user?.tenant || '').toLowerCase();
        
        // Check if Admin
        if (role.includes('admin') || tenant === 'admin') {
          return true;
        }
        
        // Check UserAttributes if present
        if (Array.isArray(user?.UserAttributes)) {
          const attrs = user.UserAttributes.reduce((acc: any, a: any) => { 
            acc[a.Name] = a.Value; 
            return acc; 
          }, {});
          const attrTenant = String(attrs['custom:tenant'] || '').toLowerCase();
          const attrRole = String(attrs['custom:role'] || '').toLowerCase();
          
          // Check if Admin from attributes
          if (attrTenant === 'admin' || attrRole.includes('admin')) {
            return true;
          }
          
          // Check if Gerente in comercial tenant from attributes
          if (attrTenant === 'comercial' || attrTenant === 'commercial') {
            // Only allow Gerente role (not other comercial roles)
            if (attrRole.includes('gerente') || attrRole.includes('gestor')) {
              return true;
            }
          }
        }
        
        // Check if Gerente in comercial tenant from direct role
        if (tenant === 'comercial' || tenant === 'commercial') {
          const userRoleFromAttr = String(user?.role || '').toLowerCase();
          // Only allow Gerente role (not other comercial roles)
          if (userRoleFromAttr.includes('gerente') || userRoleFromAttr.includes('gestor')) {
            return true;
          }
        }
      }
    } catch (e) {
      console.error('Error checking canEditMarcacaoEvents:', e);
    }
    
    return false;
  };

  // Validate Scenario edit form
  const validateScenarioEditForm = () => {
    const errors: any = {};
    
    // Validate date (mandatory)
    if (!editScenarioDate || editScenarioDate.trim() === '') {
      errors.date = 'Data é obrigatória';
    }
    
    // Validate text (mandatory)
    if (!editScenarioText || !editScenarioText.trim()) {
      errors.text = 'Texto do Cenário é obrigatório';
    }
    
    // Validate assigned_to (mandatory - at least one)
    if (!editScenarioAssignedTo || editScenarioAssignedTo.length === 0) {
      errors.assignedTo = 'Comercial / Comerciais é obrigatório';
    }
    
    return errors;
  };

  // Validate Rota edit form
  const validateRotaEditForm = () => {
    const errors: any = {};
    
    // Validate date (mandatory)
    if (!editRotaDate || editRotaDate.trim() === '') {
      errors.data = 'Data é obrigatória';
    }
    
    // Validate comerciais (mandatory - at least one)
    if (!editRotaComerciais || editRotaComerciais.length === 0) {
      errors.comerciais = 'Comercial / Comerciais é obrigatório';
    }
    
    // Validate links (at least one URL is required)
    const links = editRotaLinks || [];
    let hasValidLink = false;
    for (let i = 0; i < links.length; i++) {
      if (links[i].url && links[i].url.trim()) {
        hasValidLink = true;
        try {
          new URL(links[i].url);
        } catch (e) {
          errors.links = `Link ${i + 1}: URL inválida`;
          break;
        }
      }
    }
    if (!hasValidLink) {
      errors.links = 'Pelo menos um link é obrigatório';
    }
    
    return errors;
  };

  // Handle save Scenario changes
  const handleSaveScenario = async () => {
    if (!selectedStatusEvent) return;
    
    // Validate mandatory fields first
    const validationErrors = validateScenarioEditForm();
    if (Object.keys(validationErrors).length > 0) {
      setEditScenarioValidationErrors(validationErrors);
      setScenarioNotification({ 
        message: `Por favor, corrija os seguintes erros: ${Object.values(validationErrors).join(', ')}`, 
        type: 'error' 
      });
      setTimeout(() => setScenarioNotification(null), 5000);
      return;
    }
    
    // Clear validation errors if validation passes
    setEditScenarioValidationErrors({});
    
    setSavingScenario(true);
    try {
      // Extract scenario activity ID from selected event
      let scenarioActivityId: number | null = null;
      
      // Try to extract from scenario_id format (CN-YYNNNNN) or numeric ID
      if (selectedStatusEvent.id) {
        if (selectedStatusEvent.id.startsWith('scenario-')) {
          const idStr = selectedStatusEvent.id.replace('scenario-', '');
          scenarioActivityId = parseInt(idStr, 10);
        } else if (selectedStatusEvent.id.startsWith('CN-')) {
          // Find the activity ID from backend by scenario_id
          const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
          const userId = (editScenarioAssignedTo && editScenarioAssignedTo.length > 0) ? editScenarioAssignedTo[0] : selectedStatusEvent.userId;
          const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
          const url = new URL('/api/activities', backendUrl);
          url.searchParams.set('user_id', String(userId));
          
          const activitiesRes = await fetch(url.toString(), {
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
          });
          
          if (activitiesRes.ok) {
            const activitiesJson = await activitiesRes.json();
            const activities = Array.isArray(activitiesJson?.activities) ? activitiesJson.activities : [];
            const scenarioActivity = activities.find((a: any) => 
              a && a.activity_type === 'scenario' && 
              (a.scenario_id === selectedStatusEvent.id || String(a.id) === selectedStatusEvent.id)
            );
            
            if (scenarioActivity && scenarioActivity.id) {
              scenarioActivityId = parseInt(String(scenarioActivity.id), 10);
            }
          }
        } else {
          scenarioActivityId = parseInt(selectedStatusEvent.id, 10);
        }
      }
      
      // If still no ID, try to find from fetched events
      if (!scenarioActivityId || isNaN(scenarioActivityId)) {
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        const userId = (editScenarioAssignedTo && editScenarioAssignedTo.length > 0) ? editScenarioAssignedTo[0] : selectedStatusEvent.userId;
        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const url = new URL('/api/activities', backendUrl);
        url.searchParams.set('user_id', String(userId));
        
        const activitiesRes = await fetch(url.toString(), {
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
        });
        
        if (activitiesRes.ok) {
          const activitiesJson = await activitiesRes.json();
          const activities = Array.isArray(activitiesJson?.activities) ? activitiesJson.activities : [];
          const scenarioActivity = activities.find((a: any) => 
            a && a.activity_type === 'scenario' && 
            (a.scenario_id === selectedStatusEvent.id || String(a.id) === selectedStatusEvent.id)
          );
          
          if (scenarioActivity && scenarioActivity.id) {
            scenarioActivityId = parseInt(String(scenarioActivity.id), 10);
          }
        }
      }
      
      if (!scenarioActivityId || isNaN(scenarioActivityId)) {
        setSavingScenario(false);
        setScenarioNotification({ message: 'ID do cenário inválido.', type: 'error' });
        setTimeout(() => setScenarioNotification(null), 4000);
        return;
      }

      // Build payload for updating linked scenarios (matching CallCenterCalendar)
      // Backend expects assigned_to_users array to link multiple users
      const scenarioText = editScenarioText.trim();
      const payload: any = {
        status: scenarioText, // Send as status (backend maps to day_type)
        title: scenarioText, // Also send as title to explicitly update the title field
        location: '', // Empty location - no longer used in scenarios
        assigned_to_users: editScenarioAssignedTo.map(id => parseInt(id, 10)) // Send all user IDs
      };
      
      // Add due_date if date provided
      if (editScenarioDate) {
        const dateObj = new Date(editScenarioDate);
        dateObj.setHours(12, 0, 0, 0);
        payload.due_date = dateObj.toISOString();
      }
      
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      
      // Update all linked scenarios
      const res = await fetch(`${backendUrl}/api/callcenter/calendar/scenarios/${scenarioActivityId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      const json = await res.json().catch(() => ({} as any));
      
      if (!res.ok || !json?.success) {
        setSavingScenario(false);
        setScenarioNotification({ message: `Erro ao atualizar cenário: ${json?.message || 'Tente novamente.'}`, type: 'error' });
        setTimeout(() => setScenarioNotification(null), 4000);
        return;
      }
      
      // Small delay to ensure backend has committed changes
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Reload events to reflect changes
      await loadEvents();
      
      // Extract updated text from backend response (matching CallCenterCalendar)
      let updatedText = editScenarioText.trim();
      if (json.activity) {
        // Try to get the updated text from the backend response
        const activityTitle = json.activity.title || '';
        if (activityTitle) {
          // Extract text from title (remove "Cenário:" prefix if present)
          if (activityTitle.includes(':')) {
            const titleParts = activityTitle.split(':');
            if (titleParts.length > 1) {
              updatedText = titleParts[1].trim();
            } else {
              updatedText = activityTitle;
            }
          } else {
            updatedText = activityTitle;
          }
        }
        
        // Update selectedStatusEvent with new data from backend
        setSelectedStatusEvent({
          ...selectedStatusEvent,
          text: updatedText,
          userId: editScenarioAssignedTo[0],
          date: new Date(editScenarioDate)
        });
        
        // Also update the edit text state immediately
        setEditScenarioText(updatedText);
      }
      
      setIsEditingScenario(false);
      setSavingScenario(false);
      setScenarioNotification({ 
        message: `Cenário atualizado com sucesso para ${editScenarioAssignedTo.length} utilizador(es).`, 
        type: 'success' 
      });
      setTimeout(() => setScenarioNotification(null), 3000);
    } catch (error) {
      setSavingScenario(false);
      setScenarioNotification({ message: 'Erro de rede: Não foi possível atualizar o cenário.', type: 'error' });
      setTimeout(() => setScenarioNotification(null), 4000);
    }
  };

  // Handle delete Scenario
  const handleDeleteScenario = async () => {
    if (!selectedStatusEvent) return;
    
    setDeletingScenario(true);
    try {
      // Extract scenario activity ID from selected event
      let scenarioActivityId: number | null = null;
      
      // Try to extract from scenario_id format (CN-YYNNNNN) or numeric ID
      if (selectedStatusEvent.id) {
        if (selectedStatusEvent.id.startsWith('scenario-')) {
          const idStr = selectedStatusEvent.id.replace('scenario-', '');
          scenarioActivityId = parseInt(idStr, 10);
        } else if (selectedStatusEvent.id.startsWith('CN-')) {
          // Find the activity ID from backend by scenario_id
          const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
          const userId = selectedStatusEvent.userId;
          const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
          const url = new URL('/api/activities', backendUrl);
          url.searchParams.set('user_id', String(userId));
          
          const activitiesRes = await fetch(url.toString(), {
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
          });
          
          if (activitiesRes.ok) {
            const activitiesJson = await activitiesRes.json();
            const activities = Array.isArray(activitiesJson?.activities) ? activitiesJson.activities : [];
            const scenarioActivity = activities.find((a: any) => 
              a && a.activity_type === 'scenario' && 
              (a.scenario_id === selectedStatusEvent.id || String(a.id) === selectedStatusEvent.id)
            );
            
            if (scenarioActivity && scenarioActivity.id) {
              scenarioActivityId = parseInt(String(scenarioActivity.id), 10);
            }
          }
        } else {
          scenarioActivityId = parseInt(selectedStatusEvent.id, 10);
        }
      }
      
      // If still no ID, try to find from fetched events
      if (!scenarioActivityId || isNaN(scenarioActivityId)) {
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        const userId = selectedStatusEvent.userId;
        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const url = new URL('/api/activities', backendUrl);
        url.searchParams.set('user_id', String(userId));
        
        const activitiesRes = await fetch(url.toString(), {
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
        });
        
        if (activitiesRes.ok) {
          const activitiesJson = await activitiesRes.json();
          const activities = Array.isArray(activitiesJson?.activities) ? activitiesJson.activities : [];
          const scenarioActivity = activities.find((a: any) => 
            a && a.activity_type === 'scenario' && 
            (a.scenario_id === selectedStatusEvent.id || String(a.id) === selectedStatusEvent.id)
          );
          
          if (scenarioActivity && scenarioActivity.id) {
            scenarioActivityId = parseInt(String(scenarioActivity.id), 10);
          }
        }
      }
      
      if (!scenarioActivityId || isNaN(scenarioActivityId)) {
        setDeletingScenario(false);
        setScenarioNotification({ message: 'ID do cenário inválido.', type: 'error' });
        setTimeout(() => setScenarioNotification(null), 4000);
        return;
      }

      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      
      const response = await fetch(`${backendUrl}/api/callcenter/calendar/scenarios/${scenarioActivityId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro ao eliminar cenário: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Close modals
      setShowScenarioDeleteConfirm(false);
      setShowStatusEventDetailModal(false);
      setSelectedStatusEvent(null);
      
      // Reload events to reflect deletion
      await loadEvents();
      
      // Show success message
      setScenarioNotification({ 
        message: 'Cenário eliminado e arquivado com sucesso!', 
        type: 'success' 
      });
      setTimeout(() => setScenarioNotification(null), 3000);
      
    } catch (error: any) {
      setScenarioNotification({ 
        message: `Erro ao eliminar cenário: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
      setTimeout(() => setScenarioNotification(null), 4000);
    } finally {
      setDeletingScenario(false);
    }
  };

  // Handle save Rota changes
  const handleSaveRota = async () => {
    if (!selectedRotaEvent) return;
    
    // Validate mandatory fields first
    const validationErrors = validateRotaEditForm();
    if (Object.keys(validationErrors).length > 0) {
      setEditRotaValidationErrors(validationErrors);
      setRotaNotification({ 
        message: `Por favor, corrija os seguintes erros: ${Object.values(validationErrors).join(', ')}`, 
        type: 'error' 
      });
      setTimeout(() => setRotaNotification(null), 5000);
      return;
    }
    
    // Clear validation errors if validation passes
    setEditRotaValidationErrors({});
    
    setSavingRota(true);
    try {
      // Extract rota ID from selected event
      // The id is in format "rota-{numeric_id}" where numeric_id is the database ID
      let rotaEventId: number | null = null;
      
      // Try to extract from id format "rota-{id}"
      if (selectedRotaEvent.id) {
        const idMatch = String(selectedRotaEvent.id).match(/rota-(\d+)/);
        if (idMatch) {
          rotaEventId = parseInt(idMatch[1], 10);
        } else {
          // Try direct parse if it's just a number
          const directId = parseInt(String(selectedRotaEvent.id), 10);
          if (!isNaN(directId)) {
            rotaEventId = directId;
          }
        }
      }
      
      // If we still don't have an ID, try to get it from the backend using rota_id
      if (!rotaEventId || isNaN(rotaEventId)) {
        const rotaIdStr = selectedRotaEvent.rotaId || selectedRotaEvent.formData?.rotaId;
        if (rotaIdStr && rotaIdStr.startsWith('RT-')) {
          const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
          const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
          const response = await fetch(`${backendUrl}/api/callcenter/calendar/rotas?rota_id=${encodeURIComponent(rotaIdStr)}`, {
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.rotas && data.rotas.length > 0 && data.rotas[0].id) {
              rotaEventId = parseInt(String(data.rotas[0].id), 10);
            }
          }
        }
      }
      
      if (!rotaEventId || isNaN(rotaEventId)) {
        setSavingRota(false);
        setRotaNotification({ message: 'ID da rota inválido.', type: 'error' });
        setTimeout(() => setRotaNotification(null), 4000);
        return;
      }

      // Build payload (matching CallCenterCalendar - links array)
      const labels = ['Início da Rota', 'Continuação'];
      const links = (editRotaLinks || []).map((link: any, index: number) => ({
        url: link.url || '',
        label: labels[index] || link.label || (index === 0 ? 'Início da Rota' : 'Continuação')
      })).filter((link: any) => link.url.trim() !== '');
      
      const payload: any = {
        data: editRotaDate,
        comerciais: editRotaComerciais,
        links: links
      };
      
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      const res = await fetch(`${backendUrl}/api/callcenter/calendar/rotas/${rotaEventId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      
      const json = await res.json().catch(() => ({} as any));
      
      if (!res.ok || !json?.success) {
        setSavingRota(false);
        setRotaNotification({ message: `Erro ao atualizar rota: ${json?.message || 'Tente novamente.'}`, type: 'error' });
        setTimeout(() => setRotaNotification(null), 4000);
        return;
      }
      
      // Reload events to reflect changes
      await loadRotaEvents();
      if (selectedUser) {
        loadEvents();
      }
      
      // Update selectedRotaEvent with new data (including links)
      if (json.rota) {
        const updatedDate = json.rota.date ? new Date(json.rota.date) : null;
        const updatedUserIds = (json.rota.form_data?.comerciais || []).map((uid: number) => String(uid));
        const updatedLinks = json.rota.links || json.rota.form_data?.links || [];
        const updatedLink = json.rota.link_google_maps || json.rota.form_data?.linkGoogleMaps || '';
        
        setSelectedRotaEvent({
          ...selectedRotaEvent,
          date: updatedDate,
          userIds: updatedUserIds,
          links: updatedLinks,
          linkGoogleMaps: updatedLink,
          formData: {
            ...selectedRotaEvent.formData,
            ...json.rota.form_data,
            data: json.rota.date,
            comerciais: updatedUserIds,
            links: updatedLinks,
            linkGoogleMaps: updatedLink
          }
        });
      }
      
      setIsEditingRota(false);
      setSavingRota(false);
      setRotaNotification({ 
        message: 'Rota atualizada com sucesso!', 
        type: 'success' 
      });
      setTimeout(() => setRotaNotification(null), 3000);
    } catch (error) {
      setSavingRota(false);
      setRotaNotification({ message: 'Erro de rede: Não foi possível atualizar a rota.', type: 'error' });
      setTimeout(() => setRotaNotification(null), 4000);
    }
  };

  // Handle delete Rota
  const handleDeleteRota = async () => {
    if (!selectedRotaEvent) return;
    
    setDeletingRota(true);
    try {
      // Extract rota ID from selected event
      // The id is in format "rota-{numeric_id}" where numeric_id is the database ID
      let rotaEventId: number | null = null;
      
      // Try to extract from id format "rota-{id}"
      if (selectedRotaEvent.id) {
        const idMatch = String(selectedRotaEvent.id).match(/rota-(\d+)/);
        if (idMatch) {
          rotaEventId = parseInt(idMatch[1], 10);
        } else {
          // Try direct parse if it's just a number
          const directId = parseInt(String(selectedRotaEvent.id), 10);
          if (!isNaN(directId)) {
            rotaEventId = directId;
          }
        }
      }
      
      // If we still don't have an ID, try to get it from the backend using rota_id
      if (!rotaEventId || isNaN(rotaEventId)) {
        const rotaIdStr = selectedRotaEvent.rotaId || selectedRotaEvent.formData?.rotaId;
        if (rotaIdStr && rotaIdStr.startsWith('RT-')) {
          const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
          const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
          const response = await fetch(`${backendUrl}/api/callcenter/calendar/rotas?rota_id=${encodeURIComponent(rotaIdStr)}`, {
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.rotas && data.rotas.length > 0 && data.rotas[0].id) {
              rotaEventId = parseInt(String(data.rotas[0].id), 10);
            }
          }
        }
      }
      
      if (!rotaEventId || isNaN(rotaEventId)) {
        setDeletingRota(false);
        setRotaNotification({ message: 'ID da rota inválido.', type: 'error' });
        setTimeout(() => setRotaNotification(null), 4000);
        return;
      }

      const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
      const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
      
      const response = await fetch(`${backendUrl}/api/callcenter/calendar/rotas/${rotaEventId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        let errorMessage = `Erro ao eliminar rota (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = `Erro ao eliminar rota: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      // Close modals
      setShowRotaDeleteConfirm(false);
      setShowRotaEventDetailModal(false);
      setSelectedRotaEvent(null);
      
      // Reload events to reflect deletion (always reload, even if already deleted)
      await loadRotaEvents();
      if (selectedUser) {
        loadEvents();
      }
      
      // Show success message (handle both new deletion and already-deleted cases)
      const message = result.already_deleted 
        ? 'Rota já estava eliminada anteriormente.' 
        : 'Rota eliminada e arquivada com sucesso!';
      setRotaNotification({ 
        message: message, 
        type: 'success' 
      });
      setTimeout(() => setRotaNotification(null), 3000);
      
    } catch (error: any) {
      setRotaNotification({ 
        message: `Erro ao eliminar rota: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
      setTimeout(() => setRotaNotification(null), 4000);
    } finally {
      setDeletingRota(false);
    }
  };

  const canViewComercialUserSidebar = () => {
    try {
      const checkIsOperador = () => {
        // Check currentUserData first
        if (currentUserData) {
          const userRole = String(currentUserData.role || '').toLowerCase();
          if (userRole.includes('operador') || userRole.includes('operator') || userRole.includes('agent')) {
            return true;
          }
        }
        
        // Check localStorage
        const userRole = localStorage.getItem('userRole') || '';
        const roleLower = userRole.toLowerCase();
        if (roleLower.includes('operador') || roleLower.includes('operator') || roleLower.includes('agent')) {
          return true;
        }
        
        // Check cognito_user
        const cognitoUserRaw = localStorage.getItem('cognito_user');
        if (cognitoUserRaw) {
          try {
            const user = JSON.parse(cognitoUserRaw);
            const role = String(user?.role || '').toLowerCase();
            if (role.includes('operador') || role.includes('operator') || role.includes('agent')) {
              return true;
            }
            
            // Check UserAttributes
            if (Array.isArray(user?.UserAttributes)) {
              const attrs = user.UserAttributes.reduce((acc: any, a: any) => { 
                acc[a.Name] = a.Value; 
                return acc; 
              }, {});
              const attrRole = String(attrs['custom:role'] || '').toLowerCase();
              if (attrRole.includes('operador') || attrRole.includes('operator') || attrRole.includes('agent')) {
                return true;
              }
            }
          } catch (e) {
          }
        }
        
        return false;
      };
      
      if (checkIsOperador()) {
        return false;
      }
      
      if (isAdminOrGerenteComercial()) {
        return true;
      }

      if (currentUserData) {
        const userRole = String(currentUserData.role || '').toLowerCase();
        if (userRole.includes('coordenador') || userRole.includes('coordinator') || 
            userRole.includes('supervisor') || userRole.includes('admin') ||
            userRole.includes('gerente') || isComercialGestaoHierarchyRole(currentUserData.role)) {
          return true;
        }
      }
      
      const userRole = localStorage.getItem('userRole') || '';
      const roleLower = userRole.toLowerCase();
      if (roleLower.includes('coordenador') || roleLower.includes('coordinator') || 
          roleLower.includes('supervisor') || roleLower.includes('admin') ||
          roleLower.includes('gerente') || isComercialGestaoHierarchyRole(userRole)) {
        return true;
      }
      
      const cognitoUserRaw = localStorage.getItem('cognito_user');
      if (cognitoUserRaw) {
        try {
          const user = JSON.parse(cognitoUserRaw);
          const role = String(user?.role || '').toLowerCase();
          if (role.includes('coordenador') || role.includes('coordinator') || 
              role.includes('supervisor') || role.includes('admin') ||
              role.includes('gerente') || role.includes('gestor')) {
            return true;
          }
          
          if (Array.isArray(user?.UserAttributes)) {
            const attrs = user.UserAttributes.reduce((acc: any, a: any) => { 
              acc[a.Name] = a.Value; 
              return acc; 
            }, {});
            const attrRole = String(attrs['custom:role'] || '').toLowerCase();
            if (attrRole.includes('coordenador') || attrRole.includes('coordinator') || 
                attrRole.includes('supervisor') || attrRole.includes('admin') ||
                attrRole.includes('gerente') || attrRole.includes('gestor')) {
              return true;
            }
          }
        } catch (e) {
        }
      }
      
      if (isAdminOrGerenteComercial()) {
        return true;
      }
      
      return false;
    } catch (e) {
      return false;
    }
  };

  const handleEspecialidadeClick = (user: ComercialUser, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Always allow clicking to see especialidade modal
    setSelectedUserForEspecialidade(user);
    setSelectedEspecialidades(user.especialidade || []);
    setShowEspecialidadeModal(true);
  };

  const updateUserEspecialidade = async (userId: string, newEspecialidade: string[]) => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        return;
      }

      // Convert userId to number for backend API
      const userIdNum = parseInt(userId, 10);
      if (isNaN(userIdNum)) {
        return;
      }

      // Call backend API to update user especialidade
      const response = await axios.put(
        `${DEPLOYMENT_CONFIG.getBackendUrl()}/api/callcenter/comercial-users/${userIdNum}/especialidade`,
        { especialidade: newEspecialidade },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        // Update local state only after successful backend save
        setComercialUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, especialidade: newEspecialidade } : user
          )
        );
        // Update selectedUser if it's the same user
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(prev => prev ? { ...prev, especialidade: newEspecialidade } : null);
        }
        setShowEspecialidadeModal(false);
        setSelectedUserForEspecialidade(null);
      }
    } catch (error: any) {
    }
  };

  const handleEspecialidadeToggle = (value: string) => {
    setSelectedEspecialidades(prev => {
      if (prev.includes(value)) {
        return prev.filter(esp => esp !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSaveEspecialidade = () => {
    if (selectedUserForEspecialidade) {
      updateUserEspecialidade(selectedUserForEspecialidade.id, selectedEspecialidades);
    }
  };

  // Helper function to get especialidade label and color
  const getEspecialidadeConfig = (especialidade: string): { label: string; color: string } => {
    if (!especialidade || typeof especialidade !== 'string') {
      return { label: '', color: '#999' };
    }
    const configs: { [key: string]: { label: string; color: string } } = {
      'bairro-solar': { label: 'BS', color: '#ff6b35' }, // Orange
      'pap': { label: 'PAP', color: '#4ecdc4' }, // Teal
      'gas': { label: 'GN', color: '#95e1d3' }, // Mint
      'eletricidade': { label: 'EE', color: '#fce38a' } // Yellow
    };
    return configs[especialidade] || { label: especialidade, color: '#999' };
  };

  const getStatusEventsForDate = (date: Date) => {
    if (!selectedUser) return [];
    
    // Combine both example statusEvents and fetched scenario events from backend
    const allStatusEvents = [...statusEvents, ...fetchedScenarioEvents];
    
    return allStatusEvents.filter(event => {
      const eventDate = event.date;
      if (!eventDate) return false;
      // Handle both Date objects and date strings
      const eventDateObj = eventDate instanceof Date ? eventDate : new Date(eventDate);
      if (isNaN(eventDateObj.getTime())) return false;
      // Convert both userIds to strings for comparison (event.userId is already a string, but selectedUser.id might be a number)
      const eventUserId = String(event.userId || '');
      const selectedUserId = String(selectedUser.id || '');
      return eventDateObj.toDateString() === date.toDateString() && eventUserId === selectedUserId;
    });
  };

  const getRotaEventsForDate = (date: Date) => {
    if (!selectedUser) return rotaEvents.filter(event => {
      const eventDate = event.date;
      return eventDate.toDateString() === date.toDateString();
    });
    
    return rotaEvents.filter(event => {
      const eventDate = event.date;
      return eventDate.toDateString() === date.toDateString() && 
             event.userIds.includes(selectedUser.id);
    });
  };

  const renderEventTitle = (title: string) => {
    if (!title) return title;
    
    // Split the title and highlight "Bairro Solar" and "PAP"
    const parts = title.split(/(Bairro Solar|PAP)/g);
    return (
      <>
        {parts.map((part, index) => {
          if (part === 'Bairro Solar' || part === 'PAP') {
            return (
              <span key={index} style={{ color: '#1e40af', fontWeight: '700' }}>
                {part}
              </span>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const renderGhostStatusEvent = () => (
    <div 
      key="ghost-scenario"
      className="status-event scenario ghost-event"
    >
      <div className="status-event-header">Cenário</div>
    </div>
  );

  const renderGhostRotaEvent = () => (
    <div 
      key="ghost-rota"
      className="rota-event ghost-event"
    >
      <div className="rota-event-content">
        <div className="rota-event-icon">🗺️</div>
        <div className="rota-event-info">
          <div className="rota-event-title">ROTA</div>
        </div>
      </div>
    </div>
  );

  const renderStatusEvent = (event: StatusEvent) => (
    <div 
      key={event.id}
      className="status-event scenario"
      onClick={() => {
        setSelectedStatusEvent(event);
        setShowStatusEventDetailModal(true);
      }}
    >
      <div className="status-event-header">{event.text}</div>
    </div>
  );

  const renderRotaEvent = (event: any) => {
    // Get links from event - support both new format (links array) and legacy (linkGoogleMaps)
    const links = event.links || (event.linkGoogleMaps ? [{ url: event.linkGoogleMaps, label: 'Google Maps' }] : []);
    
    return (
      <div 
        key={event.id}
        className="rota-event"
        onClick={(e) => {
          e.stopPropagation();
          // Open detail modal (view mode)
          setSelectedRotaEvent(event);
          setShowRotaEventDetailModal(true);
        }}
      >
        <div className="rota-event-content">
          <div className="rota-event-icon">🗺️</div>
          <div className="rota-event-info">
            <div className="rota-event-title">ROTA</div>
            {links.length > 0 && (
              <div className="rota-event-links" onClick={(e) => e.stopPropagation()}>
                {links.slice(0, 2).map((link: any, index: number) => (
                  <button
                    key={index}
                    className="rota-link-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (link.url) {
                        window.open(link.url, '_blank');
                      }
                    }}
                    style={{
                      marginRight: '6px',
                      marginTop: '6px',
                      padding: '6px 12px',
                      fontSize: '0.875rem',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                      color: '#7f1d1d',
                      border: '2px solid #fca5a5',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '700',
                      boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)',
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                        e.currentTarget.style.borderColor = '#ef4444';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)';
                        e.currentTarget.style.borderColor = '#fca5a5';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.2)';
                      }
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
            {links.length === 0 && (
              <div className="rota-event-subtitle">Google Maps</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleCreateStatusEvent = (user: ComercialUser) => {
    setSelectedUserForStatus(user);
    setShowStatusEventModal(true);
  };

  const renderViewModeButtons = () => (
    <div className="view-mode-buttons">
      <button 
        className={`view-btn ${viewMode === 'daily' ? 'active' : ''}`}
        onClick={() => setViewMode('daily')}
      >
        Diário
      </button>
      <button 
        className={`view-btn ${viewMode === 'weekly' ? 'active' : ''}`}
        onClick={() => setViewMode('weekly')}
      >
        Semanal
      </button>
      <button 
        className={`view-btn ${viewMode === 'monthly' ? 'active' : ''}`}
        onClick={() => setViewMode('monthly')}
      >
        Mensal
      </button>
    </div>
  );

  const renderSubHeader = () => (
    <div className="calendar-sub-header">
      <div className="sub-header-left">
        <button className="nav-btn" onClick={() => navigateDate('prev')}>
          ←
        </button>
        <button className="today-btn" onClick={goToToday}>
          Hoje
        </button>
        <button className="nav-btn" onClick={() => navigateDate('next')}>
          →
        </button>
        <button 
          className="date-picker-btn" 
          onClick={() => setShowDatePicker(!showDatePicker)}
        >
          📅 Mês
        </button>
        <button 
          className="year-picker-btn" 
          onClick={() => setShowYearPicker(!showYearPicker)}
        >
          📆 Ano
        </button>
        <div className="search-container">
          <input
            type="text"
            className="calendar-search"
            placeholder="Pesquisar por ID, Código Postal, Contacto, Empresa"
            value={eventSearchTerm}
            onChange={(e) => {
              setEventSearchTerm(e.target.value);
              // Clear active search when user types new search term
              setActiveSearchTerm(null);
              setSelectedDate(null);
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && eventSearchTerm && eventSearchTerm.trim()) {
                await runEventSearchWithNotification(eventSearchTerm);
              }
            }}
          />
          <button
            className="search-btn"
            onClick={async () => {
              if (eventSearchTerm && eventSearchTerm.trim()) {
                await runEventSearchWithNotification(eventSearchTerm);
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              marginLeft: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) e.currentTarget.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) e.currentTarget.style.backgroundColor = '#3b82f6';
            }}
          >
            Procurar
          </button>
        </div>
      </div>
      <div className="sub-header-right">
        <button
          className="refresh-btn"
          onClick={async () => {
            if (selectedUser) {
              const startTime = Date.now();
              setLoadingStartTime(startTime);
              setIsLoadingEvents(true);
              try {
                await Promise.all([loadEvents(), loadRotaEvents()]);
                
                // Ensure minimum 2 seconds display time
                const elapsedTime = Date.now() - startTime;
                const minimumDisplayTime = 2000; // 2 seconds
                const remainingTime = minimumDisplayTime - elapsedTime;
                
                if (remainingTime > 0) {
                  await new Promise(resolve => setTimeout(resolve, remainingTime));
                }
              } catch (error) {
                // Still ensure minimum display time even on error
                const elapsedTime = Date.now() - startTime;
                const minimumDisplayTime = 2000;
                const remainingTime = minimumDisplayTime - elapsedTime;
                if (remainingTime > 0) {
                  await new Promise(resolve => setTimeout(resolve, remainingTime));
                }
              } finally {
                setIsLoadingEvents(false);
                setLoadingStartTime(null);
              }
            }
          }}
          title="Atualizar calendário"
        >
          🔄 Atualizar
        </button>
        <div className="mode-selector">
          <button 
            className={`mode-btn ${displayMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setDisplayMode('calendar')}
          >
            📅 Calendário
          </button>
          <button 
            className={`mode-btn ${displayMode === 'table' ? 'active' : ''}`}
            onClick={() => setDisplayMode('table')}
          >
            📊 Tabela
          </button>
        </div>
      </div>
    </div>
  );

  const renderCalendarHeader = () => (
    <div className="calendar-header">
      <div className="calendar-nav">
        {/* Sidebar Toggle Button for Admin/Gerente */}
        {isAdminOrGerenteComercial() && (
          <Button
            type="text"
            icon={<TeamOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ 
              fontSize: 20,
              marginRight: 8,
              backgroundColor: '#ff4d4f',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#ff7875';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.4)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ff4d4f';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 77, 79, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          />
        )}
        
        {selectedUser && (
          <button 
            className="nav-btn bloqueio-btn"
            onClick={() => {
              // Auto-fill based on selected user and selected date (Lisbon time)
              const isDayView = viewMode === 'daily';
              const autoFillDate = isDayView ? getLisbonDateString(currentDate) : '';
              
              setBloqueioFormData({
                marcacaoUsers: [selectedUser.id],
                marcacaoData: autoFillDate
              });
              setBloqueioValidationErrors({});
              setShowBloqueioModal(true);
            }}
          >
            Bloqueio
          </button>
        )}
        
        <button 
          className="nav-btn marcacao-btn"
          onClick={() => {
            setShowMarcacaoModal(true);
            setCurrentMarcacaoStep(1);
            
            // Auto-fill data based on current selection
            const initialData: any = {};
            
            // Auto-select user based on role:
            // - If Coordenador/Gerente: use selectedUser (the calendar they're viewing)
            // - If not Coordenador: use currentUserId (their own calendar)
            if (isAdminOrGerenteComercial()) {
              // Coordenador/Gerente can navigate through calendars, use selectedUser
              if (selectedUser) {
                initialData.marcacaoUsers = [selectedUser.id];
              }
            } else {
              // Regular user can only see their own calendar, use currentUserId
              if (currentUserId) {
                initialData.marcacaoUsers = [currentUserId];
              }
            }
            
            // Auto-fill date if in day view (Lisbon time)
            if (viewMode === 'daily') {
              initialData.marcacaoData = getLisbonDateString(currentDate);
            }
            
            setMarcacaoFormData(initialData);
            setValidationErrors({});
            setMarcacaoNotification(null);
          }}
        >
          Marcação
        </button>
      </div>
      <div className="calendar-title">
        {selectedUser ? (
          <div className="title-with-user">
            <div className="user-info-with-level">
              <div className="user-color" style={{ backgroundColor: selectedUser.color }}></div>
              <span className="user-indicator">
                {selectedUser.name}
              </span>
              {(isAdminOrGerenteComercial() || canViewComercialUserSidebar()) && (
                <>
                  <div 
                    className="especialidade-tag" 
                    style={{
                      display: 'flex',
                      gap: '0.25rem',
                      alignItems: 'center',
                      cursor: 'default'
                    }}
                  >
                    {selectedUser.especialidade && selectedUser.especialidade.length > 0 ? (
                      selectedUser.especialidade.filter(esp => esp).map((esp, index) => {
                        const config = getEspecialidadeConfig(esp);
                        if (!config || !config.color) return null;
                        return (
                          <span
                            key={index}
                            className="especialidade-indicator"
                            style={{
                              backgroundColor: config.color,
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {config.label}
                          </span>
                        );
                      }).filter(Boolean)
                    ) : (
                      <span
                        className="especialidade-placeholder"
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          border: '1px dashed #999',
                          color: '#666',
                          backgroundColor: '#f5f5f5',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Especialidade
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <span className="date-info">
              {viewMode === 'daily' && getCurrentDateString()}
              {viewMode === 'weekly' && getCurrentWeekString()}
              {viewMode === 'monthly' && getCurrentMonthString()}
            </span>
          </div>
        ) : (
          <div className="no-user-selected">
            <span>Selecione um utilizador para ver o calendário</span>
          </div>
        )}
      </div>
      {/* Scenarios Mode Controls */}
      {scenariosMode && selectedUser && (
        <div className="scenarios-controls">
          <div className="scenarios-selections">
            <div className="selection-group">
              <label>Status:</label>
              <div className="status-buttons">
                {['Transportes', 'Carro', 'Longe', 'RoadTrip'].map(status => (
                  <button
                    key={status}
                    className={`status-btn ${selectedStatus === status ? 'active' : ''}`}
                    onClick={() => setSelectedStatus(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="selection-group">
              <label>Localização:</label>
              <div className="location-buttons">
                {['Coimbra', 'Lisboa'].map(location => (
                  <button
                    key={location}
                    className={`location-btn ${selectedLocation === location ? 'active' : ''}`}
                    onClick={() => setSelectedLocation(location)}
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="scenarios-actions">
              <div className="selection-info">
                {selectedDays.length > 0 && (
                  <span>{selectedDays.length} dia(s) selecionado(s)</span>
                )}
              </div>
              <button
                className="btn btn-primary"
                onClick={applyScenarios}
                disabled={!selectedStatus || !selectedLocation || selectedDays.length === 0}
              >
                Aplicar Cenário
              </button>
            </div>
          </div>
        </div>
      )}
      
      {renderViewModeButtons()}
    </div>
  );

  const renderDailyView = () => {
    const statusEvents = getStatusEventsForDate(currentDate);
    const rotaEvents = getRotaEventsForDate(currentDate);
    const dayEvents = getEventsForDate(currentDate);
    const hasMatch = dayEvents.some(ev => isEventMatch(ev, eventSearchTerm));
    
    // Always start daily view from 08:00 AM
    const startHour = 8;
    const endHour = 23; // Always show until 23:00
    
    return (
      <div className={`daily-view ${hasMatch ? 'search-match-day' : ''}`}>
        {/* Status Events at Top */}
        {statusEvents.length > 0 && (
          <div className="status-events-container">
            {statusEvents.map(renderStatusEvent)}
          </div>
        )}
        
        {/* Rota Events */}
        {rotaEvents.length > 0 && (
          <div className="rota-events-container">
            {rotaEvents.map(renderRotaEvent)}
          </div>
        )}
        
        <div className="time-slots">
          {Array.from({ length: endHour - startHour + 1 }, (_, i) => {
            const hour = startHour + i;
            return (
              <div 
                key={hour} 
                className={`time-slot ${scenariosMode ? 'clickable' : ''} ${isDaySelected(currentDate) ? 'selected-day' : ''}`}
                onClick={() => {
                  if (scenariosMode) {
                    handleDayClick(currentDate);
                  }
                }}
              >
                <div className="time-label">{hour.toString().padStart(2, '0')}:00</div>
                <div className="time-content" />
              </div>
            );
          })}

          {/* Overlay that renders events spanning across time slots */}
          <div className="day-events-overlay">
            {layoutCalendarEventsInDay(dayEvents).map(({ event, col, cols }) => {
              const eventStyle = getEventStyleForDay(event, startHour, 60, col, cols);
              return (
                <div
                  key={event.id}
                  role="button"
                  tabIndex={0}
                  className={`event ${event.type === 'fecho' ? 'marcacao' : event.type}-event ${event.formData?.isRescheduled ? 'rescheduled-event' : ''} ${event.formData?.isNotOkCallCenter ? 'not-ok-callcenter-event' : ''} ${event.formData?.isNotOkCliente ? 'not-ok-cliente-event' : ''} ${event.formData?.isFechoOk ? 'fecho-ok-event' : ''} ${event.formData?.isFechoNotOk ? 'fecho-not-ok-event' : ''} ${event.formData?.isCancelado ? 'cancelado-event' : ''} ${event.formData?.isPendente ? 'pendente-event' : ''} ${event.formData?.isConcluidoSemProposta ? 'concluido-sem-proposta-event' : ''} ${event.formData?.isVisitaValida ? 'visita-valida-event' : ''}`}
                  data-product={event.product}
                  data-rescheduled={event.formData?.isRescheduled ? 'true' : 'false'}
                  data-not-ok-callcenter={event.formData?.isNotOkCallCenter ? 'true' : 'false'}
                  data-not-ok-cliente={event.formData?.isNotOkCliente ? 'true' : 'false'}
                  data-fecho-ok={event.formData?.isFechoOk ? 'true' : 'false'}
                  data-fecho-not-ok={event.formData?.isFechoNotOk ? 'true' : 'false'}
                  data-cancelado={event.formData?.isCancelado ? 'true' : 'false'}
                  data-pendente={event.formData?.isPendente ? 'true' : 'false'}
                  data-concluido-sem-proposta={event.formData?.isConcluidoSemProposta ? 'true' : 'false'}
                  data-visita-valida={event.formData?.isVisitaValida ? 'true' : 'false'}
                  style={eventStyle}
                  onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(event); } }}
                >
                  <div className="event-title">{event.title}</div>
                  <div className="event-time">
                    {event.formData?.horaInicio || event.time} - {event.formData?.horaFim || event.duration}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWeeklyView = () => {
    // Only work days: Monday through Friday
    const workDays = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'];
    // Use helper function to get Monday of the week (start of work week)
    const startOfWeek = getMondayOfWeek(currentDate);
    
    // Verify startOfWeek is actually Monday (day 1)
    if (startOfWeek.getDay() !== 1) {
      console.error('Week calculation error: startOfWeek is not Monday!', startOfWeek);
      // Force to Monday
      const year = startOfWeek.getFullYear();
      const month = startOfWeek.getMonth();
      const date = startOfWeek.getDate();
      const dayOfWeek = startOfWeek.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setFullYear(year, month, date - daysToSubtract);
      startOfWeek.setHours(0, 0, 0, 0);
    }
    
    // Time slots configuration (same as daily view)
    const startHour = 8;
    const endHour = 23; // Always show until 23:00
    
    // We have exactly 5 work days (Monday to Friday)
    const weekDays = workDays; // Monday through Friday
    
    return (
      <div className="weekly-view">
        <div className="weekly-scroll-wrapper" ref={weeklyScrollRef}>
        <div className="week-header">
          {/* Render exactly 5 work day headers (Monday to Friday) */}
          {weekDays.map((day, index) => {
            // Create a fresh date for each day to avoid mutation issues
            // Start from Monday (index 0 = Monday, index 1 = Tuesday, etc.)
            const date = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + index);
            const isToday = date.toDateString() === new Date().toDateString();
            
            return (
              <div 
                key={`day-header-${index}-${date.getTime()}`} 
                className={`day-header ${isToday ? 'today' : ''}`}
                onClick={() => {
                  setSelectedDate(date);
                  setViewMode('daily');
                }}
              >
                <div className="day-name">{day}</div>
                <div className="day-number">{date.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="week-content">
          {/* Replicate daily view exactly five times - one for each work day */}
          {Array.from({ length: 5 }, (_, dayIndex) => {
            // Create a fresh date for each day to avoid mutation issues
            // Start from Monday (dayIndex 0 = Monday, dayIndex 1 = Tuesday, etc.)
            const date = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + dayIndex);
            const isToday = date.toDateString() === new Date().toDateString();
            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
            const statusEvents = getStatusEventsForDate(date);
            const rotaEvents = getRotaEventsForDate(date);
            const dayEvents = getEventsForDate(date);
            const laidOutDayEvents = layoutCalendarEventsInDay(dayEvents);
            // Only check for matches if a search has been performed (Procurar was clicked)
            const hasMatch = activeSearchTerm ? dayEvents.some(ev => isEventMatch(ev, activeSearchTerm)) : false;
            
            return (
              <div 
                key={dayIndex} 
                className={`daily-view ${hasMatch ? 'search-match-day' : ''}`}
              >
                {/* Status Events at Top - Always show container with ghost if empty */}
                <div className="status-events-container">
                  {statusEvents.length > 0 
                    ? statusEvents.map(renderStatusEvent)
                    : renderGhostStatusEvent()
                  }
                </div>
                
                {/* Rota Events - Always show container with ghost if empty */}
                <div className="rota-events-container">
                  {rotaEvents.length > 0 
                    ? rotaEvents.map(renderRotaEvent)
                    : renderGhostRotaEvent()
                  }
                </div>
                
                <div className="time-slots">
                  {Array.from({ length: endHour - startHour + 1 }, (_, i) => {
                    const hour = startHour + i;
                    return (
                      <div 
                        key={hour} 
                        className={`time-slot ${scenariosMode ? 'clickable' : ''} ${isDaySelected(date) ? 'selected-day' : ''}`}
                        onClick={() => {
                          if (scenariosMode) {
                            handleDayClick(date);
                          }
                        }}
                      >
                        <div className="time-label">{hour.toString().padStart(2, '0')}:00</div>
                        <div className="time-content" />
                      </div>
                    );
                  })}

                  {/* Overlay that renders events spanning across time slots */}
                  <div className="day-events-overlay">
                    {laidOutDayEvents.map(({ event, col, cols }) => {
                      const eventStyle = getEventStyleForDay(event, startHour, 60, col, cols);
                      
                      // Only render events that have valid positioning (have horaInicio and horaFim)
                      if (!eventStyle || !('top' in eventStyle) || !('height' in eventStyle) || !eventStyle.top || !eventStyle.height) {
                        return null;
                      }
                      
                      const produtos = event.formData?.produtos || (event.product ? [event.product] : []);
                      const topValue = typeof eventStyle.top === 'string' ? eventStyle.top : '';
                      const heightValue = typeof eventStyle.height === 'string' ? eventStyle.height : '';
                      
                      return (
                        <div
                          key={event.id}
                          className={`event ${event.type === 'fecho' ? 'marcacao' : event.type}-event ${event.formData?.isRescheduled ? 'rescheduled-event' : ''} ${event.formData?.isNotOkCallCenter ? 'not-ok-callcenter-event' : ''} ${event.formData?.isNotOkCliente ? 'not-ok-cliente-event' : ''} ${event.formData?.isFechoOk ? 'fecho-ok-event' : ''} ${event.formData?.isFechoNotOk ? 'fecho-not-ok-event' : ''} ${event.formData?.isCancelado ? 'cancelado-event' : ''} ${event.formData?.isPendente ? 'pendente-event' : ''} ${event.formData?.isConcluidoSemProposta ? 'concluido-sem-proposta-event' : ''} ${event.formData?.isVisitaValida ? 'visita-valida-event' : ''} ${produtos.length > 1 ? 'multi-product-event' : ''} ${event.formData?.isTemporaryReservation ? 'temporary-reservation' : ''}`}
                          data-product={event.product}
                          data-produtos={produtos.join(',')}
                          data-rescheduled={event.formData?.isRescheduled ? 'true' : 'false'}
                          data-not-ok-callcenter={event.formData?.isNotOkCallCenter ? 'true' : 'false'}
                          data-not-ok-cliente={event.formData?.isNotOkCliente ? 'true' : 'false'}
                          data-fecho-ok={event.formData?.isFechoOk ? 'true' : 'false'}
                          data-fecho-not-ok={event.formData?.isFechoNotOk ? 'true' : 'false'}
                          data-cancelado={event.formData?.isCancelado ? 'true' : 'false'}
                          data-pendente={event.formData?.isPendente ? 'true' : 'false'}
                          data-concluido-sem-proposta={event.formData?.isConcluidoSemProposta ? 'true' : 'false'}
                          data-visita-valida={event.formData?.isVisitaValida ? 'true' : 'false'}
                          data-temporary-reservation={event.formData?.isTemporaryReservation ? 'true' : 'false'}
                          data-event-top={topValue}
                          data-event-height={heightValue}
                          style={eventStyle as React.CSSProperties}
                          ref={(el) => {
                            if (el && process.env.NODE_ENV === 'development') {
                              const computed = window.getComputedStyle(el);
                              const rect = el.getBoundingClientRect();
                              const overlay = el.parentElement;
                              const timeSlots = overlay?.parentElement;
                              const overlayRect = overlay?.getBoundingClientRect();
                              
                              // Extract numeric top value
                              let topValue: number | null = null;
                              if (eventStyle.top) {
                                if (typeof eventStyle.top === 'string') {
                                  topValue = parseInt(eventStyle.top, 10);
                                } else if (typeof eventStyle.top === 'number') {
                                  topValue = eventStyle.top;
                                }
                              }
                              
                              const offsetFromOverlayTop = overlayRect ? rect.top - overlayRect.top : null;
                              
                              // Check for CSS rules that might be overriding top
                              const allStyles = window.getComputedStyle(el);
                              const inlineStyle = el.getAttribute('style') || '';
                              
                              // Store in window for comparison
                              if (!(window as any).__weeklyEventDebug) {
                                (window as any).__weeklyEventDebug = {};
                              }
                              const debugData = {
                                view: 'weekly',
                                eventId: event.id,
                                time: `${event.formData?.horaInicio} - ${event.formData?.horaFim}`,
                                inlineTop: eventStyle.top,
                                inlineHeight: eventStyle.height,
                                inlineStyleAttribute: inlineStyle,
                                computedTop: computed.top,
                                computedHeight: computed.height,
                                computedPosition: computed.position,
                                actualTop: rect.top,
                                actualHeight: rect.height,
                                offsetFromOverlayTop: offsetFromOverlayTop,
                                expectedOffset: topValue,
                                overlayHeight: overlay?.offsetHeight,
                                timeSlotsHeight: timeSlots?.offsetHeight,
                                timeSlotsPosition: timeSlots ? window.getComputedStyle(timeSlots).position : 'N/A',
                                elementClasses: el.className,
                                elementTagName: el.tagName
                              };
                              (window as any).__weeklyEventDebug[event.id] = debugData;
                              const weeklyData = (window as any).__weeklyEventDebug[event.id];
                              if (!(window as any).__eventDebugSummary) {
                                (window as any).__eventDebugSummary = { daily: {}, weekly: {} };
                              }
                              (window as any).__eventDebugSummary.weekly[event.id] = weeklyData;
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(event); } }}
                        >
                          <div className="event-title">
                            {renderEventTitle(event.title)}
                            {event.type === 'bloqueio' && event.description && (
                              <span className="event-description-inline"> - {event.description}</span>
                            )}
                          </div>
                          <div className="event-time">
                            {event.formData?.horaInicio || event.time} - {event.formData?.horaFim || event.duration}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    );
  };

  const renderMonthlyView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return (
      <div className="monthly-view">
        <div className="month-header">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className="month-day-header">{day}</div>
          ))}
        </div>
        <div className="month-grid">
          {days.map((date, index) => {
            const isCurrentMonth = date.getMonth() === month;
            const isToday = date.toDateString() === new Date().toDateString();
            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
            const events = isCurrentMonth ? getEventsForDate(date) : [];
            const rotaEvents = isCurrentMonth ? getRotaEventsForDate(date) : [];
            
            const hasMatch = events.some(ev => isEventMatch(ev, eventSearchTerm));
            return (
              <div 
                key={index} 
                className={`month-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${scenariosMode && isCurrentMonth ? 'clickable' : ''} ${isDaySelected(date) ? 'selected-day' : ''} ${hasMatch ? 'search-match-day' : ''}`}
                onClick={() => {
                  if (scenariosMode && isCurrentMonth) {
                    handleDayClick(date);
                  } else if (isCurrentMonth) {
                    handleDateSelect(date);
                  }
                }}
              >
                <div className="day-number">{date.getDate()}</div>
                {getStatusEventsForDate(date).slice(0, 2).map(renderStatusEvent)}
                {rotaEvents.slice(0, 2).map(renderRotaEvent)}
                {events.slice(0, 2).map((event) => (
                  <div 
                    key={event.id}
                    className={`event ${event.type === 'fecho' ? 'marcacao' : event.type}-event small ${event.formData?.isRescheduled ? 'rescheduled-event' : ''} ${event.formData?.isNotOkCallCenter ? 'not-ok-callcenter-event' : ''} ${event.formData?.isNotOkCliente ? 'not-ok-cliente-event' : ''} ${event.formData?.isFechoOk ? 'fecho-ok-event' : ''} ${event.formData?.isFechoNotOk ? 'fecho-not-ok-event' : ''} ${event.formData?.isCancelado ? 'cancelado-event' : ''} ${event.formData?.isPendente ? 'pendente-event' : ''} ${event.formData?.isConcluidoSemProposta ? 'concluido-sem-proposta-event' : ''} ${event.formData?.isVisitaValida ? 'visita-valida-event' : ''}`}
                    data-product={event.product}
                    data-rescheduled={event.formData?.isRescheduled ? 'true' : 'false'}
                    data-not-ok-callcenter={event.formData?.isNotOkCallCenter ? 'true' : 'false'}
                    data-not-ok-cliente={event.formData?.isNotOkCliente ? 'true' : 'false'}
                    data-fecho-ok={event.formData?.isFechoOk ? 'true' : 'false'}
                    data-fecho-not-ok={event.formData?.isFechoNotOk ? 'true' : 'false'}
                    data-cancelado={event.formData?.isCancelado ? 'true' : 'false'}
                    data-pendente={event.formData?.isPendente ? 'true' : 'false'}
                    data-concluido-sem-proposta={event.formData?.isConcluidoSemProposta ? 'true' : 'false'}
                    data-visita-valida={event.formData?.isVisitaValida ? 'true' : 'false'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event);
                    }}
                  >
                    <div className="event-title">{event.title}</div>
                  </div>
                ))}
                {events.length > 2 && (
                  <div className="more-events">+{events.length - 2} mais</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDatePicker = () => {
    if (!showDatePicker) return null;
    
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    return (
      <div className="date-picker-modal">
        <div className="date-picker-content">
          <div className="date-picker-header">
            <h3>Selecionar Data</h3>
            <button type="button" className="close-btn" onClick={() => setShowDatePicker(false)}>×</button>
          </div>
          <div className="date-picker-body">
            <div className="month-picker">
              {Array.from({ length: 12 }, (_, i) => (
                <button
                  key={i}
                  className={`month-btn ${i === currentMonth ? 'active' : ''}`}
                  onClick={() => goToDate(currentYear, i)}
                >
                  {new Date(0, i).toLocaleDateString('pt-PT', { month: 'long' })}
                </button>
              ))}
            </div>
            <div className="year-picker">
              <button onClick={() => goToDate(currentYear - 1, currentMonth)}>←</button>
              <span>{currentYear}</span>
              <button onClick={() => goToDate(currentYear + 1, currentMonth)}>→</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderYearPicker = () => {
    if (!showYearPicker) return null;
    
    const currentYear = currentDate.getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
    
    return (
      <div className="year-picker-modal">
        <div className="year-picker-content">
          <div className="year-picker-header">
            <h3>Selecionar Ano</h3>
            <button type="button" className="close-btn" onClick={() => setShowYearPicker(false)}>×</button>
          </div>
          <div className="year-picker-body">
            {years.map(year => (
              <button
                key={year}
                className={`year-btn ${year === currentYear ? 'active' : ''}`}
                onClick={() => goToDate(year, currentDate.getMonth())}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSidebar = () => {
    // Show sidebar for admin, Gerente comercial, and Gestor users from comercial tenant
    if (!isAdminOrGerenteComercial() && !canViewComercialUserSidebar()) {
      return null;
    }

    return (
      <Drawer
        title={<Text strong style={{ fontSize: 20 }}>Calendários</Text>}
        placement="left"
        closable={true}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={isMobileOrTablet ? '80vw' : 300}
        style={{
          borderTopRightRadius: 18,
          borderBottomRightRadius: 18,
        }}
        headerStyle={{ borderBottom: '1px solid #f0f0f0', background: '#fff', borderTopRightRadius: 18 }}
        bodyStyle={{
          padding: '16px',
          background: '#fff',
          maxHeight: '100vh',
          overflowY: 'auto',
        }}
      >
        <div className="search-section" style={{ marginBottom: '16px' }}>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Pesquisar utilizador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="user-search-input"
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d9d9d9' }}
            />
          </div>
        </div>
        
        <div className="user-list">
          {filteredUsers.map(user => (
            <div 
              key={user.id} 
              className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
              onClick={() => {
                selectUser(user.id);
                setDrawerOpen(false);
              }}
              style={{ 
                cursor: 'pointer',
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border: selectedUser?.id === user.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                backgroundColor: selectedUser?.id === user.id ? '#e6f7ff' : '#fff',
                transition: 'all 0.2s'
              }}
            >
              <div className="user-info">
                <div className="user-name-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div 
                    className="user-color"
                    style={{ backgroundColor: user.color }}
                  ></div>
                  <span
                    className="user-name"
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      minWidth: 0,
                    }}
                  >
                    {user.name || 'Unknown'}
                  </span>
                </div>
                <div 
                  className="especialidade-tag-sidebar"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleEspecialidadeClick(user, e);
                  }}
                  style={{
                    display: 'flex',
                    gap: '0.25rem',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    minWidth: '60px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    backgroundColor: user.especialidade && user.especialidade.length > 0 ? 'transparent' : '#f5f5f5',
                    borderStyle: user.especialidade && user.especialidade.length > 0 ? 'solid' : 'dashed',
                    color: user.especialidade && user.especialidade.length > 0 ? 'white' : '#666',
                    marginTop: '0.5rem'
                  }}
                >
                  {user.especialidade && user.especialidade.length > 0 ? (
                    user.especialidade.filter(esp => esp).map((esp, index) => {
                      const config = getEspecialidadeConfig(esp);
                      if (!config || !config.color) return null;
                      return (
                        <span
                          key={index}
                          className="especialidade-indicator"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          style={{
                            backgroundColor: config.color,
                            color: 'white',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '6px',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none'
                          }}
                        >
                          {config.label}
                        </span>
                      );
                    }).filter(Boolean)
                  ) : (
                    'Espec.'
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Drawer>
    );
  };

  const renderStatusEventModal = () => {
    if (!showStatusEventModal || !selectedUserForStatus) return null;
    
    return (
      <div className="event-modal">
        <div className="event-modal-content">
          <div className="event-modal-header">
            <h3>Definir Status Semanal - {selectedUserForStatus.name}</h3>
            <button type="button" className="close-btn" onClick={() => setShowStatusEventModal(false)}>×</button>
          </div>
          <div className="event-modal-body">
            <div className="form-group">
              <label>Semana de:</label>
              <input 
                type="date" 
                defaultValue={currentDate.toISOString().split('T')[0]}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Status para a Semana:</label>
              <select className="form-select">
                <option value="">Selecionar status</option>
                <option value="Transportes">Transportes</option>
                <option value="Carro">Carro</option>
                <option value="Longe">Longe</option>
                <option value="RoadTrip">RoadTrip</option>
              </select>
            </div>
            <div className="form-group">
              <label>Localização Base:</label>
              <select className="form-select">
                <option value="">Selecionar localização</option>
                <option value="Coimbra">Coimbra</option>
                <option value="Lisboa">Lisboa</option>
              </select>
            </div>
            <div className="form-group">
              <label>Dias da Semana:</label>
              <div className="week-days">
                {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map((day, index) => (
                  <label key={day} className="day-checkbox">
                    <input type="checkbox" defaultChecked={index < 5} />
                    <span>{day}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="event-actions">
              <button className="btn btn-primary">Definir Status Semanal</button>
              <button className="btn btn-secondary" onClick={() => setShowStatusEventModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusEventDetailModal = () => {
    if (!showStatusEventDetailModal || !selectedStatusEvent) return null;
    
    // Get all linked users from editScenarioAssignedTo (populated when modal opens)
    const linkedUsers = editScenarioAssignedTo
      .map(userId => comercialUsers.find(u => u.id === userId))
      .filter(Boolean) as ComercialUser[];
    
    const user = comercialUsers.find(u => u.id === selectedStatusEvent.userId);
    
    return (
      <div className="event-modal status-detail-modal">
        <div className="event-modal-content">
          <div className="event-modal-header status-detail-header">
            <h3>{isEditingScenario ? 'Editar Cenário' : selectedStatusEvent.text}</h3>
            <button type="button" className="close-btn" onClick={() => { setShowStatusEventDetailModal(false); setIsEditingScenario(false); }}>×</button>
          </div>
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {scenarioNotification && (
              <div 
                className={`rota-notification rota-notification-${scenarioNotification.type}`}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: scenarioNotification.type === 'success' ? '#10b981' : '#ef4444',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  maxWidth: '400px',
                  textAlign: 'center',
                  width: '90%'
                }}
              >
                {scenarioNotification.type === 'success' ? '✓ ' : '✗ '}
                {scenarioNotification.message}
              </div>
            )}
            <div className="status-details-compact">
              {isEditingScenario ? (
                <form className="rota-form">
                  <div className="form-section active">
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          ID do Cenário
                        </label>
                        <input 
                          type="text" 
                          className="form-input"
                          value={selectedStatusEvent.id} 
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#f5f5f5', 
                            color: '#666',
                            cursor: 'not-allowed'
                          }} 
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Data <span className="required">*</span>
                        </label>
                        <input 
                          type="date"
                          className={`form-input ${editScenarioValidationErrors.date ? 'error' : ''}`}
                          value={editScenarioDate} 
                          onChange={(e) => {
                            setEditScenarioDate(e.target.value);
                            if (editScenarioValidationErrors.date) {
                              setEditScenarioValidationErrors({...editScenarioValidationErrors, date: undefined});
                            }
                          }}
                          required
                        />
                        {editScenarioValidationErrors.date && (
                          <span className="error-message">{editScenarioValidationErrors.date}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Título do Cenário (Texto do Cenário) <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-input ${editScenarioValidationErrors.text ? 'error' : ''}`}
                          value={editScenarioText}
                          onChange={(e) => {
                            setEditScenarioText(e.target.value);
                            if (editScenarioValidationErrors.text) {
                              setEditScenarioValidationErrors({...editScenarioValidationErrors, text: undefined});
                            }
                          }}
                          placeholder="Digite o título/texto que aparecerá no cartão do cenário..."
                          required
                        />
                        {editScenarioValidationErrors.text && (
                          <span className="error-message">{editScenarioValidationErrors.text}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Comercial / Comerciais <span className="required">*</span>
                        </label>
                        <div className={`multi-select-container ${editScenarioValidationErrors.assignedTo ? 'error' : ''}`}>
                          <select
                            className="form-input"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                const newAssignedTo = [...(editScenarioAssignedTo || []), e.target.value];
                                setEditScenarioAssignedTo(newAssignedTo);
                                e.target.value = '';
                                if (editScenarioValidationErrors.assignedTo) {
                                  setEditScenarioValidationErrors({...editScenarioValidationErrors, assignedTo: undefined});
                                }
                              }
                            }}
                          >
                            <option value="">Selecionar comercial</option>
                            {comercialUsers.map(user => {
                              const isSelected = editScenarioAssignedTo?.includes(user.id);
                              return (
                                <option 
                                  key={user.id} 
                                  value={user.id}
                                  disabled={isSelected}
                                  style={isSelected ? { 
                                    backgroundColor: '#fee2e2', 
                                    color: '#991b1b',
                                    fontWeight: '600'
                                  } : {}}
                                >
                                  {isSelected ? `${user.name} ✓ (já selecionado)` : user.name}
                                </option>
                              );
                            })}
                          </select>
                          <div className="selected-comerciais">
                            {editScenarioAssignedTo?.map((userId: string) => {
                              const user = comercialUsers.find(u => u.id === userId);
                              return user ? (
                                <span key={userId} className="selected-tag">
                                  {user.name}
                                  <button
                                    type="button"
                                    className="remove-tag"
                                    onClick={() => {
                                      setEditScenarioAssignedTo(editScenarioAssignedTo.filter((id: string) => id !== userId));
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                        {editScenarioValidationErrors.assignedTo && (
                          <span className="error-message">{editScenarioValidationErrors.assignedTo}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <>
                  {/* Header with key info */}
                  <div className="event-header-info">
                    <div className="event-time-info">
                      <span className="date-badge" title="Data do Cenário">
                        Data: {selectedStatusEvent.date.toLocaleDateString('pt-PT')}
                      </span>
                      <span className="status-badge" title="Texto do Cenário">
                        Texto: {selectedStatusEvent.text}
                      </span>
                    </div>
                    <div className="event-product-info">
                      <span className="comercial-badge" title="Comercial / Comerciais Responsáveis">
                        Comercial{linkedUsers.length > 1 ? ' / Comerciais' : ''}: {linkedUsers.length > 0 ? linkedUsers.map(u => u.name).join(', ') : (user?.name || 'N/A')}
                      </span>
                    </div>
                  </div>

                  {/* Status Information */}
                  <div className="status-info-section">
                    <h4>📋 Informação do Cenário</h4>
                    <div className="status-details">
                      <div className="status-main">
                        <div className="status-field">
                          <span className="field-label">ID do Cenário:</span>
                          <span className="field-value">{selectedStatusEvent.id}</span>
                        </div>
                        <div className="status-field">
                          <span className="field-label">Texto do Cenário:</span>
                          <span className="field-value">{selectedStatusEvent.text}</span>
                        </div>
                        <div className="status-field">
                          <span className="field-label">Data:</span>
                          <span className="field-value">{selectedStatusEvent.date.toLocaleDateString('pt-PT')}</span>
                        </div>
                        <div className="status-field">
                          <span className="field-label">Comercial{linkedUsers.length > 1 ? ' / Comerciais' : ''}:</span>
                          <span className="field-value">{linkedUsers.length > 0 ? linkedUsers.map(u => u.name).join(', ') : (user?.name || 'N/A')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="event-actions">
              {(() => {
                const canEdit = canEditEvents();
                return !isEditingScenario && canEdit;
              })() && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => setIsEditingScenario(true)}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: '#8b5cf6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s',
                    marginRight: '10px'
                  }}
                >
                  Editar
                </button>
              )}
              {(() => {
                const canEdit = canEditEvents();
                return !isEditingScenario && canEdit;
              })() && (
                <button 
                  className="btn btn-danger" 
                  onClick={() => setShowScenarioDeleteConfirm(true)}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: '#ef4444', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s',
                    marginRight: '10px'
                  }}
                >
                  Eliminar
                </button>
              )}
              {!isEditingScenario && (
                <button className="btn btn-secondary" onClick={() => setShowStatusEventDetailModal(false)}>Fechar</button>
              )}
              {isEditingScenario && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSaveScenario}
                    disabled={savingScenario || !editScenarioDate || !editScenarioText || !editScenarioText.trim() || !editScenarioAssignedTo || editScenarioAssignedTo.length === 0}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: (savingScenario || !editScenarioDate || !editScenarioText || !editScenarioText.trim() || !editScenarioAssignedTo || editScenarioAssignedTo.length === 0) ? '#ccc' : '#8b5cf6', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: (savingScenario || !editScenarioDate || !editScenarioText || !editScenarioText.trim() || !editScenarioAssignedTo || editScenarioAssignedTo.length === 0) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {savingScenario ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditingScenario(false);
                      setEditScenarioValidationErrors({});
                      setScenarioNotification(null);
                    }}
                    disabled={savingScenario}
                  >
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRotaEventDetailModal = () => {
    if (!showRotaEventDetailModal || !selectedRotaEvent) return null;
    
    // Handle both sample data structure and backend data structure
    const rotaDate = selectedRotaEvent.date || (selectedRotaEvent.formData?.data ? new Date(selectedRotaEvent.formData.data) : null) || (selectedRotaEvent.data ? new Date(selectedRotaEvent.data) : null);
    const rotaId = selectedRotaEvent.rotaId || selectedRotaEvent.id || 'N/A';
    const linkGoogleMaps = selectedRotaEvent.linkGoogleMaps || selectedRotaEvent.formData?.linkGoogleMaps || '';
    // Get links - support both new format (links array) and legacy (linkGoogleMaps)
    const links = selectedRotaEvent.links || selectedRotaEvent.formData?.links || [];
    const finalLinks = links.length > 0 ? links : (linkGoogleMaps ? [{ url: linkGoogleMaps, label: 'Google Maps' }] : []);
    const title = selectedRotaEvent.title || selectedRotaEvent.formData?.title || '';
    const description = selectedRotaEvent.description || selectedRotaEvent.formData?.description || '';
    
    // Get comerciais - handle both userIds and formData.comerciais
    const userIds = selectedRotaEvent.userIds || selectedRotaEvent.formData?.comerciais || [];
    const comerciais = userIds.map((userId: string) => 
      comercialUsers.find(u => u.id === String(userId))?.name
    ).filter(Boolean).join(', ') || 'N/A';
    
    return (
      <div className="event-modal rota-detail-modal">
        <div className="event-modal-content">
          <div className="event-modal-header rota-detail-header">
            <h3>{isEditingRota ? 'Editar Rota' : 'ROTA'}</h3>
            <button type="button" className="close-btn" onClick={() => { setShowRotaEventDetailModal(false); setIsEditingRota(false); }}>×</button>
          </div>
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {rotaNotification && (
              <div 
                className={`rota-notification rota-notification-${rotaNotification.type}`}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: rotaNotification.type === 'success' ? '#10b981' : '#ef4444',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  maxWidth: '400px',
                  textAlign: 'center',
                  width: '90%'
                }}
              >
                {rotaNotification.type === 'success' ? '✓ ' : '✗ '}
                {rotaNotification.message}
              </div>
            )}
            <div className="rota-details-compact">
              {isEditingRota ? (
                <form className="rota-form">
                  <div className="form-section active">
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          ID da Rota
                        </label>
                        <input 
                          type="text" 
                          className="form-input"
                          value={rotaId} 
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#f5f5f5', 
                            color: '#666',
                            cursor: 'not-allowed'
                          }} 
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Data <span className="required">*</span>
                        </label>
                        <input 
                          type="date"
                          className={`form-input ${editRotaValidationErrors.data ? 'error' : ''}`}
                          value={editRotaDate} 
                          onChange={(e) => {
                            setEditRotaDate(e.target.value);
                            if (editRotaValidationErrors.data) {
                              setEditRotaValidationErrors({...editRotaValidationErrors, data: undefined});
                            }
                          }}
                          required
                        />
                        {editRotaValidationErrors.data && (
                          <span className="error-message">{editRotaValidationErrors.data}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Comercial / Comerciais <span className="required">*</span>
                        </label>
                        <div className={`multi-select-container ${editRotaValidationErrors.comerciais ? 'error' : ''}`}>
                          <select
                            className="form-input"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                const newComerciais = [...(editRotaComerciais || []), e.target.value];
                                setEditRotaComerciais(newComerciais);
                                e.target.value = '';
                                if (editRotaValidationErrors.comerciais) {
                                  setEditRotaValidationErrors({...editRotaValidationErrors, comerciais: undefined});
                                }
                              }
                            }}
                          >
                            <option value="">Selecionar comercial</option>
                            {comercialUsers.map(user => {
                              const isSelected = editRotaComerciais?.includes(user.id);
                              return (
                                <option 
                                  key={user.id} 
                                  value={user.id}
                                  disabled={isSelected}
                                  style={isSelected ? { 
                                    backgroundColor: '#fee2e2', 
                                    color: '#991b1b',
                                    fontWeight: '600'
                                  } : {}}
                                >
                                  {isSelected ? `${user.name} ✓ (já selecionado)` : user.name}
                                </option>
                              );
                            })}
                          </select>
                          <div className="selected-comerciais">
                            {editRotaComerciais?.map((userId: string) => {
                              const user = comercialUsers.find(u => u.id === userId);
                              return user ? (
                                <span key={userId} className="selected-tag">
                                  {user.name}
                                  <button
                                    type="button"
                                    className="remove-tag"
                                    onClick={() => {
                                      setEditRotaComerciais(editRotaComerciais.filter((id: string) => id !== userId));
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                        {editRotaValidationErrors.comerciais && (
                          <span className="error-message">{editRotaValidationErrors.comerciais}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">
                          Links da Rota <span className="required">*</span>
                        </label>
                        {[0, 1].map((index: number) => {
                          const currentLinks = editRotaLinks || [];
                          const link = currentLinks[index] || { url: '', label: index === 0 ? 'Início da Rota' : 'Continuação' };
                          const labels = ['Início da Rota', 'Continuação'];
                          return (
                            <div key={index} style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', color: '#374151' }}>
                                {labels[index]}
                              </label>
                              <input
                                type="url"
                                className={`form-input ${editRotaValidationErrors.links ? 'error' : ''}`}
                                placeholder="https://maps.google.com/..."
                                value={link.url || ''}
                                onChange={(e) => {
                                  const newLinks = [...currentLinks];
                                  while (newLinks.length < 2) {
                                    newLinks.push({ url: '', label: labels[newLinks.length] });
                                  }
                                  newLinks[index] = { url: e.target.value, label: labels[index] };
                                  setEditRotaLinks(newLinks);
                                  if (editRotaValidationErrors.links) {
                                    setEditRotaValidationErrors({...editRotaValidationErrors, links: undefined});
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                        {editRotaValidationErrors.links && (
                          <span className="error-message">{editRotaValidationErrors.links}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <>
                  {/* Header with key info */}
                  <div className="event-header-info">
                    <div className="event-time-info">
                      <span className="date-badge" title="Data da Rota">
                        Data: {rotaDate ? (rotaDate instanceof Date ? rotaDate.toLocaleDateString('pt-PT') : new Date(rotaDate).toLocaleDateString('pt-PT')) : 'N/A'}
                      </span>
                      <span className="rota-badge" title="Tipo de Rota">
                        Tipo: Rota Google Maps
                      </span>
                    </div>
                    <div className="event-product-info">
                      <span className="comercial-badge" title="Comerciais Responsáveis">
                        Comerciais: {comerciais}
                      </span>
                    </div>
                  </div>

                  {/* Rota Information */}
                  <div className="rota-info-section">
                    <h4>🗺️ Informação da Rota</h4>
                    <div className="rota-details">
                      <div className="rota-main">
                        <div className="rota-field">
                          <span className="field-label">ID da Rota:</span>
                          <span className="field-value">{rotaId}</span>
                        </div>
                        {title && (
                          <div className="rota-field">
                            <span className="field-label">Título:</span>
                            <span className="field-value">{title}</span>
                          </div>
                        )}
                        <div className="rota-field">
                          <span className="field-label">Data:</span>
                          <span className="field-value">
                            {rotaDate ? (rotaDate instanceof Date ? rotaDate.toLocaleDateString('pt-PT') : new Date(rotaDate).toLocaleDateString('pt-PT')) : 'N/A'}
                          </span>
                        </div>
                        <div className="rota-field">
                          <span className="field-label">Comerciais:</span>
                          <span className="field-value">{comerciais}</span>
                        </div>
                        {description && (
                          <div className="rota-field">
                            <span className="field-label">Descrição:</span>
                            <span className="field-value">{description}</span>
                          </div>
                        )}
                        <div className="rota-field">
                          <span className="field-label">Links da Rota:</span>
                          <span className="field-value">
                            {finalLinks.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                                {finalLinks.slice(0, 2).map((link: any, index: number) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      if (link.url) {
                                        window.open(link.url, '_blank');
                                      }
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                                      color: '#7f1d1d',
                                      border: '2px solid #fca5a5',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem',
                                      fontWeight: '700',
                                      boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)',
                                      transition: 'all 0.2s ease',
                                      letterSpacing: '0.5px'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (e.currentTarget) {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                                        e.currentTarget.style.borderColor = '#ef4444';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (e.currentTarget) {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)';
                                        e.currentTarget.style.borderColor = '#fca5a5';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.2)';
                                      }
                                    }}
                                  >
                                    {index + 1}
                                  </button>
                                ))}
                              </div>
                            ) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="event-actions">
              {(() => {
                const canEdit = canEditEvents();
                return !isEditingRota && canEdit;
              })() && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => setIsEditingRota(true)}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#f87171', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                      marginRight: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#fca5a5';
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#f87171';
                    }}
                  >
                    Editar
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setShowRotaDeleteConfirm(true)}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                      marginRight: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#ef4444';
                    }}
                  >
                    Eliminar
                  </button>
                </>
              )}
              {isEditingRota && (
                <>
                  {(() => {
                    const hasValidRotaLink = (editRotaLinks || []).some((link: any) => link.url && link.url.trim());
                    const saveDisabled = savingRota || editRotaComerciais.length === 0 || !editRotaDate || !hasValidRotaLink;
                    return (
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSaveRota}
                    disabled={saveDisabled}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: saveDisabled ? '#ccc' : '#f87171', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: saveDisabled ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!saveDisabled && e.currentTarget) e.currentTarget.style.backgroundColor = '#fca5a5';
                    }}
                    onMouseLeave={(e) => {
                      if (!saveDisabled && e.currentTarget) e.currentTarget.style.backgroundColor = '#f87171';
                    }}
                  >
                    {savingRota ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                    );
                  })()}
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setIsEditingRota(false);
                      setEditRotaValidationErrors({});
                      setRotaNotification(null);
                      // Reset to original values
                      const rotaDate = selectedRotaEvent.date || (selectedRotaEvent.formData?.data ? new Date(selectedRotaEvent.formData.data) : null) || (selectedRotaEvent.data ? new Date(selectedRotaEvent.data) : null);
                      if (rotaDate) {
                        let dateStr: string;
                        if (rotaDate instanceof Date) {
                          const year = rotaDate.getFullYear();
                          const month = String(rotaDate.getMonth() + 1).padStart(2, '0');
                          const day = String(rotaDate.getDate()).padStart(2, '0');
                          dateStr = `${year}-${month}-${day}`;
                        } else {
                          dateStr = rotaDate;
                        }
                        setEditRotaDate(dateStr);
                      }
                      const userIds = selectedRotaEvent.userIds || selectedRotaEvent.formData?.comerciais || [];
                      setEditRotaComerciais(userIds.map((id: string | number) => String(id)));
                      const linkGoogleMapsVal = selectedRotaEvent.linkGoogleMaps || selectedRotaEvent.formData?.linkGoogleMaps || '';
                      setEditRotaLinkGoogleMaps(linkGoogleMapsVal);
                      const links = selectedRotaEvent.links || selectedRotaEvent.formData?.links || [];
                      const labels = ['Início da Rota', 'Continuação'];
                      const finalLinks = links.length > 0 ? links : (linkGoogleMapsVal ? [{ url: linkGoogleMapsVal, label: 'Google Maps' }] : []);
                      setEditRotaLinks([0, 1].map((i) => finalLinks[i] || { url: '', label: labels[i] }));
                    }}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#95a5a6', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#7f8c8d';
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#95a5a6';
                    }}
                  >
                    Cancelar
                  </button>
                </>
              )}
              <button 
                className="btn btn-secondary" 
                onClick={() => { setShowRotaEventDetailModal(false); setIsEditingRota(false); }}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#95a5a6', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) e.currentTarget.style.backgroundColor = '#7f8c8d';
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) e.currentTarget.style.backgroundColor = '#95a5a6';
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Render Scenario delete confirmation modal
  const renderScenarioDeleteConfirmModal = () => {
    if (!showScenarioDeleteConfirm || !selectedStatusEvent) return null;
    
    const user = comercialUsers.find(u => u.id === selectedStatusEvent.userId);
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000,
          backdropFilter: 'blur(2px)'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !deletingScenario) {
            setShowScenarioDeleteConfirm(false);
          }
        }}
      >
        <div 
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            zIndex: 100001,
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              Confirmar Eliminação
            </h3>
            <button 
              onClick={() => !deletingScenario && setShowScenarioDeleteConfirm(false)}
              disabled={deletingScenario}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: deletingScenario ? 'not-allowed' : 'pointer',
                color: '#6b7280',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingScenario) e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ padding: '24px' }}>
            <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151', lineHeight: '1.5' }}>
              Tem a certeza que deseja eliminar este <strong>cenário</strong>?
            </p>
            
            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>ID do Cenário:</strong> {selectedStatusEvent.id}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Texto do Cenário:</strong> {selectedStatusEvent.text}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Data:</strong> {selectedStatusEvent.date.toLocaleDateString('pt-PT')}
              </div>
              <div>
                <strong>Comercial:</strong> {user?.name || 'N/A'}
              </div>
            </div>
          </div>
          
          <div style={{
            padding: '24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button 
              onClick={() => setShowScenarioDeleteConfirm(false)}
              disabled={deletingScenario}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#374151',
                cursor: deletingScenario ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: deletingScenario ? 0.5 : 1,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingScenario) e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Cancelar
            </button>
            <button 
              onClick={handleDeleteScenario}
              disabled={deletingScenario}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: deletingScenario ? '#9ca3af' : '#ef4444',
                color: '#ffffff',
                cursor: deletingScenario ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingScenario) e.currentTarget.style.background = '#dc2626';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget && !deletingScenario) e.currentTarget.style.background = '#ef4444';
              }}
            >
              {deletingScenario ? 'Eliminando...' : 'Eliminar e Arquivar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Rota delete confirmation modal
  const renderRotaDeleteConfirmModal = () => {
    if (!showRotaDeleteConfirm || !selectedRotaEvent) return null;
    
    const rotaDate = selectedRotaEvent.date || (selectedRotaEvent.formData?.data ? new Date(selectedRotaEvent.formData.data) : null) || (selectedRotaEvent.data ? new Date(selectedRotaEvent.data) : null);
    const rotaId = selectedRotaEvent.rotaId || selectedRotaEvent.id || 'N/A';
    const userIds = selectedRotaEvent.userIds || selectedRotaEvent.formData?.comerciais || [];
    const comerciais = userIds.map((userId: string) => 
      comercialUsers.find(u => u.id === String(userId))?.name
    ).filter(Boolean).join(', ') || 'N/A';
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000,
          backdropFilter: 'blur(2px)'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !deletingRota) {
            setShowRotaDeleteConfirm(false);
          }
        }}
      >
        <div 
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            zIndex: 100001,
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              Confirmar Eliminação
            </h3>
            <button 
              onClick={() => !deletingRota && setShowRotaDeleteConfirm(false)}
              disabled={deletingRota}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: deletingRota ? 'not-allowed' : 'pointer',
                color: '#6b7280',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingRota) e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ padding: '24px' }}>
            <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151', lineHeight: '1.5' }}>
              Tem a certeza que deseja eliminar esta <strong>rota</strong>?
            </p>
            
            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>ID da Rota:</strong> {rotaId}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Data:</strong> {rotaDate ? (rotaDate instanceof Date ? rotaDate.toLocaleDateString('pt-PT') : new Date(rotaDate).toLocaleDateString('pt-PT')) : 'N/A'}
              </div>
              <div>
                <strong>Comerciais:</strong> {comerciais}
              </div>
            </div>
          </div>
          
          <div style={{
            padding: '24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button 
              onClick={() => setShowRotaDeleteConfirm(false)}
              disabled={deletingRota}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#374151',
                cursor: deletingRota ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: deletingRota ? 0.5 : 1,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingRota) e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Cancelar
            </button>
            <button 
              onClick={handleDeleteRota}
              disabled={deletingRota}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: deletingRota ? '#9ca3af' : '#ef4444',
                color: '#ffffff',
                cursor: deletingRota ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingRota) e.currentTarget.style.background = '#dc2626';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget && !deletingRota) e.currentTarget.style.background = '#ef4444';
              }}
            >
              {deletingRota ? 'Eliminando...' : 'Eliminar e Arquivar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEspecialidadeModal = () => {
    if (!showEspecialidadeModal || !selectedUserForEspecialidade) return null;
    
    const especialidadeOptions = [
      { value: 'bairro-solar', label: 'Bairro Solar' },
      { value: 'pap', label: 'Pay As Produced (PAP)' },
      { value: 'gas', label: 'Gás' },
      { value: 'eletricidade', label: 'Eletricidade' }
    ];
    
    return (
      <div className="event-modal">
        <div className="event-modal-content">
          <div className="event-modal-header">
            <h3>Especialidade</h3>
            <button type="button" className="close-btn" onClick={() => {
              setShowEspecialidadeModal(false);
              setSelectedUserForEspecialidade(null);
            }}>×</button>
          </div>
          <div className="event-modal-body">
            <div className="level-modal-content">
              <div className="user-info">
                <div 
                  className="user-color-large"
                  style={{ backgroundColor: selectedUserForEspecialidade.color }}
                ></div>
                <h4>{selectedUserForEspecialidade.name}</h4>
              </div>
              
              <div className="level-selection">
                <h5>Selecionar Especialidade:</h5>
                <div className="especialidade-options">
                  {especialidadeOptions.map(option => {
                    const isSelected = selectedEspecialidades.includes(option.value);
                    const config = getEspecialidadeConfig(option.value);
                    return (
                      <button
                        key={option.value}
                        className={`especialidade-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleEspecialidadeToggle(option.value)}
                        style={{ borderColor: isSelected ? config.color : '#ddd' }}
                      >
                        <div 
                          className="especialidade-circle"
                          style={{ 
                            backgroundColor: isSelected ? config.color : 'transparent',
                            borderColor: config.color
                          }}
                        >
                          {config.label}
                        </div>
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="event-actions">
              <button className="btn btn-primary" onClick={handleSaveEspecialidade}>
                Guardar
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setShowEspecialidadeModal(false);
                setSelectedUserForEspecialidade(null);
              }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMarcacaoModal = () => {
    if (!showMarcacaoModal) return null;
    
    return (
      <div className="event-modal marcacao-modal">
        <div className="event-modal-content marcacao-modal-content">
          <div className="event-modal-header marcacao-header">
            <button 
              type="button"
              className="close-btn" 
              onClick={() => {
                setShowMarcacaoModal(false);
                setMarcacaoNotification(null);
                setMarcacaoFormData({});
                setCurrentMarcacaoStep(1);
                setValidationErrors({});
              }}
            >
              ×
            </button>
            
            {/* Line 1: Title */}
            <h1>Marcação</h1>
            
            {/* Line 2: Subtitle */}
            <p className="modal-subtitle">Criar uma nova marcação no calendário</p>
            
            {/* Line 3: Progress Steps */}
            <div className="progress-steps-grid">
           <div className={`step ${currentMarcacaoStep === 1 ? 'active' : currentMarcacaoStep > 1 ? 'completed' : ''}`}>
             <div className="step-icon">1</div>
             <div className="step-title">Agendamento</div>
           </div>
           <div className={`step ${currentMarcacaoStep === 2 ? 'active' : currentMarcacaoStep > 2 ? 'completed' : ''}`}>
             <div className="step-icon">2</div>
             <div className="step-title">Produto</div>
           </div>
           <div className={`step ${currentMarcacaoStep === 3 ? 'active' : currentMarcacaoStep > 3 ? 'completed' : ''}`}>
             <div className="step-icon">3</div>
             <div className="step-title">Cliente</div>
           </div>
            </div>
          </div>
          
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {marcacaoNotification && (
              <div 
                className={`marcacao-notification marcacao-notification-${marcacaoNotification.type}`}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: marcacaoNotification.type === 'success' ? '#10b981' : '#ef4444',
                  color: 'white',
                  fontWeight: '500',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  maxWidth: '90%',
                  textAlign: 'center'
                }}
              >
                {marcacaoNotification.message}
              </div>
            )}
            <form className="marcacao-form">
              {/* Step 1: Agendamento */}
              {currentMarcacaoStep === 1 && (
                <div className="form-section active">
                  <h3 className="section-title">Agendamento</h3>
                  <p className="section-description">Defina a data, hora e responsável para esta marcação</p>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <span className="required">*</span> Data
                      </label>
                      <input 
                        type="date" 
                        className={`form-input ${validationErrors.marcacaoData ? 'error' : ''}`}
                        value={marcacaoFormData.marcacaoData || ''}
                        onChange={(e) => setMarcacaoFormData({...marcacaoFormData, marcacaoData: e.target.value})}
                      />
                      {validationErrors.marcacaoData && (
                        <span className="error-message">{validationErrors.marcacaoData}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        <span className="required">*</span> Hora de Início
                      </label>
                      <input 
                        type="time" 
                        className={`form-input ${validationErrors.horaInicio ? 'error' : ''}`}
                        value={marcacaoFormData.horaInicio || ''}
                        onChange={(e) => setMarcacaoFormData({...marcacaoFormData, horaInicio: e.target.value})}
                      />
                      {validationErrors.horaInicio && (
                        <span className="error-message">{validationErrors.horaInicio}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        <span className="required">*</span> Hora de Fim
                      </label>
                      <input 
                        type="time" 
                        className={`form-input ${validationErrors.horaFim ? 'error' : ''}`}
                        value={marcacaoFormData.horaFim || ''}
                        onChange={(e) => setMarcacaoFormData({...marcacaoFormData, horaFim: e.target.value})}
                      />
                      {validationErrors.horaFim && (
                        <span className="error-message">{validationErrors.horaFim}</span>
                      )}
                    </div>
                    
                    <div className="form-group full-width">
                      <label className="form-label">Observações</label>
                      <textarea 
                        className="form-input" 
                        rows={3} 
                        placeholder="Observações adicionais sobre esta marcação..."
                        value={marcacaoFormData.marcacaoDescription || ''}
                        onChange={(e) => setMarcacaoFormData({...marcacaoFormData, marcacaoDescription: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Produto da Marcação */}
              {currentMarcacaoStep === 2 && (
                <div className="form-section active">
                  <h3 className="section-title">Produto da Marcação</h3>
                  <p className="section-description">Selecione o tipo de produto para esta marcação</p>
                  
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span> PRODUTO DA MARCAÇÃO
                  </label>
                  <select 
                    className={`form-input ${validationErrors.produto ? 'error' : ''}`}
                    value={marcacaoFormData.produto || ''} 
                    onChange={(e) => setMarcacaoFormData({...marcacaoFormData, produto: e.target.value})}
                  >
                    <option value="">Selecione o produto</option>
                    <option value="bairro-solar">Bairro Solar</option>
                    <option value="pap">PAY AS PRODUCED (PAP)</option>
                    <option value="gas">Gás</option>
                    <option value="eletricidade">Eletricidade</option>
                  </select>
                  {validationErrors.produto && (
                    <span className="error-message">{validationErrors.produto}</span>
                  )}
                </div>
                </div>
              )}

              {/* Step 3: Informação do Cliente */}
              {currentMarcacaoStep === 3 && (
                <div className="form-section active">
                  <h3 className="section-title">Informação do Cliente</h3>
                  <p className="section-description">Dados básicos do cliente para esta marcação</p>
                  
                  <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Nome Empresa
                    </label>
                    <input 
                      type="text" 
                      className={`form-input ${validationErrors.nomeEmpresa ? 'error' : ''}`}
                      placeholder="Nome da empresa"
                      value={marcacaoFormData.nomeEmpresa || ''}
                      onChange={(e) => setMarcacaoFormData({...marcacaoFormData, nomeEmpresa: e.target.value})}
                    />
                    {validationErrors.nomeEmpresa && (
                      <span className="error-message">{validationErrors.nomeEmpresa}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Nome Responsável/Proprietário
                    </label>
                    <input 
                      type="text" 
                      className={`form-input ${validationErrors.nomeResponsavel ? 'error' : ''}`}
                      placeholder="Nome do responsável"
                      value={marcacaoFormData.nomeResponsavel || ''}
                      onChange={(e) => setMarcacaoFormData({...marcacaoFormData, nomeResponsavel: e.target.value})}
                    />
                    {validationErrors.nomeResponsavel && (
                      <span className="error-message">{validationErrors.nomeResponsavel}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> Contacto Cliente
                    </label>
                    <input 
                      type="tel" 
                      className={`form-input ${validationErrors.contactoCliente ? 'error' : ''}`}
                      placeholder="Número de telefone"
                      value={marcacaoFormData.contactoCliente || ''}
                      onChange={(e) => setMarcacaoFormData({...marcacaoFormData, contactoCliente: e.target.value})}
                    />
                    {validationErrors.contactoCliente && (
                      <span className="error-message">{validationErrors.contactoCliente}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Código-Postal / Localidade</label>
                    <div className="postal-code-group">
                      <div className="postal-code-field">
                        <input 
                          type="text" 
                          className="form-input" 
                          value={marcacaoFormData.codigoPostal1 || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setMarcacaoFormData({...marcacaoFormData, codigoPostal1: value});
                          }}
                          placeholder="0000"
                          maxLength={4}
                        />
                        <div className="field-hint">4 dígitos</div>
                      </div>
                      <div className="postal-code-field">
                        <input 
                          type="text" 
                          className="form-input" 
                          value={marcacaoFormData.codigoPostal2 || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setMarcacaoFormData({...marcacaoFormData, codigoPostal2: value});
                          }}
                          placeholder="000"
                          maxLength={3}
                        />
                        <div className="field-hint">3 dígitos</div>
                      </div>
                      <div className="postal-code-field localidade-field">
                        <input 
                          type="text" 
                          className="form-input" 
                          value={marcacaoFormData.localidade || ''}
                          onChange={(e) => {
                            setMarcacaoFormData({...marcacaoFormData, localidade: e.target.value});
                          }}
                          placeholder="Localidade"
                        />
                        <div className="field-hint">Localidade</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">
                      <span className="required">*</span> Morada
                    </label>
                    <input 
                      type="text" 
                      className={`form-input ${validationErrors.morada ? 'error' : ''}`}
                      placeholder="Morada completa"
                      value={marcacaoFormData.morada || ''}
                      onChange={(e) => setMarcacaoFormData({...marcacaoFormData, morada: e.target.value})}
                    />
                    {validationErrors.morada && (
                      <span className="error-message">{validationErrors.morada}</span>
                    )}
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Link Google Maps</label>
                    <input 
                      type="url" 
                      className="form-input" 
                      value={marcacaoFormData.linkGoogleMaps || ''}
                      onChange={(e) => setMarcacaoFormData({...marcacaoFormData, linkGoogleMaps: e.target.value})}
                      placeholder="https://maps.google.com/..."
                    />
                  </div>
                  </div>
                </div>
              )}

              
              {/* Form Navigation */}
              <div className="form-navigation">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handlePrevStep}
                  style={{ display: currentMarcacaoStep === 1 ? 'none' : 'block' }}
                >
                  Anterior
                </button>
                <div></div>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={async () => {
                    if (currentMarcacaoStep < 3) {
                      handleNextStep();
                    } else {
                      // Finish marcação - validate final step first
                      if (!validateStep(3)) {
                        return;
                      }
                      
                      // Validate all steps before submitting
                      const allErrors: any = {};
                      for (let step = 1; step <= 3; step++) {
                        const stepErrors = validateStep(step);
                        Object.assign(allErrors, stepErrors);
                      }
                      
                      if (Object.keys(allErrors).length > 0) {
                        setValidationErrors(allErrors);
                        setMarcacaoNotification({ 
                          message: 'Por favor, corrija os erros no formulário', 
                          type: 'error' 
                        });
                        setTimeout(() => setMarcacaoNotification(null), 4000);
                        return;
                      }
                      
                      // Submit to backend
                      await handleMarcacaoSubmit();
                    }
                  }}
                  disabled={savingMarcacao}
                >
                  {savingMarcacao ? 'A guardar...' : (currentMarcacaoStep === 3 ? 'Criar Marcação' : 'Seguinte')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderFechoModal = () => {
    if (!showFechoModal) return null;
    return (
      <div className="event-modal marcacao-modal fecho-modal">
        <div className="event-modal-content marcacao-modal-content">
          <div className="event-modal-header marcacao-header" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
            <button type="button" className="close-btn" onClick={() => { setShowFechoModal(false); setFechoFormData({}); setCurrentFechoStep(1); setFechoValidationErrors({}); setFechoNotification(null); setSelectedMarcacaoForFecho(null); }}>×</button>
            <h1>[Fecho]</h1>
            <p className="modal-subtitle">Criar Fecho a partir da Marcação</p>
            <div className="progress-steps-grid">
              <div className={`step ${currentFechoStep === 1 ? 'active' : currentFechoStep > 1 ? 'completed' : ''}`}><div className="step-icon">1</div><div className="step-title">Agendamento</div></div>
              <div className={`step ${currentFechoStep === 2 ? 'active' : currentFechoStep > 2 ? 'completed' : ''}`}><div className="step-icon">2</div><div className="step-title">Cliente</div></div>
            </div>
          </div>
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {fechoNotification && (
              <div className={`marcacao-notification marcacao-notification-${fechoNotification.type}`} style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, padding: '12px 24px', borderRadius: '8px', backgroundColor: fechoNotification.type === 'success' ? '#10b981' : '#ef4444', color: 'white', fontWeight: '500', maxWidth: '90%', textAlign: 'center' }}>
                {fechoNotification.message}
              </div>
            )}
            <form className="marcacao-form">
              {currentFechoStep === 1 && (
                <div className="form-section active">
                  <h3 className="section-title">Agendamento</h3>
                  <p className="section-description">Defina a data e hora para este Fecho</p>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Data</label>
                      <input type="date" className={`form-input ${fechoValidationErrors.marcacaoData ? 'error' : ''}`} value={fechoFormData.marcacaoData || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, marcacaoData: e.target.value })} />
                      {fechoValidationErrors.marcacaoData && <span className="error-message">{fechoValidationErrors.marcacaoData}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Hora de Início</label>
                      <input type="time" className={`form-input ${fechoValidationErrors.horaInicio ? 'error' : ''}`} value={fechoFormData.horaInicio || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, horaInicio: e.target.value })} />
                      {fechoValidationErrors.horaInicio && <span className="error-message">{fechoValidationErrors.horaInicio}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Hora de Fim</label>
                      <input type="time" className={`form-input ${fechoValidationErrors.horaFim ? 'error' : ''}`} value={fechoFormData.horaFim || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, horaFim: e.target.value })} />
                      {fechoValidationErrors.horaFim && <span className="error-message">{fechoValidationErrors.horaFim}</span>}
                    </div>
                    <div className="form-group full-width">
                      <label className="form-label">Observações</label>
                      <textarea className="form-input" rows={3} placeholder="Observações..." value={fechoFormData.marcacaoDescription || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, marcacaoDescription: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}
              {currentFechoStep === 2 && (
                <div className="form-section active">
                  <h3 className="section-title">Informação do Cliente</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Nome Empresa</label>
                      <input type="text" className={`form-input ${fechoValidationErrors.nomeEmpresa ? 'error' : ''}`} value={fechoFormData.nomeEmpresa || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, nomeEmpresa: e.target.value })} />
                      {fechoValidationErrors.nomeEmpresa && <span className="error-message">{fechoValidationErrors.nomeEmpresa}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Nome Responsável/Proprietário</label>
                      <input type="text" className={`form-input ${fechoValidationErrors.nomeResponsavel ? 'error' : ''}`} value={fechoFormData.nomeResponsavel || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, nomeResponsavel: e.target.value })} />
                      {fechoValidationErrors.nomeResponsavel && <span className="error-message">{fechoValidationErrors.nomeResponsavel}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label"><span className="required">*</span> Contacto Cliente</label>
                      <input type="tel" className={`form-input ${fechoValidationErrors.contactoCliente ? 'error' : ''}`} value={fechoFormData.contactoCliente || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, contactoCliente: e.target.value })} />
                      {fechoValidationErrors.contactoCliente && <span className="error-message">{fechoValidationErrors.contactoCliente}</span>}
                    </div>
                    <div className="form-group full-width">
                      <label className="form-label"><span className="required">*</span> Morada</label>
                      <input type="text" className={`form-input ${fechoValidationErrors.morada ? 'error' : ''}`} value={fechoFormData.morada || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, morada: e.target.value })} />
                      {fechoValidationErrors.morada && <span className="error-message">{fechoValidationErrors.morada}</span>}
                    </div>
                    <div className="form-group full-width">
                      <label className="form-label">Link Google Maps</label>
                      <input type="url" className="form-input" value={fechoFormData.linkGoogleMaps || ''} onChange={(e) => setFechoFormData({ ...fechoFormData, linkGoogleMaps: e.target.value })} placeholder="https://maps.google.com/..." />
                    </div>
                  </div>
                </div>
              )}
              <div className="form-navigation">
                <button type="button" className="btn btn-secondary" onClick={() => { setCurrentFechoStep(Math.max(1, currentFechoStep - 1)); setFechoValidationErrors({}); }} style={{ display: currentFechoStep === 1 ? 'none' : 'block' }}>Anterior</button>
                <div></div>
                <button type="button" className="btn btn-primary" onClick={async () => {
                  if (currentFechoStep < 2) {
                    if (validateFechoStep(currentFechoStep)) { setCurrentFechoStep(currentFechoStep + 1); setFechoValidationErrors({}); }
                  } else {
                    for (let step = 1; step <= 2; step++) if (!validateFechoStep(step)) return;
                    await handleFechoSubmit();
                  }
                }} disabled={savingFecho}>
                  {savingFecho ? 'A guardar...' : (currentFechoStep === 2 ? 'Criar Fecho' : 'Seguinte')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderRotaModal = () => {
    if (!showRotaModal) return null;
    
    const handleRotaSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      
      const errors = validateRotaForm();
      if (Object.keys(errors).length > 0) {
        setRotaValidationErrors(errors);
        return;
      }
      
      setRotaValidationErrors({});
      
      // Create new rota event
      const newRotaEvent = {
        id: `rota-${Date.now()}`,
        data: rotaFormData.data,
        comerciais: rotaFormData.comerciais,
        linkGoogleMaps: rotaFormData.linkGoogleMaps,
        userId: selectedUser?.id,
        level: 2 // Importance level 2
      };
      
      setRotaEvents(prev => [...prev, newRotaEvent]);
      setShowRotaModal(false);
      setRotaFormData({});
    };
    
    return (
      <div className="event-modal rota-modal">
        <div className="event-modal-content rota-modal-content">
          <div className="event-modal-header rota-header">
            <button type="button" className="close-btn" onClick={() => setShowRotaModal(false)}>×</button>
            
            <h1>Nova Rota</h1>
            <p className="modal-subtitle">Definir rota para o comercial</p>
          </div>
          
          <div className="event-modal-body">
            <form className="rota-form" onSubmit={handleRotaSubmit}>
              <div className="form-section active">
                <h3 className="section-title">Informações da Rota</h3>
                <p className="section-description">Configure a data, comercial e link da rota</p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Data <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input ${rotaValidationErrors.data ? 'error' : ''}`}
                      value={rotaFormData.data || ''}
                      onChange={(e) => setRotaFormData({...rotaFormData, data: e.target.value})}
                      required
                    />
                    {rotaValidationErrors.data && (
                      <span className="error-message">{rotaValidationErrors.data}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      Comercial / Comerciais <span className="required">*</span>
                    </label>
                    <div className={`multi-select-container ${rotaValidationErrors.comerciais ? 'error' : ''}`}>
                      <select
                        className="form-input"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            const newComerciais = [...(rotaFormData.comerciais || []), e.target.value];
                            setRotaFormData({...rotaFormData, comerciais: newComerciais});
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Selecionar comercial</option>
                        {comercialUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                      <div className="selected-comerciais">
                        {rotaFormData.comerciais?.map((userId: string) => {
                          const user = comercialUsers.find(u => u.id === userId);
                          return user ? (
                            <span key={userId} className="selected-tag">
                              {user.name}
                              <button
                                type="button"
                                className="remove-tag"
                                onClick={() => {
                                  setRotaFormData({
                                    ...rotaFormData,
                                    comerciais: rotaFormData.comerciais.filter((id: string) => id !== userId)
                                  });
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                    {rotaValidationErrors.comerciais && (
                      <span className="error-message">{rotaValidationErrors.comerciais}</span>
                    )}
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">
                      Link da Rota no Google Maps <span className="required">*</span>
                    </label>
                    <input
                      type="url"
                      className={`form-input ${rotaValidationErrors.linkGoogleMaps ? 'error' : ''}`}
                      placeholder="https://maps.google.com/..."
                      value={rotaFormData.linkGoogleMaps || ''}
                      onChange={(e) => setRotaFormData({...rotaFormData, linkGoogleMaps: e.target.value})}
                      required
                    />
                    {rotaValidationErrors.linkGoogleMaps && (
                      <span className="error-message">{rotaValidationErrors.linkGoogleMaps}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRotaModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary rota-btn-primary">
                  Criar Rota
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderBloqueioModal = () => {
    if (!showBloqueioModal) return null;
    
    const handleBloqueioSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      
      const errors = validateBloqueioForm();
      if (Object.keys(errors).length > 0) {
        setBloqueioValidationErrors(errors);
        return;
      }
      
      setBloqueioValidationErrors({});
      
      // Create new bloqueio event
      const newBloqueioEvent = {
        id: `bloqueio-${Date.now()}`,
        title: 'BLOQUEIO',
        time: bloqueioFormData.horaInicio,
        duration: bloqueioFormData.horaFim,
        type: 'bloqueio',
        description: 'Período bloqueado para eventos',
        attendees: bloqueioFormData.marcacaoUsers.map((id: string) => 
          comercialUsers.find((u: ComercialUser) => u.id === id)?.name || ''
        ),
        location: 'Bloqueio de horário',
        status: 'blocked',
        date: new Date(bloqueioFormData.marcacaoData),
        product: 'bloqueio',
        formData: {
          marcacaoId: `BLOQ-${Date.now()}`,
          marcacaoUsers: bloqueioFormData.marcacaoUsers,
          marcacaoData: bloqueioFormData.marcacaoData,
          horaInicio: bloqueioFormData.horaInicio,
          horaFim: bloqueioFormData.horaFim,
          marcacaoDescription: bloqueioFormData.marcacaoDescription || 'Período bloqueado'
        }
      };
      
      setBloqueioEvents(prev => [...prev, newBloqueioEvent]);
      setShowBloqueioModal(false);
      setBloqueioFormData({});
    };
    
    return (
      <div className="event-modal bloqueio-modal">
        <div className="event-modal-content bloqueio-modal-content">
          <div className="event-modal-header bloqueio-header">
            <button type="button" className="close-btn" onClick={() => setShowBloqueioModal(false)}>×</button>
            
            <h1>Novo Bloqueio</h1>
            <p className="modal-subtitle">Definir período bloqueado para o comercial</p>
          </div>
          
          <div className="event-modal-body">
            <form className="bloqueio-form" onSubmit={handleBloqueioSubmit}>
              <div className="form-section active">
                <h3 className="section-title">Informações do Bloqueio</h3>
                <p className="section-description">Configure a data, horário e comercial para o bloqueio</p>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Data <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input ${bloqueioValidationErrors.marcacaoData ? 'error' : ''}`}
                      value={bloqueioFormData.marcacaoData || ''}
                      onChange={(e) => setBloqueioFormData({...bloqueioFormData, marcacaoData: e.target.value})}
                      required
                    />
                    {bloqueioValidationErrors.marcacaoData && (
                      <span className="error-message">{bloqueioValidationErrors.marcacaoData}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      Hora de Início <span className="required">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-input ${bloqueioValidationErrors.horaInicio ? 'error' : ''}`}
                      value={bloqueioFormData.horaInicio || ''}
                      onChange={(e) => setBloqueioFormData({...bloqueioFormData, horaInicio: e.target.value})}
                      required
                    />
                    {bloqueioValidationErrors.horaInicio && (
                      <span className="error-message">{bloqueioValidationErrors.horaInicio}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      Hora de Fim <span className="required">*</span>
                    </label>
                    <input
                      type="time"
                      className={`form-input ${bloqueioValidationErrors.horaFim ? 'error' : ''}`}
                      value={bloqueioFormData.horaFim || ''}
                      onChange={(e) => setBloqueioFormData({...bloqueioFormData, horaFim: e.target.value})}
                      required
                    />
                    {bloqueioValidationErrors.horaFim && (
                      <span className="error-message">{bloqueioValidationErrors.horaFim}</span>
                    )}
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">
                      Comercial / Comerciais <span className="required">*</span>
                    </label>
                    <div className={`multi-select-container ${bloqueioValidationErrors.marcacaoUsers ? 'error' : ''}`}>
                      <select
                        className="form-input"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            const newUsers = [...(bloqueioFormData.marcacaoUsers || []), e.target.value];
                            setBloqueioFormData({...bloqueioFormData, marcacaoUsers: newUsers});
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Selecionar comercial</option>
                        {comercialUsers.filter((user: ComercialUser) => !bloqueioFormData.marcacaoUsers?.includes(user.id)).map((user: ComercialUser) => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                      <div className="selected-comerciais">
                        {bloqueioFormData.marcacaoUsers?.map((userId: string) => {
                          const user = comercialUsers.find((u: ComercialUser) => u.id === userId);
                          return user ? (
                            <span key={userId} className="selected-tag">
                              {user.name}
                              <button
                                type="button"
                                className="remove-tag"
                                onClick={() => {
                                  const newUsers = bloqueioFormData.marcacaoUsers.filter((id: string) => id !== userId);
                                  setBloqueioFormData({...bloqueioFormData, marcacaoUsers: newUsers});
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                    {bloqueioValidationErrors.marcacaoUsers && (
                      <span className="error-message">{bloqueioValidationErrors.marcacaoUsers}</span>
                    )}
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Descrição</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={bloqueioFormData.marcacaoDescription || ''}
                      onChange={(e) => setBloqueioFormData({...bloqueioFormData, marcacaoDescription: e.target.value})}
                      placeholder="Descrição do bloqueio (opcional)"
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowBloqueioModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary bloqueio-btn-primary">
                  Criar Bloqueio
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderTableView = () => {
    // Use fetchedEvents (marcações from backend) instead of sampleEvents
    // Filter events that are marcações (they have type 'marcacao' or formData with marcacaoId)
    // Comercial Calendar MUST NOT render Bloqueio events
    const marcacaoEvents = fetchedEvents.filter(event => 
      (event.type === 'marcacao' || event.formData?.marcacaoId) && event.type !== 'bloqueio'
    );
    
    // Also include sampleEvents as fallback if no fetchedEvents
    const allEvents = marcacaoEvents.length > 0 
      ? marcacaoEvents.filter(event => {
          // Comercial Calendar MUST NOT render Bloqueio events
          if (event.type === 'bloqueio') {
            return false;
          }
          // Filter by selected user if one is selected
          if (selectedUser) {
            const userIds = event.formData?.marcacaoUsers || [];
            // Handle both string and number IDs
            const userMatches = userIds.some((uid: any) => String(uid) === String(selectedUser.id));
            if (!userMatches) return false;
          }
          
          
          return true;
        })
      : sampleEvents.filter(event => {
          // Comercial Calendar MUST NOT render Bloqueio events
          if (event.type === 'bloqueio') {
            return false;
          }
          // Filter by selected user if one is selected
          if (selectedUser && !event.formData?.marcacaoUsers?.includes(selectedUser.id)) {
            return false;
          }
          
          
          return true;
        });

    // Sort events by date and time (earlier events first)
    const sortedEvents = [...allEvents].sort((a, b) => {
      // First compare dates
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      
      // Reset time to midnight for date comparison
      dateA.setHours(0, 0, 0, 0);
      dateB.setHours(0, 0, 0, 0);
      
      // If dates are different, sort by date (earlier first)
      const dateDiff = dateA.getTime() - dateB.getTime();
      if (dateDiff !== 0) {
        return dateDiff;
      }
      
      // If dates are the same, sort by time (earlier first)
      const timeA = a.formData?.horaInicio || a.time || '00:00';
      const timeB = b.formData?.horaInicio || b.time || '00:00';
      
      // Compare time strings (HH:MM format)
      return timeA.localeCompare(timeB);
    });

    return (
      <div className="table-view">
        <div className="table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>ID Marcação</th>
                <th>Data</th>
                <th>Horário</th>
                <th>Produto</th>
                <th>Empresa</th>
                <th>Comercial / Comerciais</th>
                <th>Responsável</th>
                <th>Contacto</th>
                <th>Morada</th>
                <th>Código Postal</th>
                <th>Google Maps</th>
                <th>Já tem Paineis</th>
                <th>Potência 13.80Kw</th>
                <th>Potência 6.9Kw</th>
                <th>Trifásico</th>
                <th>Tipo Telhado</th>
                <th>Paga mais que 250€</th>
                <th>Gás Natural</th>
                <th>Comercializadora</th>
                <th>Valor Fatura</th>
                <th>Fatura Presente</th>
                <th>Observações</th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map((event) => (
                <tr 
                  key={event.id} 
                  className={`${isEventMatch(event, eventSearchTerm) ? 'match-row' : ''}`}
                  onClick={() => handleEventClick(event)}
                >
                  <td>{event.formData?.marcacaoId || 'N/A'}</td>
                  <td>{event.formData?.marcacaoData || 'N/A'}</td>
                  <td>
                    {event.formData?.horaInicio || event.time} - {event.formData?.horaFim || event.duration}
                  </td>
                  <td>
                    <span className={`product-badge ${event.product}`}>
                      {event.formData?.produto === 'bairro-solar' ? 'Bairro Solar' :
                       event.formData?.produto === 'pap' ? 'PAP' :
                       event.formData?.produto === 'gas' ? 'Gás' :
                       event.formData?.produto === 'eletricidade' ? 'Eletricidade' : 'N/A'}
                    </span>
                  </td>
                  <td>{event.formData?.nomeEmpresa || 'N/A'}</td>
                  <td>
                    {(() => {
                      const userIds = event.formData?.marcacaoUsers || [];
                      if (userIds.length === 0 && event.formData?.marcacaoUser) {
                        // Fallback to single marcacaoUser for backward compatibility
                        const user = comercialUsers.find(u => u.id === String(event.formData.marcacaoUser));
                        return user ? user.name : 'N/A';
                      }
                      const userNames = userIds.map((userId: string | number) => {
                        const user = comercialUsers.find(u => u.id === String(userId));
                        return user ? user.name : null;
                      }).filter(Boolean);
                      return userNames.length > 0 ? userNames.join(', ') : 'N/A';
                    })()}
                  </td>
                  <td>{event.formData?.nomeResponsavel || 'N/A'}</td>
                  <td>{event.formData?.contactoCliente || 'N/A'}</td>
                  <td>{event.formData?.morada || 'N/A'}</td>
                  <td>
                    {event.formData?.codigoPostal1 && event.formData?.codigoPostal2 
                      ? `${event.formData.codigoPostal1}-${event.formData.codigoPostal2}`
                      : 'N/A'
                    }
                  </td>
                  <td>
                    {event.formData?.linkGoogleMaps ? (
                      <a 
                        href={event.formData.linkGoogleMaps} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="maps-link"
                      >
                        Ver no Maps
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.jaTemPaineis === 'sim' || event.formData?.jaTemPaineisPAP === 'sim' ? 'yes' : 'no'}`}>
                      {event.formData?.jaTemPaineis === 'sim' || event.formData?.jaTemPaineisPAP === 'sim' ? 'Sim' : 
                       event.formData?.jaTemPaineis === 'não' || event.formData?.jaTemPaineisPAP === 'não' ? 'Não' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.potencia1035 === 'sim' ? 'yes' : 'no'}`}>
                      {event.formData?.potencia1035 === 'sim' ? 'Sim' : 
                       event.formData?.potencia1035 === 'não' ? 'Não' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.potencia69 === 'sim' ? 'yes' : 'no'}`}>
                      {event.formData?.potencia69 === 'sim' ? 'Sim' : 
                       event.formData?.potencia69 === 'não' ? 'Não' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`field-value ${(event.formData?.trifasicoPAP || event.formData?.trifasico) === 'sim' ? 'yes' : 'no'}`}>
                      {(event.formData?.trifasicoPAP || event.formData?.trifasico) === 'sim' ? 'Sim' : 
                       (event.formData?.trifasicoPAP || event.formData?.trifasico) === 'não' || (event.formData?.trifasicoPAP || event.formData?.trifasico) === 'nao' ? 'Não' : 
                       (event.formData?.trifasicoPAP || event.formData?.trifasico) === 'nao_sabe' ? 'Não Sabe' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    {event.formData?.tipoTelhado || event.formData?.tipoTelhadoPAP || 'N/A'}
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.pagaMais250 === 'sim' ? 'yes' : 'no'}`}>
                      {event.formData?.pagaMais250 === 'sim' ? 'Sim' : 
                       event.formData?.pagaMais250 === 'não' ? 'Não' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.gasNatural ? 'yes' : 'no'}`}>
                      {event.formData?.gasNatural ? 'Confirmo' : 
                       event.formData?.gasNatural === false ? 'Não confirmo' : 'N/A'}
                    </span>
                  </td>
                  <td>
                    {event.formData?.comercializadoraGas || event.formData?.comercializadoraEletricidade || 'N/A'}
                  </td>
                  <td>
                    {event.formData?.valorFaturaGas || event.formData?.valorFaturaEletricidade || 'N/A'}
                  </td>
                  <td>
                    <span className={`field-value ${event.formData?.faturaPresenteBairro === 'sim' || event.formData?.faturaPresentePAP === 'sim' || event.formData?.faturaPresenteGas === 'sim' || event.formData?.faturaPresenteEletricidade === 'sim' ? 'yes' : 'no'}`}>
                      {event.formData?.faturaPresenteBairro === 'sim' || event.formData?.faturaPresentePAP === 'sim' || event.formData?.faturaPresenteGas === 'sim' || event.formData?.faturaPresenteEletricidade === 'sim' ? 'Sim' : 
                       event.formData?.faturaPresenteBairro === 'não' || event.formData?.faturaPresentePAP === 'não' || event.formData?.faturaPresenteGas === 'não' || event.formData?.faturaPresenteEletricidade === 'não' ? 'Não' : 'N/A'}
                    </span>
                  </td>
                  <td>{event.formData?.marcacaoDescription || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMarcacaoEventDetailModal = () => {
    if (!showMarcacaoEventDetailModal || !selectedMarcacaoEvent) return null;
    
    const formData = selectedMarcacaoEvent.formData || {};
    const isFechoEvent = selectedMarcacaoEvent.type === 'fecho' || (formData.marcacaoId && String(formData.marcacaoId).startsWith('FECHO-'));
    const oc = formData.original_callcenter || {};
    const co = formData.comercial_overrides || {};
    const getVal = (...sources: (string | boolean | undefined)[]) => {
      const v = sources.find(x => x != null && x !== '');
      return v === true || v === false ? String(v) : (v ?? '');
    };
    // Original CallCenter input (preserved for all fields including Cliente) with explicit product keys
    const originalFormData = formData.original_callcenter
      ? {
          ...formData,
          ...oc,
          comercializadoraBairroSolar: getVal(oc.comercializadoraBairroSolar, formData.comercializadoraBairroSolar),
          comercializadoraPAP: getVal(oc.comercializadoraPAP, formData.comercializadoraPAP),
          comercializadoraGas: getVal(oc.comercializadoraGas, formData.comercializadoraGas),
          comercializadoraEletricidade: getVal(oc.comercializadoraEletricidade, formData.comercializadoraEletricidade),
          valorFaturaGas: getVal(oc.valorFaturaGas, formData.valorFaturaGas),
          valorFaturaEletricidade: getVal(oc.valorFaturaEletricidade, formData.valorFaturaEletricidade),
          faturaPresenteGas: getVal(oc.faturaPresenteGas, formData.faturaPresenteGas),
          faturaPresenteEletricidade: getVal(oc.faturaPresenteEletricidade, formData.faturaPresenteEletricidade),
          gasNatural: getVal(oc.gasNatural, formData.gasNatural)
        }
      : formData;
    // Only show "Original (CallCenter)" when a comercial user has made alterations
    const hasComercialAlterations = !!(
      (formData.comercial_overrides && Object.keys(formData.comercial_overrides).length > 0) ||
      (formData.informacoesComerciais?.nota && String(formData.informacoesComerciais.nota).trim() !== '')
    );
    // Merge original_callcenter + form_data + comercial_overrides with explicit product keys so Comercializadora etc. show
    const currentFormData = {
      ...oc,
      ...formData,
      ...co,
      comercializadoraBairroSolar: getVal(co.comercializadoraBairroSolar, formData.comercializadoraBairroSolar, oc.comercializadoraBairroSolar),
      comercializadoraPAP: getVal(co.comercializadoraPAP, formData.comercializadoraPAP, oc.comercializadoraPAP),
      comercializadoraGas: getVal(co.comercializadoraGas, formData.comercializadoraGas, oc.comercializadoraGas),
      comercializadoraEletricidade: getVal(co.comercializadoraEletricidade, formData.comercializadoraEletricidade, oc.comercializadoraEletricidade),
      valorFaturaGas: getVal(co.valorFaturaGas, formData.valorFaturaGas, oc.valorFaturaGas),
      valorFaturaEletricidade: getVal(co.valorFaturaEletricidade, formData.valorFaturaEletricidade, oc.valorFaturaEletricidade),
      faturaPresenteGas: getVal(co.faturaPresenteGas, formData.faturaPresenteGas, oc.faturaPresenteGas),
      faturaPresenteEletricidade: getVal(co.faturaPresenteEletricidade, formData.faturaPresenteEletricidade, oc.faturaPresenteEletricidade),
      gasNatural: getVal(co.gasNatural, formData.gasNatural, oc.gasNatural)
    };
    const displayFormData = hasComercialAlterations ? originalFormData : currentFormData;
    // Normalize product key for matching (e.g. 'gás' -> 'gas', 'Bairro Solar' -> 'bairro-solar')
    const normProd = (p: string) => String(p || '').toLowerCase().replace(/\s+/g, '-').replace(/á/g, 'a').replace(/ã/g, 'a');
    
    // Parse date - handle both DD/MM/YYYY and Date object
    let marcacaoDate: Date | null = null;
    if (selectedMarcacaoEvent.date) {
      marcacaoDate = selectedMarcacaoEvent.date;
    } else if (formData.marcacaoData) {
      // Try parsing DD/MM/YYYY format
      const dateParts = formData.marcacaoData.split('/');
      if (dateParts.length === 3) {
        marcacaoDate = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
      } else {
        // Try YYYY-MM-DD format
        marcacaoDate = new Date(formData.marcacaoData);
      }
    }
    if (!marcacaoDate || isNaN(marcacaoDate.getTime())) {
      marcacaoDate = new Date();
    }
    
    // Format date to DD/MM/YYYY
    const formatDateDDMMYYYY = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    // Format marcacaoData string to DD/MM/YYYY if needed
    const formatMarcacaoData = (dateStr: string | undefined): string => {
      // Ensure we have a valid date to use as fallback
      const fallbackDate = marcacaoDate || new Date();
      
      if (!dateStr) return formatDateDDMMYYYY(fallbackDate);
      
      // Trim whitespace and take only date part (before any space or T for datetime strings)
      const cleanDateStr = dateStr.trim().split(' ')[0].split('T')[0];
      
      // If already in DD/MM/YYYY format, return as is
      if (cleanDateStr.includes('/')) {
        const parts = cleanDateStr.split('/');
        if (parts.length === 3 && parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
          return cleanDateStr;
        }
      }
      
      // If in YYYY-MM-DD format, convert to DD/MM/YYYY
      if (cleanDateStr.includes('-')) {
        const parts = cleanDateStr.split('-');
        if (parts.length === 3) {
          // Handle YYYY-MM-DD format (year is first part with 4 digits)
          if (parts[0].length === 4) {
            const day = parts[2].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[0];
            return `${day}/${month}/${year}`;
          }
          // Handle DD-MM-YYYY format (year is last part with 4 digits)
          if (parts[2].length === 4) {
            return cleanDateStr.replace(/-/g, '/');
          }
        }
      }
      
      // Try to parse and format using Date object
      try {
        const parsedDate = new Date(cleanDateStr);
        if (!isNaN(parsedDate.getTime())) {
          return formatDateDDMMYYYY(parsedDate);
        }
      } catch (e) {
        // Fallback
      }
      
      // Final fallback
      return formatDateDDMMYYYY(fallbackDate);
    };
    
    const marcacaoId = formData.marcacaoId || 'N/A';
    const produto = formData.produto || selectedMarcacaoEvent.product || 'N/A';
    
    // Get produtos array (for multi-product: show all fields grouped by product, matching CallCenterCalendar)
    let produtos: string[] = [];
    if (formData.produtos && Array.isArray(formData.produtos)) {
      produtos = formData.produtos;
    } else if (selectedMarcacaoEvent.formData?.produtos && Array.isArray(selectedMarcacaoEvent.formData.produtos)) {
      produtos = selectedMarcacaoEvent.formData.produtos;
    } else if (selectedMarcacaoEvent.title) {
      const produtoTitles: Record<string, string> = {
        'Bairro Solar': 'bairro-solar',
        'PAP': 'pap',
        'Gás': 'gas',
        'Eletricidade': 'eletricidade'
      };
      const titleParts = selectedMarcacaoEvent.title.split(' - ');
      const extractedProdutos: string[] = [];
      for (const part of titleParts) {
        const trimmedPart = part.trim();
        for (const [displayName, productKey] of Object.entries(produtoTitles)) {
          if (trimmedPart === displayName && !extractedProdutos.includes(productKey)) {
            extractedProdutos.push(productKey);
            break;
          }
        }
        if (trimmedPart && !Object.keys(produtoTitles).includes(trimmedPart) && extractedProdutos.length > 0) {
          break;
        }
      }
      if (extractedProdutos.length > 0) produtos = extractedProdutos;
    }
    if (produtos.length === 0 && produto && produto !== 'N/A') {
      produtos = [produto];
    }
    // Normalize product keys for consistent matching (e.g. 'Gás' -> 'gas', 'Bairro Solar' -> 'bairro-solar')
    produtos = produtos.map((p: string) => {
      const normalized = normProd(p);
      return normalized || p;
    });
    
    // Get comerciais names
    const userIds = formData.marcacaoUsers || [];
    const comerciais = userIds.map((userId: string | number) => 
      comercialUsers.find(u => u.id === String(userId))?.name
    ).filter(Boolean).join(', ') || 'N/A';
    
    // Get product display name (single) and helper for multiple
    const produtoDisplay = produto === 'bairro-solar' ? 'Bairro Solar' : 
                           produto === 'pap' ? 'PAP (Pay As Produced)' : 
                           produto === 'gas' ? 'Gás' : 
                           produto === 'eletricidade' ? 'Eletricidade' : 'N/A';
    const productNamesMap: { [key: string]: string } = {
      'bairro-solar': 'Bairro Solar',
      'pap': 'PAY AS PRODUCED (PAP)',
      'gas': 'Gás',
      'eletricidade': 'Eletricidade'
    };
    
    // Check if event was rescheduled (has reschedule data, regardless of current substage)
    const hasRescheduleData = formData.rescheduleReason || formData.rescheduleOldDate || 
                              formData.rescheduleOldHoraInicio || formData.oldDate ||
                              formData.oldHoraInicio;
    const isRescheduled = hasRescheduleData || 
                          formData.isRescheduled || 
                          formData.gestao_substage === 'agendamentos-remarcacao';
    const isNotOkCallCenter = formData.gestao_substage === 'feedback-pendencia-not-ok-callcenter';
    const isNotOkCliente = formData.gestao_substage === 'feedback-pendencia-not-ok-cliente';
    const isPendente = formData.gestao_substage === 'feedback-pendencia-pendente';
    const isConcluidoSemProposta = formData.gestao_substage === 'feedback-pendencia-concluido-sem-proposta';
    const visitaValidaSubstages = [
      'feedback-pendencia-concluido-com-proposta',
      'resultado-fecho-ok',
      'resultado-fecho-not-ok',
      'resultado-aguardar',
      'resultado-cancelado'
    ];
    const isVisitaValida = formData.gestao_substage && visitaValidaSubstages.includes(formData.gestao_substage);
    
    // Get product colors for dynamic styling
    const getProductColors = (
      prod: string,
      isRescheduledEvent: boolean = false,
      isNotOkCallCenterEvent: boolean = false,
      isNotOkClienteEvent: boolean = false,
      isVisitaValidaEvent: boolean = false,
      isPendenteEvent: boolean = false,
      isConcluidoSemPropostaEvent: boolean = false
    ) => {
      if (isNotOkClienteEvent) {
        return {
          primary: '#dc2626',
          secondary: '#b91c1c',
          light: '#ef4444',
          text: '#ffffff'
        };
      }
      
      if (isNotOkCallCenterEvent) {
        return {
          primary: '#1e3a8a',
          secondary: '#1e40af',
          light: '#3b82f6',
          text: '#ffffff'
        };
      }

      if (isPendenteEvent) {
        return {
          primary: '#ca8a04',
          secondary: '#a16207',
          light: '#eab308',
          text: '#ffffff'
        };
      }

      if (isConcluidoSemPropostaEvent) {
        return {
          primary: '#6b21a8',
          secondary: '#581c87',
          light: '#7e22ce',
          text: '#ffffff'
        };
      }
      
      // If Visita Válida (feedback-pendencia except not-ok-callcenter), use light green color
      if (isVisitaValidaEvent) {
        return {
          primary: '#86efac',
          secondary: '#4ade80',
          light: '#bbf7d0',
          text: '#166534'
        };
      }
      
      // If rescheduled, use salmon color
      if (isRescheduledEvent) {
        return {
          primary: '#E39D90',
          secondary: '#E39D90',
          light: '#E39D90',
          text: '#ffffff'
        };
      }
      
      // Otherwise, use product-specific colors
      switch (prod) {
        case 'bairro-solar':
          return {
            primary: '#3b82f6',
            secondary: '#2563eb',
            light: '#60a5fa',
            text: '#1e40af'
          };
        case 'pap':
          return {
            primary: '#3b82f6',
            secondary: '#2563eb',
            light: '#60a5fa',
            text: '#1e40af'
          };
        case 'gas':
          return {
            primary: '#f59e0b',
            secondary: '#d97706',
            light: '#fbbf24',
            text: '#92400e'
          };
        case 'eletricidade':
          return {
            primary: '#f59e0b',
            secondary: '#d97706',
            light: '#fbbf24',
            text: '#92400e'
          };
        default:
          return {
            primary: '#3b82f6',
            secondary: '#2563eb',
            light: '#60a5fa',
            text: '#1e40af'
          };
      }
    };
    
    const productColors = getProductColors(
      produto,
      isRescheduled,
      isNotOkCallCenter,
      isNotOkCliente,
      isVisitaValida,
      isPendente,
      isConcluidoSemProposta
    );
    
    return (
      <div className="event-modal marcacao-detail-modal">
        <div className="event-modal-content">
          <div 
            className="event-modal-header marcacao-detail-header"
            style={{
              background: `linear-gradient(135deg, ${productColors.primary} 0%, ${productColors.secondary} 100%)`,
              borderBottom: `1px solid ${productColors.light}`
            }}
          >
            <h3>{isEditingMarcacao ? (isFechoEvent ? 'Editar Fecho' : 'Editar Marcação') : (selectedMarcacaoEvent.title || 'MARCACAO')}</h3>
            <button type="button" className="close-btn" onClick={() => { 
              setShowMarcacaoEventDetailModal(false); 
              setSelectedMarcacaoEvent(null);
              setIsCallCenterInfoCollapsed(false);
              setIsRescheduleReasonCollapsed(false);
              setIsNotOkCallCenterFeedbackCollapsed(false);
              setIsEditingMarcacao(false);
              setEditMarcacaoValidationErrors({});
              setEditMarcacaoNotification(null);
              if (modalOnly && onCloseModal) onCloseModal();
            }}>×</button>
          </div>
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {editMarcacaoNotification && (
              <div 
                className={`rota-notification rota-notification-${editMarcacaoNotification.type}`}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: editMarcacaoNotification.type === 'success' ? '#10b981' : '#ef4444',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  maxWidth: '400px',
                  textAlign: 'center',
                  width: '90%'
                }}
              >
                {editMarcacaoNotification.type === 'success' ? '✓ ' : '✗ '}
                {editMarcacaoNotification.message}
              </div>
            )}
            {isEditingMarcacao ? (
              <form className="marcacao-edit-form" onSubmit={(e) => { e.preventDefault(); handleSaveMarcacao(); }}>
                <div className="form-section active">
                  <h3 className="section-title">{isFechoEvent ? 'Editar Fecho' : 'Editar Marcação'}</h3>
                  
                  {/* Step 1: Agendamento */}
                  <div className="form-section-inner">
                    <h4>Agendamento</h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          Data <span className="required">*</span>
                        </label>
                        <input 
                          type="date" 
                          className={`form-input ${editMarcacaoValidationErrors.marcacaoData ? 'error' : ''}`}
                          value={editMarcacaoFormData.marcacaoData || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, marcacaoData: e.target.value});
                            if (editMarcacaoValidationErrors.marcacaoData) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, marcacaoData: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.marcacaoData && (
                          <span className="error-message">{editMarcacaoValidationErrors.marcacaoData}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Hora de Início <span className="required">*</span>
                        </label>
                        <input 
                          type="time" 
                          className={`form-input ${editMarcacaoValidationErrors.horaInicio ? 'error' : ''}`}
                          value={editMarcacaoFormData.horaInicio || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, horaInicio: e.target.value});
                            if (editMarcacaoValidationErrors.horaInicio) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, horaInicio: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.horaInicio && (
                          <span className="error-message">{editMarcacaoValidationErrors.horaInicio}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Hora de Fim <span className="required">*</span>
                        </label>
                        <input 
                          type="time" 
                          className={`form-input ${editMarcacaoValidationErrors.horaFim ? 'error' : ''}`}
                          value={editMarcacaoFormData.horaFim || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, horaFim: e.target.value});
                            if (editMarcacaoValidationErrors.horaFim) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, horaFim: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.horaFim && (
                          <span className="error-message">{editMarcacaoValidationErrors.horaFim}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">
                          Comercial / Comerciais <span className="required">*</span>
                        </label>
                        <div className={`multi-select-container ${editMarcacaoValidationErrors.marcacaoUsers ? 'error' : ''}`}>
                          <select
                            className="form-input"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                const newUsers = [...(editMarcacaoFormData.marcacaoUsers || []), e.target.value];
                                setEditMarcacaoFormData({...editMarcacaoFormData, marcacaoUsers: newUsers});
                                e.target.value = '';
                                if (editMarcacaoValidationErrors.marcacaoUsers) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, marcacaoUsers: undefined});
                                }
                              }
                            }}
                          >
                            <option value="">Selecionar comercial</option>
                            {comercialUsers.map(user => {
                              const isSelected = editMarcacaoFormData.marcacaoUsers?.includes(user.id);
                              return (
                                <option 
                                  key={user.id} 
                                  value={user.id}
                                  disabled={isSelected}
                                >
                                  {isSelected ? `${user.name} ✓ (já selecionado)` : user.name}
                                </option>
                              );
                            })}
                          </select>
                          <div className="selected-comerciais">
                            {editMarcacaoFormData.marcacaoUsers?.map((userId: string) => {
                              const user = comercialUsers.find(u => u.id === userId);
                              return user ? (
                                <span key={userId} className="selected-tag">
                                  {user.name}
                                  <button
                                    type="button"
                                    className="remove-tag"
                                    onClick={() => {
                                      setEditMarcacaoFormData({
                                        ...editMarcacaoFormData,
                                        marcacaoUsers: editMarcacaoFormData.marcacaoUsers.filter((id: string) => id !== userId)
                                      });
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                        {editMarcacaoValidationErrors.marcacaoUsers && (
                          <span className="error-message">{editMarcacaoValidationErrors.marcacaoUsers}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">Nota</label>
                        <textarea 
                          className="form-input" 
                          rows={3} 
                          value={editMarcacaoFormData.marcacaoDescription || ''}
                          onChange={(e) => setEditMarcacaoFormData({...editMarcacaoFormData, marcacaoDescription: e.target.value})}
                          placeholder="Adicione uma nota sobre esta marcação..."
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 2: Produto(s) - read-only list, then editable fields per product */}
                  <div className="form-section-inner">
                    <h4>Produto(s)</h4>
                    <div className="form-group">
                      <label className="form-label">Produtos da marcação</label>
                      <input
                        type="text"
                        className="form-input"
                        value={(editMarcacaoFormData.produtos && Array.isArray(editMarcacaoFormData.produtos)
                          ? (editMarcacaoFormData.produtos as string[]).map((p: string) => p === 'bairro-solar' ? 'Bairro Solar' : p === 'pap' ? 'PAP' : p === 'gas' ? 'Gás' : p === 'eletricidade' ? 'Eletricidade' : p).join(' + ')
                          : produtoDisplay)}
                        disabled
                        style={{ backgroundColor: '#f5f5f5', color: '#666', cursor: 'not-allowed' }}
                      />
                    </div>
                    
                    {/* Product-specific fields: one block per product in produtos */}
                    {((editMarcacaoFormData.produtos && Array.isArray(editMarcacaoFormData.produtos))
                      ? (editMarcacaoFormData.produtos as string[])
                      : (editMarcacaoFormData.produto ? [editMarcacaoFormData.produto] : [])).map((prod: string) => (
                      <div key={prod} className="form-grid" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                        <div className="form-group full-width" style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: 'var(--wondertrade-red)' }}>
                            {prod === 'bairro-solar' ? 'Bairro Solar' : prod === 'pap' ? 'PAP' : prod === 'gas' ? 'Gás' : prod === 'eletricidade' ? 'Eletricidade' : prod}
                          </strong>
                        </div>
                    {prod === 'bairro-solar' && (
                      <div className="form-grid form-grid--product-fields">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Já tem Paineis?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.jaTemPaineis ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.jaTemPaineis === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, jaTemPaineis: 'sim'});
                                if (editMarcacaoValidationErrors.jaTemPaineis) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, jaTemPaineis: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.jaTemPaineis === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, jaTemPaineis: 'nao'});
                                if (editMarcacaoValidationErrors.jaTemPaineis) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, jaTemPaineis: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.jaTemPaineis && (
                            <span className="error-message">{editMarcacaoValidationErrors.jaTemPaineis}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Potência 13.80Kw?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.potencia1035 ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia1035 === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia1035: 'sim'});
                                if (editMarcacaoValidationErrors.potencia1035) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia1035: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia1035 === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia1035: 'nao'});
                                if (editMarcacaoValidationErrors.potencia1035) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia1035: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia1035 === 'nao_sabe' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia1035: 'nao_sabe'});
                                if (editMarcacaoValidationErrors.potencia1035) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia1035: undefined});
                                }
                              }}
                            >
                              Não Sabe
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.potencia1035 && (
                            <span className="error-message">{editMarcacaoValidationErrors.potencia1035}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Trifásico?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.trifasico ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.trifasico === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, trifasico: 'sim'});
                                if (editMarcacaoValidationErrors.trifasico) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, trifasico: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.trifasico === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, trifasico: 'nao'});
                                if (editMarcacaoValidationErrors.trifasico) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, trifasico: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.trifasico === 'nao_sabe' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, trifasico: 'nao_sabe'});
                                if (editMarcacaoValidationErrors.trifasico) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, trifasico: undefined});
                                }
                              }}
                            >
                              Não Sabe
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.trifasico && (
                            <span className="error-message">{editMarcacaoValidationErrors.trifasico}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Tipo de Telhado
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.tipoTelhado ? 'error' : ''}`}
                            placeholder="Ex: Telha cerâmica"
                            value={editMarcacaoFormData.tipoTelhado || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, tipoTelhado: e.target.value});
                              if (editMarcacaoValidationErrors.tipoTelhado) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, tipoTelhado: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.tipoTelhado && (
                            <span className="error-message">{editMarcacaoValidationErrors.tipoTelhado}</span>
                          )}
                        </div>
                        
                        <div className="form-group full-width">
                          <label className="form-label">
                            <span className="required">*</span> Fatura presente no dia da visita?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.faturaPresenteBairro ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteBairro === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteBairro: 'sim'});
                                if (editMarcacaoValidationErrors.faturaPresenteBairro) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteBairro: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteBairro === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteBairro: 'nao'});
                                if (editMarcacaoValidationErrors.faturaPresenteBairro) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteBairro: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.faturaPresenteBairro && (
                            <span className="error-message">{editMarcacaoValidationErrors.faturaPresenteBairro}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {prod === 'pap' && (
                      <div className="form-grid form-grid--product-fields">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Já tem Paineis?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.jaTemPaineisPAP ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.jaTemPaineisPAP === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, jaTemPaineisPAP: 'sim'});
                                if (editMarcacaoValidationErrors.jaTemPaineisPAP) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, jaTemPaineisPAP: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.jaTemPaineisPAP === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, jaTemPaineisPAP: 'nao'});
                                if (editMarcacaoValidationErrors.jaTemPaineisPAP) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, jaTemPaineisPAP: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.jaTemPaineisPAP && (
                            <span className="error-message">{editMarcacaoValidationErrors.jaTemPaineisPAP}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Potência 6.9Kw?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.potencia69 ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia69 === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia69: 'sim'});
                                if (editMarcacaoValidationErrors.potencia69) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia69: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia69 === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia69: 'nao'});
                                if (editMarcacaoValidationErrors.potencia69) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia69: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.potencia69 === 'nao_sabe' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, potencia69: 'nao_sabe'});
                                if (editMarcacaoValidationErrors.potencia69) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, potencia69: undefined});
                                }
                              }}
                            >
                              Não Sabe
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.potencia69 && (
                            <span className="error-message">{editMarcacaoValidationErrors.potencia69}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Tipo de Telhado
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.tipoTelhadoPAP ? 'error' : ''}`}
                            placeholder="Ex: Telha cerâmica"
                            value={editMarcacaoFormData.tipoTelhadoPAP || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, tipoTelhadoPAP: e.target.value});
                              if (editMarcacaoValidationErrors.tipoTelhadoPAP) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, tipoTelhadoPAP: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.tipoTelhadoPAP && (
                            <span className="error-message">{editMarcacaoValidationErrors.tipoTelhadoPAP}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Paga mais que 250€?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.pagaMais250 ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.pagaMais250 === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, pagaMais250: 'sim'});
                                if (editMarcacaoValidationErrors.pagaMais250) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, pagaMais250: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.pagaMais250 === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, pagaMais250: 'nao'});
                                if (editMarcacaoValidationErrors.pagaMais250) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, pagaMais250: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.pagaMais250 && (
                            <span className="error-message">{editMarcacaoValidationErrors.pagaMais250}</span>
                          )}
                        </div>
                        
                        <div className="form-group full-width">
                          <label className="form-label">
                            <span className="required">*</span> Fatura presente no dia da visita?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.faturaPresentePAP ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresentePAP === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresentePAP: 'sim'});
                                if (editMarcacaoValidationErrors.faturaPresentePAP) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresentePAP: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresentePAP === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresentePAP: 'nao'});
                                if (editMarcacaoValidationErrors.faturaPresentePAP) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresentePAP: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.faturaPresentePAP && (
                            <span className="error-message">{editMarcacaoValidationErrors.faturaPresentePAP}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {prod === 'gas' && (
                      <div className="form-grid form-grid--product-fields">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Gás natural e Canalizado?
                          </label>
                          <div className={`checkbox-group ${editMarcacaoValidationErrors.gasNatural ? 'error' : ''}`}>
                            <div className="checkbox-wrapper">
                              <input 
                                id="gas-natural-edit-checkbox"
                                type="checkbox" 
                                checked={editMarcacaoFormData.gasNatural === true}
                                onChange={(e) => {
                                  setEditMarcacaoFormData({...editMarcacaoFormData, gasNatural: e.target.checked});
                                  if (editMarcacaoValidationErrors.gasNatural) {
                                    setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, gasNatural: undefined});
                                  }
                                }}
                              />
                              <label className="terms-label" htmlFor="gas-natural-edit-checkbox">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 200 200" className="checkbox-svg">
                                  <mask fill="white" id="path-1-inside-1_476_5-gas-edit">
                                    <rect height={200} width={200} />
                                  </mask>
                                  <rect mask="url(#path-1-inside-1_476_5-gas-edit)" strokeWidth={40} className="checkbox-box" height={200} width={200} />
                                  <path strokeWidth={15} d="M52 111.018L76.9867 136L149 64" className="checkbox-tick" />
                                </svg>
                                <span className="label-text">Confirmo</span>
                              </label>
                            </div>
                          </div>
                          {editMarcacaoValidationErrors.gasNatural && (
                            <span className="error-message">{editMarcacaoValidationErrors.gasNatural}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Comercializadora Atual
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.comercializadoraGas ? 'error' : ''}`}
                            placeholder="Ex: EDP Comercial"
                            value={editMarcacaoFormData.comercializadoraGas || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, comercializadoraGas: e.target.value});
                              if (editMarcacaoValidationErrors.comercializadoraGas) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, comercializadoraGas: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.comercializadoraGas && (
                            <span className="error-message">{editMarcacaoValidationErrors.comercializadoraGas}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Valor da Última Fatura
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.valorFaturaGas ? 'error' : ''}`}
                            placeholder="Ex: 85.50€"
                            value={editMarcacaoFormData.valorFaturaGas || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, valorFaturaGas: e.target.value});
                              if (editMarcacaoValidationErrors.valorFaturaGas) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, valorFaturaGas: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.valorFaturaGas && (
                            <span className="error-message">{editMarcacaoValidationErrors.valorFaturaGas}</span>
                          )}
                        </div>
                        
                        <div className="form-group full-width">
                          <label className="form-label">
                            <span className="required">*</span> Fatura Presente no Dia da Reunião?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.faturaPresenteGas ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteGas === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteGas: 'sim'});
                                if (editMarcacaoValidationErrors.faturaPresenteGas) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteGas: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteGas === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteGas: 'nao'});
                                if (editMarcacaoValidationErrors.faturaPresenteGas) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteGas: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.faturaPresenteGas && (
                            <span className="error-message">{editMarcacaoValidationErrors.faturaPresenteGas}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {prod === 'eletricidade' && (
                      <div className="form-grid form-grid--product-fields">
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Comercializadora Atual
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.comercializadoraEletricidade ? 'error' : ''}`}
                            placeholder="Ex: EDP Comercial"
                            value={editMarcacaoFormData.comercializadoraEletricidade || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, comercializadoraEletricidade: e.target.value});
                              if (editMarcacaoValidationErrors.comercializadoraEletricidade) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, comercializadoraEletricidade: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.comercializadoraEletricidade && (
                            <span className="error-message">{editMarcacaoValidationErrors.comercializadoraEletricidade}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label className="form-label">
                            <span className="required">*</span> Valor da Última Fatura
                          </label>
                          <input 
                            type="text" 
                            className={`form-input ${editMarcacaoValidationErrors.valorFaturaEletricidade ? 'error' : ''}`}
                            placeholder="Ex: 125.30€"
                            value={editMarcacaoFormData.valorFaturaEletricidade || ''}
                            onChange={(e) => {
                              setEditMarcacaoFormData({...editMarcacaoFormData, valorFaturaEletricidade: e.target.value});
                              if (editMarcacaoValidationErrors.valorFaturaEletricidade) {
                                setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, valorFaturaEletricidade: undefined});
                              }
                            }}
                          />
                          {editMarcacaoValidationErrors.valorFaturaEletricidade && (
                            <span className="error-message">{editMarcacaoValidationErrors.valorFaturaEletricidade}</span>
                          )}
                        </div>
                        
                        <div className="form-group full-width">
                          <label className="form-label">
                            <span className="required">*</span> Fatura Presente no Dia da Reunião?
                          </label>
                          <div className={`button-group ${editMarcacaoValidationErrors.faturaPresenteEletricidade ? 'error' : ''}`}>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteEletricidade === 'sim' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteEletricidade: 'sim'});
                                if (editMarcacaoValidationErrors.faturaPresenteEletricidade) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteEletricidade: undefined});
                                }
                              }}
                            >
                              Sim
                            </button>
                            <button 
                              type="button" 
                              className={`btn-option ${editMarcacaoFormData.faturaPresenteEletricidade === 'nao' ? 'selected' : ''}`}
                              onClick={() => {
                                setEditMarcacaoFormData({...editMarcacaoFormData, faturaPresenteEletricidade: 'nao'});
                                if (editMarcacaoValidationErrors.faturaPresenteEletricidade) {
                                  setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, faturaPresenteEletricidade: undefined});
                                }
                              }}
                            >
                              Não
                            </button>
                          </div>
                          {editMarcacaoValidationErrors.faturaPresenteEletricidade && (
                            <span className="error-message">{editMarcacaoValidationErrors.faturaPresenteEletricidade}</span>
                          )}
                        </div>
                      </div>
                    )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Step 4: Cliente */}
                  <div className="form-section-inner">
                    <h4>Cliente</h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          Nome da empresa <span className="required">*</span>
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${editMarcacaoValidationErrors.nomeEmpresa ? 'error' : ''}`}
                          value={editMarcacaoFormData.nomeEmpresa || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, nomeEmpresa: e.target.value});
                            if (editMarcacaoValidationErrors.nomeEmpresa) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, nomeEmpresa: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.nomeEmpresa && (
                          <span className="error-message">{editMarcacaoValidationErrors.nomeEmpresa}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Nome do responsável <span className="required">*</span>
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${editMarcacaoValidationErrors.nomeResponsavel ? 'error' : ''}`}
                          value={editMarcacaoFormData.nomeResponsavel || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, nomeResponsavel: e.target.value});
                            if (editMarcacaoValidationErrors.nomeResponsavel) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, nomeResponsavel: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.nomeResponsavel && (
                          <span className="error-message">{editMarcacaoValidationErrors.nomeResponsavel}</span>
                        )}
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">
                          Morada <span className="required">*</span>
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${editMarcacaoValidationErrors.morada ? 'error' : ''}`}
                          value={editMarcacaoFormData.morada || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, morada: e.target.value});
                            if (editMarcacaoValidationErrors.morada) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, morada: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.morada && (
                          <span className="error-message">{editMarcacaoValidationErrors.morada}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Contacto do cliente <span className="required">*</span>
                        </label>
                        <input 
                          type="text" 
                          className={`form-input ${editMarcacaoValidationErrors.contactoCliente ? 'error' : ''}`}
                          value={editMarcacaoFormData.contactoCliente || ''}
                          onChange={(e) => {
                            setEditMarcacaoFormData({...editMarcacaoFormData, contactoCliente: e.target.value});
                            if (editMarcacaoValidationErrors.contactoCliente) {
                              setEditMarcacaoValidationErrors({...editMarcacaoValidationErrors, contactoCliente: undefined});
                            }
                          }}
                          required
                        />
                        {editMarcacaoValidationErrors.contactoCliente && (
                          <span className="error-message">{editMarcacaoValidationErrors.contactoCliente}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Código postal 1</label>
                        <input 
                          type="text" 
                          className="form-input"
                          value={editMarcacaoFormData.codigoPostal1 || ''}
                          onChange={(e) => setEditMarcacaoFormData({...editMarcacaoFormData, codigoPostal1: e.target.value})}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Código postal 2</label>
                        <input 
                          type="text" 
                          className="form-input"
                          value={editMarcacaoFormData.codigoPostal2 || ''}
                          onChange={(e) => setEditMarcacaoFormData({...editMarcacaoFormData, codigoPostal2: e.target.value})}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label className="form-label">Link Google Maps</label>
                        <input 
                          type="url" 
                          className="form-input"
                          value={editMarcacaoFormData.linkGoogleMaps || ''}
                          onChange={(e) => setEditMarcacaoFormData({...editMarcacaoFormData, linkGoogleMaps: e.target.value})}
                          placeholder="https://maps.google.com/..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
            <div className="marcacao-details-compact">
              {/* Header with key info */}
              <div className="event-header-info">
                <div className="event-time-info">
                  <span className="time-badge" title={`Horário: ${formData.horaInicio || selectedMarcacaoEvent.time} - ${formData.horaFim || selectedMarcacaoEvent.duration}`}>
                    Horário: {formData.horaInicio || selectedMarcacaoEvent.time} - {formData.horaFim || selectedMarcacaoEvent.duration}
                  </span>
                  <span className="date-badge" title={`Data: ${formatMarcacaoData(formData.marcacaoData)}`}>
                    Data: {formatMarcacaoData(formData.marcacaoData)}
                  </span>
                </div>
                <div className="event-product-info">
                  {produtos.length > 1 ? (
                    produtos.map((p: string) => {
                      const displayName = productNamesMap[p] || p;
                      return (
                        <span key={p} className={`product-badge product-badge-${p}`} title="Produto">
                          {displayName}
                        </span>
                      );
                    })
                  ) : (
                    <span className={`product-badge product-badge-${produto}`} title="Produto">
                      {produtoDisplay}
                    </span>
                  )}
                  <span className="comercial-badge" title="Comercial / Comerciais Responsáveis">
                    {(() => {
                      const userCount = Array.isArray(userIds) ? userIds.length : 0;
                      const label = userCount > 1 ? 'Comerciais' : 'Comercial';
                      return `${label}: ${comerciais}`;
                    })()}
                  </span>
                </div>
              </div>

              {/* When comercial has altered: "Informação Comercial" (current values) then "Original (CallCenter)". Otherwise: normal Cliente + Detalhes. */}
              {hasComercialAlterations ? (
                <>
                  {/* Informação Comercial - current values (result of editing) */}
                  <div 
                    className="marcacao-info-section marcacao-info-section-collapsible"
                    style={{
                      borderLeft: `4px solid ${productColors.primary}`,
                      backgroundColor: 'rgba(239, 246, 255, 0.5)',
                      borderRadius: '8px',
                      padding: '0 0 0 4px',
                      marginBottom: '1rem'
                    }}
                  >
                    <h4 
                      onClick={() => setIsComercialCurrentCollapsed(!isComercialCurrentCollapsed)}
                      style={{ 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        color: '#334155',
                        fontWeight: 700,
                        fontSize: '1rem'
                      }}
                    >
                      <span>{isComercialCurrentCollapsed ? '▶' : '▼'}</span>
                      📋 Informação Comercial
                    </h4>
                    <p style={{ margin: '0 0 0 1.5rem', fontSize: '0.8rem', color: '#64748b', fontWeight: 400 }}>
                      Valores atuais (resultado da edição pelo comercial)
                    </p>
                    {!isComercialCurrentCollapsed && (
                      <div className="marcacao-details">
                        <div className="marcacao-main">
                          <h5 style={{ margin: '1rem 0 0.5rem 0', color: '#1f2937', fontSize: '0.95rem', fontWeight: 600 }}>👤 Cliente</h5>
                          <div className="marcacao-field">
                            <span className="field-label">Nome da empresa:</span>
                            <span className="field-value">{currentFormData.nomeEmpresa || 'N/A'}</span>
                          </div>
                          <div className="marcacao-field">
                            <span className="field-label">Nome do responsável:</span>
                            <span className="field-value">{currentFormData.nomeResponsavel || 'N/A'}</span>
                          </div>
                          <div className="marcacao-field">
                            <span className="field-label">Morada:</span>
                            <span className="field-value">{currentFormData.morada || selectedMarcacaoEvent.location || 'N/A'}</span>
                          </div>
                          <div className="marcacao-field">
                            <span className="field-label">Contacto:</span>
                            <span className="field-value">{currentFormData.contactoCliente || 'N/A'}</span>
                          </div>
                          {(currentFormData.codigoPostal1 || currentFormData.codigoPostal2) && (
                            <div className="marcacao-field">
                              <span className="field-label">Código postal:</span>
                              <span className="field-value">{currentFormData.codigoPostal1 || ''}-{currentFormData.codigoPostal2 || ''}{currentFormData.localidade ? ` - ${currentFormData.localidade}` : ''}</span>
                            </div>
                          )}
                          {currentFormData.localidade && !currentFormData.codigoPostal1 && !currentFormData.codigoPostal2 && (
                            <div className="marcacao-field">
                              <span className="field-label">Localidade:</span>
                              <span className="field-value">{currentFormData.localidade}</span>
                            </div>
                          )}
                          {currentFormData.linkGoogleMaps && (
                            <div className="marcacao-field">
                              <span className="field-label">Link Google Maps:</span>
                              <span className="field-value">
                                <a href={currentFormData.linkGoogleMaps} target="_blank" rel="noopener noreferrer" className="maps-link" style={{ color: productColors.primary, textDecoration: 'underline' }}>Abrir no Google Maps</a>
                              </span>
                            </div>
                          )}
                          <div className="marcacao-field">
                            <span className="field-label">ID da Marcação:</span>
                            <span className="field-value">{marcacaoId}</span>
                          </div>
                          {currentFormData.marcacaoDescription && (
                            <div className="marcacao-field">
                              <span className="field-label">Nota:</span>
                              <span className="field-value">{currentFormData.marcacaoDescription}</span>
                            </div>
                          )}
                          {produtos.map((prod: string) => {
                            const productName = productNamesMap[prod] || prod;
                            return (
                              <div key={prod} style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h5 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1rem', fontWeight: '600', borderBottom: '2px solid #d1d5db', paddingBottom: '0.5rem' }}>{productName}</h5>
                                {normProd(prod) === 'bairro-solar' && (
                                  <>
                                    <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{currentFormData.jaTemPaineis === 'sim' ? 'Sim' : currentFormData.jaTemPaineis === 'nao' ? 'Não' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Potência 13.80Kw?:</span><span className="field-value">{currentFormData.potencia1035 === 'sim' ? 'Sim' : currentFormData.potencia1035 === 'nao' ? 'Não' : currentFormData.potencia1035 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{currentFormData.trifasico === 'sim' ? 'Sim' : currentFormData.trifasico === 'nao' ? 'Não' : currentFormData.trifasico === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{currentFormData.tipoTelhado || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{currentFormData.faturaPresenteBairro === 'sim' ? 'Sim' : currentFormData.faturaPresenteBairro === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  </>
                                )}
                                {normProd(prod) === 'pap' && (
                                  <>
                                    <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{currentFormData.jaTemPaineisPAP === 'sim' ? 'Sim' : currentFormData.jaTemPaineisPAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Potência 6.9Kw?:</span><span className="field-value">{currentFormData.potencia69 === 'sim' ? 'Sim' : currentFormData.potencia69 === 'nao' ? 'Não' : currentFormData.potencia69 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{(currentFormData.trifasicoPAP || currentFormData.trifasico) === 'sim' ? 'Sim' : (currentFormData.trifasicoPAP || currentFormData.trifasico) === 'nao' ? 'Não' : (currentFormData.trifasicoPAP || currentFormData.trifasico) === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{currentFormData.tipoTelhadoPAP || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{currentFormData.comercializadoraPAP || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Paga mais que 250€?:</span><span className="field-value">{currentFormData.pagaMais250 === 'sim' ? 'Sim' : currentFormData.pagaMais250 === 'nao' ? 'Não' : 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{currentFormData.faturaPresentePAP === 'sim' ? 'Sim' : currentFormData.faturaPresentePAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  </>
                                )}
                                {normProd(prod) === 'gas' && (
                                  <>
                                    <div className="marcacao-field"><span className="field-label">Gás natural e Canalizado?:</span><span className="field-value">{currentFormData.gasNatural === true || currentFormData.gasNatural === 'true' || currentFormData.gasNatural === 'sim' ? 'Sim' : 'Não'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{currentFormData.comercializadoraGas || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{currentFormData.valorFaturaGas || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{currentFormData.faturaPresenteGas === 'sim' ? 'Sim' : currentFormData.faturaPresenteGas === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  </>
                                )}
                                {normProd(prod) === 'eletricidade' && (
                                  <>
                                    <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{currentFormData.comercializadoraEletricidade || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{currentFormData.valorFaturaEletricidade || 'N/A'}</span></div>
                                    <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{currentFormData.faturaPresenteEletricidade === 'sim' ? 'Sim' : currentFormData.faturaPresenteEletricidade === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Original (CallCenter) */}
                  <div 
                    className="marcacao-info-section marcacao-info-section-collapsible"
                    style={{
                      borderLeft: '4px solid #64748b',
                      backgroundColor: 'rgba(248, 250, 252, 0.8)',
                      borderRadius: '8px',
                      padding: '0 0 0 4px',
                      marginBottom: '1rem'
                    }}
                  >
                    <h4 
                      onClick={() => setIsCallCenterInfoCollapsed(!isCallCenterInfoCollapsed)}
                      style={{ 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        color: '#334155',
                        fontWeight: 700,
                        fontSize: '1rem'
                      }}
                    >
                      <span>{isCallCenterInfoCollapsed ? '▶' : '▼'}</span>
                      📋 Original (CallCenter)
                    </h4>
                    <p style={{ margin: '0 0 0 1.5rem', fontSize: '0.8rem', color: '#64748b', fontWeight: 400 }}>
                      Informação original da marcação pelo CallCenter
                    </p>
                    {!isCallCenterInfoCollapsed && (
                    <div className="marcacao-details">
                      <div className="marcacao-main">
                        <h5 style={{ margin: '1rem 0 0.5rem 0', color: '#1f2937', fontSize: '0.95rem', fontWeight: 600 }}>👤 Cliente</h5>
                        <div className="marcacao-field">
                          <span className="field-label">Nome da empresa:</span>
                          <span className="field-value">{originalFormData.nomeEmpresa || 'N/A'}</span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Nome do responsável:</span>
                          <span className="field-value">{originalFormData.nomeResponsavel || 'N/A'}</span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Morada:</span>
                          <span className="field-value">{originalFormData.morada || selectedMarcacaoEvent.location || 'N/A'}</span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Contacto:</span>
                          <span className="field-value">{originalFormData.contactoCliente || 'N/A'}</span>
                        </div>
                        {(originalFormData.codigoPostal1 || originalFormData.codigoPostal2) && (
                          <div className="marcacao-field">
                            <span className="field-label">Código postal:</span>
                            <span className="field-value">{originalFormData.codigoPostal1 || ''}-{originalFormData.codigoPostal2 || ''}{originalFormData.localidade ? ` - ${originalFormData.localidade}` : ''}</span>
                          </div>
                        )}
                        {originalFormData.localidade && !originalFormData.codigoPostal1 && !originalFormData.codigoPostal2 && (
                          <div className="marcacao-field">
                            <span className="field-label">Localidade:</span>
                            <span className="field-value">{originalFormData.localidade}</span>
                          </div>
                        )}
                        {originalFormData.linkGoogleMaps && (
                          <div className="marcacao-field">
                            <span className="field-label">Link Google Maps:</span>
                            <span className="field-value">
                              <a href={originalFormData.linkGoogleMaps} target="_blank" rel="noopener noreferrer" className="maps-link" style={{ color: productColors.primary, textDecoration: 'underline' }}>Abrir no Google Maps</a>
                            </span>
                          </div>
                        )}
                        <div className="marcacao-field">
                          <span className="field-label">ID da Marcação:</span>
                          <span className="field-value">{marcacaoId}</span>
                        </div>
                        {originalFormData.marcacaoDescription && (
                          <div className="marcacao-field">
                            <span className="field-label">Nota:</span>
                            <span className="field-value">{originalFormData.marcacaoDescription}</span>
                          </div>
                        )}
                        {produtos.map((prod: string) => {
                          const productName = productNamesMap[prod] || prod;
                          return (
                            <div key={prod} style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <h5 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1rem', fontWeight: '600', borderBottom: '2px solid #d1d5db', paddingBottom: '0.5rem' }}>{productName}</h5>
                              {normProd(prod) === 'bairro-solar' && (
                                <>
                                  <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{originalFormData.jaTemPaineis === 'sim' ? 'Sim' : originalFormData.jaTemPaineis === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Potência 13.80Kw?:</span><span className="field-value">{originalFormData.potencia1035 === 'sim' ? 'Sim' : originalFormData.potencia1035 === 'nao' ? 'Não' : originalFormData.potencia1035 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{originalFormData.trifasico === 'sim' ? 'Sim' : originalFormData.trifasico === 'nao' ? 'Não' : originalFormData.trifasico === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{originalFormData.tipoTelhado || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{originalFormData.faturaPresenteBairro === 'sim' ? 'Sim' : originalFormData.faturaPresenteBairro === 'nao' ? 'Não' : 'N/A'}</span></div>
                                </>
                              )}
                              {normProd(prod) === 'pap' && (
                                <>
                                  <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{originalFormData.jaTemPaineisPAP === 'sim' ? 'Sim' : originalFormData.jaTemPaineisPAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Potência 6.9Kw?:</span><span className="field-value">{originalFormData.potencia69 === 'sim' ? 'Sim' : originalFormData.potencia69 === 'nao' ? 'Não' : originalFormData.potencia69 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{(originalFormData.trifasicoPAP || originalFormData.trifasico) === 'sim' ? 'Sim' : (originalFormData.trifasicoPAP || originalFormData.trifasico) === 'nao' ? 'Não' : (originalFormData.trifasicoPAP || originalFormData.trifasico) === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{originalFormData.tipoTelhadoPAP || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{originalFormData.comercializadoraPAP || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Paga mais que 250€?:</span><span className="field-value">{originalFormData.pagaMais250 === 'sim' ? 'Sim' : originalFormData.pagaMais250 === 'nao' ? 'Não' : 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{originalFormData.faturaPresentePAP === 'sim' ? 'Sim' : originalFormData.faturaPresentePAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                                </>
                              )}
                              {normProd(prod) === 'gas' && (
                                <>
                                  <div className="marcacao-field"><span className="field-label">Gás natural e Canalizado?:</span><span className="field-value">{originalFormData.gasNatural === true || originalFormData.gasNatural === 'true' || originalFormData.gasNatural === 'sim' ? 'Sim' : 'Não'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{originalFormData.comercializadoraGas || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{originalFormData.valorFaturaGas || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{originalFormData.faturaPresenteGas === 'sim' ? 'Sim' : originalFormData.faturaPresenteGas === 'nao' ? 'Não' : 'N/A'}</span></div>
                                </>
                              )}
                              {normProd(prod) === 'eletricidade' && (
                                <>
                                  <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{originalFormData.comercializadoraEletricidade || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{originalFormData.valorFaturaEletricidade || 'N/A'}</span></div>
                                  <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{originalFormData.faturaPresenteEletricidade === 'sim' ? 'Sim' : originalFormData.faturaPresenteEletricidade === 'nao' ? 'Não' : 'N/A'}</span></div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                </>
              ) : produtos.length > 0 ? (
                <div className="marcacao-info-section" style={{ marginBottom: '1rem' }}>
                  <div className="marcacao-details">
                    <div className="marcacao-main">
                      <h5 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '0.95rem', fontWeight: 600 }}>👤 Cliente</h5>
                      <div className="marcacao-field">
                        <span className="field-label">Nome da empresa:</span>
                        <span className="field-value">{displayFormData.nomeEmpresa || 'N/A'}</span>
                      </div>
                      <div className="marcacao-field">
                        <span className="field-label">Nome do responsável:</span>
                        <span className="field-value">{displayFormData.nomeResponsavel || 'N/A'}</span>
                      </div>
                      <div className="marcacao-field">
                        <span className="field-label">Morada:</span>
                        <span className="field-value">{displayFormData.morada || selectedMarcacaoEvent.location || 'N/A'}</span>
                      </div>
                      <div className="marcacao-field">
                        <span className="field-label">Contacto:</span>
                        <span className="field-value">{displayFormData.contactoCliente || 'N/A'}</span>
                      </div>
                      {(displayFormData.codigoPostal1 || displayFormData.codigoPostal2) && (
                        <div className="marcacao-field">
                          <span className="field-label">Código postal:</span>
                          <span className="field-value">{displayFormData.codigoPostal1 || ''}-{displayFormData.codigoPostal2 || ''}{displayFormData.localidade ? ` - ${displayFormData.localidade}` : ''}</span>
                        </div>
                      )}
                      {displayFormData.localidade && !displayFormData.codigoPostal1 && !displayFormData.codigoPostal2 && (
                        <div className="marcacao-field">
                          <span className="field-label">Localidade:</span>
                          <span className="field-value">{displayFormData.localidade}</span>
                        </div>
                      )}
                      {displayFormData.linkGoogleMaps && (
                        <div className="marcacao-field">
                          <span className="field-label">Link Google Maps:</span>
                          <span className="field-value">
                            <a href={displayFormData.linkGoogleMaps} target="_blank" rel="noopener noreferrer" className="maps-link" style={{ color: productColors.primary, textDecoration: 'underline' }}>Abrir no Google Maps</a>
                          </span>
                        </div>
                      )}
                      <h5 style={{ margin: '1rem 0 0.5rem 0', color: '#1f2937', fontSize: '0.95rem', fontWeight: 600 }}>📋 Detalhes do produto</h5>
                      <div className="marcacao-field">
                        <span className="field-label">ID da Marcação:</span>
                        <span className="field-value">{marcacaoId}</span>
                      </div>
                      {displayFormData.marcacaoDescription && (
                        <div className="marcacao-field">
                          <span className="field-label">Nota:</span>
                          <span className="field-value">{displayFormData.marcacaoDescription}</span>
                        </div>
                      )}
                      {produtos.map((prod: string) => {
                        const productName = productNamesMap[prod] || prod;
                        return (
                          <div key={prod} style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <h5 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1rem', fontWeight: '600', borderBottom: '2px solid #d1d5db', paddingBottom: '0.5rem' }}>{productName}</h5>
                            {normProd(prod) === 'bairro-solar' && (
                              <>
                                <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{displayFormData.jaTemPaineis === 'sim' ? 'Sim' : displayFormData.jaTemPaineis === 'nao' ? 'Não' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Potência 13.80Kw?:</span><span className="field-value">{displayFormData.potencia1035 === 'sim' ? 'Sim' : displayFormData.potencia1035 === 'nao' ? 'Não' : displayFormData.potencia1035 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{displayFormData.trifasico === 'sim' ? 'Sim' : displayFormData.trifasico === 'nao' ? 'Não' : displayFormData.trifasico === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{displayFormData.tipoTelhado || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{displayFormData.faturaPresenteBairro === 'sim' ? 'Sim' : displayFormData.faturaPresenteBairro === 'nao' ? 'Não' : 'N/A'}</span></div>
                              </>
                            )}
                            {normProd(prod) === 'pap' && (
                              <>
                                <div className="marcacao-field"><span className="field-label">Já tem Paineis?:</span><span className="field-value">{displayFormData.jaTemPaineisPAP === 'sim' ? 'Sim' : displayFormData.jaTemPaineisPAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Potência 6.9Kw?:</span><span className="field-value">{displayFormData.potencia69 === 'sim' ? 'Sim' : displayFormData.potencia69 === 'nao' ? 'Não' : displayFormData.potencia69 === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Trifásico?:</span><span className="field-value">{(displayFormData.trifasicoPAP || displayFormData.trifasico) === 'sim' ? 'Sim' : (displayFormData.trifasicoPAP || displayFormData.trifasico) === 'nao' ? 'Não' : (displayFormData.trifasicoPAP || displayFormData.trifasico) === 'nao_sabe' ? 'Não Sabe' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Tipo de Telhado:</span><span className="field-value">{displayFormData.tipoTelhadoPAP || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{displayFormData.comercializadoraPAP || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Paga mais que 250€?:</span><span className="field-value">{displayFormData.pagaMais250 === 'sim' ? 'Sim' : displayFormData.pagaMais250 === 'nao' ? 'Não' : 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Fatura presente no dia da visita?:</span><span className="field-value">{displayFormData.faturaPresentePAP === 'sim' ? 'Sim' : displayFormData.faturaPresentePAP === 'nao' ? 'Não' : 'N/A'}</span></div>
                              </>
                            )}
                            {normProd(prod) === 'gas' && (
                              <>
                                <div className="marcacao-field"><span className="field-label">Gás natural e Canalizado?:</span><span className="field-value">{displayFormData.gasNatural === true || displayFormData.gasNatural === 'true' || displayFormData.gasNatural === 'sim' ? 'Sim' : 'Não'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{displayFormData.comercializadoraGas || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{displayFormData.valorFaturaGas || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{displayFormData.faturaPresenteGas === 'sim' ? 'Sim' : displayFormData.faturaPresenteGas === 'nao' ? 'Não' : 'N/A'}</span></div>
                              </>
                            )}
                            {normProd(prod) === 'eletricidade' && (
                              <>
                                <div className="marcacao-field"><span className="field-label">Comercializadora Atual:</span><span className="field-value">{displayFormData.comercializadoraEletricidade || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Valor da Última Fatura:</span><span className="field-value">{displayFormData.valorFaturaEletricidade || 'N/A'}</span></div>
                                <div className="marcacao-field"><span className="field-label">Fatura Presente no Dia da Reunião?:</span><span className="field-value">{displayFormData.faturaPresenteEletricidade === 'sim' ? 'Sim' : displayFormData.faturaPresenteEletricidade === 'nao' ? 'Não' : 'N/A'}</span></div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Informação Comercial - só aparece se existir conteúdo guardado */}
              {formData.informacoesComerciais && (
                (formData.informacoesComerciais.nota && String(formData.informacoesComerciais.nota).trim() !== '') ||
                Object.keys(formData.informacoesComerciais).some(k => k !== 'nota' && formData.informacoesComerciais[k])
              ) ? (
                <div 
                  className="marcacao-info-section"
                  style={{
                    borderLeft: `4px solid ${productColors.primary}`,
                    backgroundColor: 'rgba(239, 246, 255, 0.5)',
                    borderRadius: '8px',
                    padding: '0 0 0 4px',
                    marginBottom: '1rem'
                  }}
                >
                  <h4 style={{ color: '#334155', fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>
                    📋 Informação Comercial
                  </h4>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#64748b', fontWeight: 400 }}>
                    Informação adicionada pelo comercial, separada do original CallCenter
                  </p>
                  <div className="marcacao-details">
                    <div className="marcacao-main">
                      <div className="marcacao-field" style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <span className="field-label" style={{ fontWeight: 600, color: '#475569' }}>Nota:</span>
                        <div style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap', color: '#334155' }}>
                          {formData.informacoesComerciais.nota?.trim() || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Motivo de Reagendamento (Collapsible) - Only show if rescheduled */}
              {isRescheduled && (
                <div 
                  className="marcacao-info-section marcacao-info-section-collapsible"
                  style={{
                    borderLeft: `4px solid ${productColors.primary}`
                  }}
                >
                  <h4 
                    onClick={() => setIsRescheduleReasonCollapsed(!isRescheduleReasonCollapsed)}
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      color: '#374151'
                    }}
                  >
                    <span>{isRescheduleReasonCollapsed ? '▶' : '▼'}</span>
                    📅 Motivo de Reagendamento
                  </h4>
                  {!isRescheduleReasonCollapsed && (
                    <div className="marcacao-details">
                      <div className="marcacao-main">
                        {/* Always show old and new date/time */}
                        <div className="marcacao-field">
                          <span className="field-label">Data Anterior:</span>
                          <span className="field-value">
                            {(() => {
                              const oldDateStr = formData.rescheduleOldDate || formData.oldDate;
                              if (!oldDateStr) return 'N/A';
                              // Format from YYYY-MM-DD or DD/MM/YYYY to DD/MM/YYYY
                              if (oldDateStr.includes('-')) {
                                const parts = oldDateStr.split('-');
                                if (parts.length === 3 && parts[0].length === 4) {
                                  // YYYY-MM-DD format
                                  return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                }
                              }
                              if (oldDateStr.includes('/')) {
                                const parts = oldDateStr.split('/');
                                if (parts.length === 3 && parts[2].length === 4) {
                                  // Already DD/MM/YYYY format
                                  return oldDateStr;
                                }
                              }
                              return formatMarcacaoData(oldDateStr);
                            })()}
                          </span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Hora Anterior:</span>
                          <span className="field-value">
                            {formData.rescheduleOldHoraInicio || formData.oldHoraInicio || 'N/A'}
                            {(formData.rescheduleOldHoraFim || formData.oldHoraFim) && 
                              ` - ${formData.rescheduleOldHoraFim || formData.oldHoraFim}`}
                          </span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Nova Data:</span>
                          <span className="field-value">{formatMarcacaoData(formData.marcacaoData)}</span>
                        </div>
                        <div className="marcacao-field">
                          <span className="field-label">Hora Nova:</span>
                          <span className="field-value">
                            {formData.horaInicio || ''}
                            {formData.horaFim && ` - ${formData.horaFim}`}
                          </span>
                        </div>
                        {/* Show reason if exists and is not empty */}
                        {formData.rescheduleReason && formData.rescheduleReason.trim() && (
                          <div className="marcacao-field">
                            <span className="field-label">Motivo da Remarcação:</span>
                            <span className="field-value">
                              {formData.rescheduleReason}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Not Ok CallCenter Feedback (Collapsible) - Only show if Not Ok CallCenter and has feedback */}
              {isNotOkCallCenter && formData.feedback?.callCenterFeedback && (
                <div 
                  className="marcacao-info-section marcacao-info-section-collapsible"
                  style={{
                    borderLeft: `4px solid ${productColors.primary}`
                  }}
                >
                  <h4 
                    onClick={() => setIsNotOkCallCenterFeedbackCollapsed(!isNotOkCallCenterFeedbackCollapsed)}
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      color: '#374151'
                    }}
                  >
                    <span>{isNotOkCallCenterFeedbackCollapsed ? '▶' : '▼'}</span>
                    📞 Feedback Not Ok CallCenter
                  </h4>
                  {!isNotOkCallCenterFeedbackCollapsed && (
                    <div className="marcacao-details">
                      <div className="marcacao-main">
                        {(() => {
                          const callCenterFeedback = formData.feedback.callCenterFeedback;
                          const feedbackLabels: Record<string, string> = {
                            'data': 'Data',
                            'horario': 'Horário',
                            'contacto': 'Contacto',
                            'linkGoogleMaps': 'Link Google Maps',
                            'informacoesCallCenter': 'Informações CallCenter',
                            'outro': 'Outro'
                          };
                          
                          const getFeedbackText = (optionValue: any): string => {
                            if (typeof optionValue === 'string') {
                              return optionValue || '';
                            }
                            if (typeof optionValue === 'object' && optionValue?.text) {
                              return optionValue.text || '';
                            }
                            return '';
                          };
                          
                          const feedbackEntries = Object.entries(callCenterFeedback).filter(([key, value]) => {
                            const text = getFeedbackText(value);
                            return text && text.trim() !== '';
                          });
                          
                          if (feedbackEntries.length === 0) {
                            return (
                              <div className="marcacao-field">
                                <span className="field-value">Nenhum feedback disponível</span>
                              </div>
                            );
                          }
                          
                          return feedbackEntries.map(([key, value]) => {
                            const text = getFeedbackText(value);
                            const label = feedbackLabels[key] || key;
                            return (
                              <div key={key} className="marcacao-field">
                                <span className="field-label">{label}:</span>
                                <span className="field-value">{text}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
          
          <div className="event-actions">
            {isEditingMarcacao && (
              <>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveMarcacao}
                  disabled={savingMarcacao}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: savingMarcacao ? '#ccc' : productColors.primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: savingMarcacao ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s',
                    marginRight: '10px'
                  }}
                >
                  {savingMarcacao ? 'Salvando...' : 'Salvar Alterações'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsEditingMarcacao(false);
                    setEditMarcacaoValidationErrors({});
                    setEditMarcacaoNotification(null);
                  }}
                  disabled={savingMarcacao}
                >
                  Cancelar
                </button>
              </>
            )}
            {!isEditingMarcacao && (
              <>
                {/* Editar: visible for every comercial user */}
                <button 
                  className="btn btn-primary" 
                  onClick={() => setIsEditingMarcacao(true)}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: productColors.primary, 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s',
                    marginRight: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (e.currentTarget) e.currentTarget.style.backgroundColor = productColors.secondary;
                  }}
                  onMouseLeave={(e) => {
                    if (e.currentTarget) e.currentTarget.style.backgroundColor = productColors.primary;
                  }}
                >
                  Editar
                </button>
                {/* Ver Propostas: open modal with Solicitar Propostas associated to this marcação */}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPropostasMarcacaoModal(true)}
                  style={{
                    marginRight: '10px',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    border: `1px solid ${productColors.primary}`,
                    color: productColors.primary,
                    backgroundColor: 'transparent'
                  }}
                >
                  Ver Propostas
                </button>
                {/* Fecho: only for Marcação (not when viewing a Fecho event) */}
                {!isFechoEvent && (
                <button 
                  type="button"
                  className="btn btn-primary" 
                  onClick={() => {
                    if (!selectedMarcacaoEvent) return;
                    const fd = selectedMarcacaoEvent.formData || {};
                    const norm = (p: string) => (p || '').toLowerCase().replace(/\s+/g, '-').replace(/á/g, 'a').replace(/ã/g, 'a');
                    const produtos = fd.produtos && Array.isArray(fd.produtos) ? fd.produtos : (fd.produto ? [norm(fd.produto) || fd.produto] : []);
                    setSelectedMarcacaoForFecho(selectedMarcacaoEvent);
                    setFechoFormData({
                      marcacaoData: '',
                      horaInicio: fd.horaInicio || selectedMarcacaoEvent.time || '',
                      horaFim: fd.horaFim || selectedMarcacaoEvent.duration || '',
                      marcacaoDescription: fd.marcacaoDescription || '',
                      produto: fd.produto || selectedMarcacaoEvent.product || '',
                      produtos: produtos.length ? produtos : (fd.produto ? [fd.produto] : []),
                      nomeEmpresa: fd.nomeEmpresa || '',
                      nomeResponsavel: fd.nomeResponsavel || '',
                      morada: fd.morada || selectedMarcacaoEvent.location || '',
                      contactoCliente: fd.contactoCliente || '',
                      codigoPostal1: fd.codigoPostal1 || '',
                      codigoPostal2: fd.codigoPostal2 || '',
                      localidade: fd.localidade || '',
                      linkGoogleMaps: fd.linkGoogleMaps || '',
                      marcacaoUsers: fd.marcacaoUsers || [selectedUser?.id].filter(Boolean),
                      ...fd
                    });
                    setCurrentFechoStep(1);
                    setFechoValidationErrors({});
                    setFechoNotification(null);
                    setShowMarcacaoEventDetailModal(false);
                    setSelectedMarcacaoEvent(null);
                    setShowFechoModal(true);
                  }}
                  style={{ 
                    marginRight: '10px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  Fecho
                </button>
                )}
                {canEditMarcacaoEvents() && (
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setShowMarcacaoDeleteConfirm(true)}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                      marginRight: '10px'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) e.currentTarget.style.backgroundColor = '#ef4444';
                    }}
                  >
                    Eliminar
                  </button>
                )}
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowMarcacaoEventDetailModal(false); 
                    setSelectedMarcacaoEvent(null);
                    setIsCallCenterInfoCollapsed(false);
                    setIsRescheduleReasonCollapsed(false);
                    setIsNotOkCallCenterFeedbackCollapsed(false);
                    if (modalOnly && onCloseModal) onCloseModal();
                  }}
                >
                  Fechar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPropostasMarcacaoModal = () => {
    if (!showPropostasMarcacaoModal || !selectedMarcacaoEvent) return null;
    const formData = selectedMarcacaoEvent.formData || {};
    const proposalData = formData.proposalData;
    const propostaIds = formData.propostaId || [];
    const propostaIdsArray = Array.isArray(propostaIds) ? propostaIds : propostaIds ? [propostaIds] : [];
    const proposalsList: any[] = [];
    if (proposalData) {
      if (Array.isArray(proposalData)) {
        proposalsList.push(...proposalData);
      } else if (typeof proposalData === 'object' && proposalData !== null) {
        proposalsList.push(proposalData);
      }
    }
    const estadoProposta = (formData.estadoProposta || '').toString().toLowerCase();
    const claimedRaw = formData.claimedProposals;
    const claimedProposals =
      typeof claimedRaw === 'object' && claimedRaw !== null ? (claimedRaw as Record<string, unknown>) : undefined;

    // Build request objects for RequestCard (same shape as Minhas Propostas / board API)
    const requests = proposalsList.map((proposal: any, idx: number) => {
      const propostaId = propostaIdsArray[idx] || propostaIdsArray[0] || `PRP-${selectedMarcacaoEvent!.id}-${idx}`;
      const claim = getClaimForPropostaId(claimedProposals, propostaId);
      const scdRaw = claim?.service_completion_data;
      const scd = Array.isArray(scdRaw) ? scdRaw : [];
      const servicosList = Array.isArray(proposal?.servicos) ? proposal.servicos : [];
      const rowTerminal = (row: any) =>
        row &&
        (row.completed_at ||
          row.cancelled_at ||
          String(row.status || '').toLowerCase() === 'completed' ||
          String(row.status || '').toLowerCase() === 'cancelled');
      const allServicesAnswered =
        servicosList.length > 0 &&
        servicosList.every((s: any) => {
          const iid = s?.instanceId;
          if (!iid) return false;
          return scd.some((row: any) => row?.instanceId === iid && rowTerminal(row));
        });

      let kanbanSection: string = 'available';
      let boardStatus: string = 'available';
      if (claim?.status === 'cancelled' || estadoProposta === 'cancelado') {
        kanbanSection = 'completed';
        boardStatus = 'cancelled';
      } else if (
        claim?.status === 'completed' ||
        estadoProposta === 'concluido' ||
        estadoProposta === 'entregue' ||
        allServicesAnswered
      ) {
        kanbanSection = 'completed';
        boardStatus = 'completed';
      } else if (claim?.backoffice_user_id) {
        boardStatus = 'in_progress';
        kanbanSection = 'in_progress';
      }

      return {
        id: propostaId,
        form_answers: proposal,
        form_data: proposal,
        proposal_request: { form_data: proposal, form_answers: proposal },
        kanbanSection,
        boardStatus,
        proposal_claim: claim,
        completed_at: claim?.completed_at ?? null,
        completion_notes: claim?.completion_notes ?? null,
        backoffice_user: claim?.backoffice_user ?? null,
        cancelled_at: claim?.cancelled_at ?? null,
        cancelled_by_username: claim?.cancelled_by_username ?? null,
        cancellation_feedback: claim?.cancellation_feedback ?? null,
        submitted_at: proposal.submittedAt || proposal.submitted_at || proposal.created_at || proposal.updated_at || new Date().toISOString()
      };
    });
    return (
      <div
        className="event-modal propostas-marcacao-modal minhas-propostas-modal"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100002,
          background: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(2px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          overflowY: 'auto'
        }}
        onClick={() => setShowPropostasMarcacaoModal(false)}
      >
        <div
          className="event-modal-content minhas-propostas-modal-content propostas-marcacao-modal-content"
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '0',
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            zIndex: 100003
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header minhas-propostas-modal-header" style={{
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '20px 20px 0 0',
            background: '#4b5563'
          }}>
            <h2 className="minhas-propostas-title" style={{
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: '700',
              margin: 0,
              padding: 0,
              lineHeight: '1.2'
            }}>Propostas desta Marcação</h2>
            <button
              type="button"
              className="close-btn"
              onClick={() => setShowPropostasMarcacaoModal(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ×
            </button>
          </div>
          <div className="modal-body propostas-marcacao-modal-body" style={{
            padding: '1.5rem',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {requests.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '15px', textAlign: 'center', padding: '32px 0' }}>
                Nenhuma proposta solicitada para esta marcação.
              </p>
            ) : (
              <div className="contracts-documents-container propostas-marcacao-cards" style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden'
              }}>
                {requests.map((request) => (
                  <div key={request.id} id={`proposal-${request.id}`}>
                    <RequestCard request={request} scrollableBody />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0
          }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowPropostasMarcacaoModal(false)}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMarcacaoDeleteConfirmModal = () => {
    if (!showMarcacaoDeleteConfirm || !selectedMarcacaoEvent) return null;
    
    const formData = selectedMarcacaoEvent.formData || {};
    const marcacaoId = formData.marcacaoId || selectedMarcacaoEvent.id?.replace('marcacao-', '') || 'N/A';
    const produto = formData.produto || selectedMarcacaoEvent.product || 'N/A';
    const produtoDisplay = produto === 'bairro-solar' ? 'Bairro Solar' : 
                           produto === 'pap' ? 'PAP (Pay As Produced)' : 
                           produto === 'gas' ? 'Gás' : 
                           produto === 'eletricidade' ? 'Eletricidade' : 'N/A';
    
    // Get marcação date
    let marcacaoDate: Date | null = null;
    if (selectedMarcacaoEvent.date) {
      marcacaoDate = selectedMarcacaoEvent.date;
    } else if (formData.marcacaoData) {
      const dateParts = formData.marcacaoData.split('/');
      if (dateParts.length === 3) {
        marcacaoDate = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
      } else {
        marcacaoDate = new Date(formData.marcacaoData);
      }
    }
    
    // Get comerciais names
    const userIds = formData.marcacaoUsers || [];
    const comerciais = userIds.map((userId: string | number) => 
      comercialUsers.find(u => u.id === String(userId))?.name
    ).filter(Boolean).join(', ') || 'N/A';
    
    // Get company name
    const nomeEmpresa = formData.nomeEmpresa || selectedMarcacaoEvent.title?.split(' - ')[1] || 'N/A';
    
    // Get product colors for dynamic styling
    const getProductColors = (prod: string) => {
      switch (prod) {
        case 'bairro-solar':
          return { primary: '#2563eb', secondary: '#1d4ed8' };
        case 'pap':
          return { primary: '#2563eb', secondary: '#1d4ed8' };
        case 'gas':
          return { primary: '#ea580c', secondary: '#c2410c' };
        case 'eletricidade':
          return { primary: '#ea580c', secondary: '#c2410c' };
        default:
          return { primary: '#6366f1', secondary: '#4f46e5' };
      }
    };
    const productColors = getProductColors(produto);
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000,
          backdropFilter: 'blur(2px)'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !deletingMarcacao) {
            setShowMarcacaoDeleteConfirm(false);
          }
        }}
      >
        <div 
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            zIndex: 100001,
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: productColors.primary,
            color: 'white'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              Confirmar Eliminação
            </h3>
            <button 
              onClick={() => !deletingMarcacao && setShowMarcacaoDeleteConfirm(false)}
              disabled={deletingMarcacao}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: deletingMarcacao ? 'not-allowed' : 'pointer',
                color: 'white',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                lineHeight: '1',
                opacity: deletingMarcacao ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingMarcacao) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ padding: '24px' }}>
            <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151', lineHeight: '1.5' }}>
              Tem a certeza que deseja eliminar esta <strong>marcação</strong>?
            </p>
            
            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>ID da Marcação:</strong> {marcacaoId}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Produto:</strong> {produtoDisplay}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Empresa:</strong> {nomeEmpresa}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Data:</strong> {marcacaoDate ? marcacaoDate.toLocaleDateString('pt-PT') : formData.marcacaoData || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Hora:</strong> {formData.horaInicio || selectedMarcacaoEvent.time || 'N/A'} - {formData.horaFim || selectedMarcacaoEvent.duration || 'N/A'}
              </div>
              <div>
                <strong>Comerciais:</strong> {comerciais}
              </div>
            </div>
          </div>
          
          <div style={{
            padding: '24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button 
              onClick={() => setShowMarcacaoDeleteConfirm(false)}
              disabled={deletingMarcacao}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#374151',
                cursor: deletingMarcacao ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: deletingMarcacao ? 0.5 : 1,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingMarcacao) e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Cancelar
            </button>
            <button 
              onClick={handleDeleteMarcacao}
              disabled={deletingMarcacao}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: deletingMarcacao ? '#9ca3af' : '#ef4444',
                color: '#ffffff',
                cursor: deletingMarcacao ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget && !deletingMarcacao) e.currentTarget.style.backgroundColor = '#dc2626';
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget) e.currentTarget.style.backgroundColor = deletingMarcacao ? '#9ca3af' : '#ef4444';
              }}
            >
              {deletingMarcacao ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFechoEventDetailModal = () => {
    if (!showFechoEventDetailModal || !selectedFechoEvent) return null;
    const formData = selectedFechoEvent.formData || {};
    const produto = formData.produto || selectedFechoEvent.product || 'N/A';
    const produtoDisplay = produto === 'bairro-solar' ? 'Bairro Solar' : produto === 'pap' ? 'PAP' : produto === 'gas' ? 'Gás' : produto === 'eletricidade' ? 'Eletricidade' : produto;
    const fechoColors = { primary: '#059669', secondary: '#047857', light: '#10b981' };
    return (
      <div className="event-modal marcacao-detail-modal fecho-detail-modal">
        <div className="event-modal-content">
          <div className="event-modal-header marcacao-detail-header" style={{ background: `linear-gradient(135deg, ${fechoColors.primary} 0%, ${fechoColors.secondary} 100%)` }}>
            <h3>{isEditingFecho ? 'Editar Fecho' : selectedFechoEvent.title || '[Fecho]'}</h3>
            <button type="button" className="close-btn" onClick={() => { setShowFechoEventDetailModal(false); setSelectedFechoEvent(null); setIsEditingFecho(false); setEditFechoNotification(null); }}>×</button>
          </div>
          <div className="event-modal-body" style={{ position: 'relative' }}>
            {editFechoNotification && (
              <div className="rota-notification" style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, padding: '12px 24px', borderRadius: 8, backgroundColor: editFechoNotification.type === 'success' ? '#10b981' : '#ef4444', color: 'white', fontWeight: 600, fontSize: 14 }}>{editFechoNotification.message}</div>
            )}
            {isEditingFecho ? (
              <form className="marcacao-edit-form" onSubmit={(e) => { e.preventDefault(); handleSaveFecho(); }}>
                <div className="form-section active">
                  <h4>Agendamento</h4>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Data *</label><input type="date" className="form-input" value={editFechoFormData.marcacaoData || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, marcacaoData: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Hora Início *</label><input type="time" className="form-input" value={editFechoFormData.horaInicio || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, horaInicio: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Hora Fim *</label><input type="time" className="form-input" value={editFechoFormData.horaFim || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, horaFim: e.target.value })} /></div>
                  </div>
                  <h4>Produto</h4>
                  <div className="form-group"><select className="form-input" value={editFechoFormData.produto || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, produto: e.target.value })}><option value="bairro-solar">Bairro Solar</option><option value="pap">PAP</option><option value="gas">Gás</option><option value="eletricidade">Eletricidade</option></select></div>
                  <h4>Cliente</h4>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Nome Empresa *</label><input type="text" className="form-input" value={editFechoFormData.nomeEmpresa || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, nomeEmpresa: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Responsável *</label><input type="text" className="form-input" value={editFechoFormData.nomeResponsavel || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, nomeResponsavel: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Contacto *</label><input type="tel" className="form-input" value={editFechoFormData.contactoCliente || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, contactoCliente: e.target.value })} /></div>
                    <div className="form-group full-width"><label className="form-label">Morada *</label><input type="text" className="form-input" value={editFechoFormData.morada || ''} onChange={(e) => setEditFechoFormData({ ...editFechoFormData, morada: e.target.value })} /></div>
                  </div>
                </div>
                <div className="event-modal-footer" style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-primary" onClick={handleSaveFecho} disabled={savingMarcacao}>{savingMarcacao ? 'A guardar...' : 'Salvar'}</button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setIsEditingFecho(false); setEditFechoNotification(null); }}>Cancelar</button>
                </div>
              </form>
            ) : (
              <>
                <div className="marcacao-details-compact">
                  <div className="event-header-info">
                    <span className="time-badge">Horário: {formData.horaInicio || selectedFechoEvent.time} - {formData.horaFim || selectedFechoEvent.duration}</span>
                    <span className="date-badge">Data: {selectedFechoEvent.date ? selectedFechoEvent.date.toLocaleDateString('pt-PT') : formData.marcacaoData || 'N/A'}</span>
                  </div>
                  <div className="event-product-info"><span className="product-badge">Produto: {produtoDisplay}</span></div>
                  <h4>Cliente</h4>
                  <div className="client-details">
                    <strong>{formData.nomeEmpresa || 'N/A'}</strong>
                    <div>Responsável: {formData.nomeResponsavel || 'N/A'}</div>
                    <div>Contacto: {formData.contactoCliente || 'N/A'}</div>
                    <div>Morada: {formData.morada || selectedFechoEvent.location || 'N/A'}</div>
                  </div>
                  {formData.marcacaoId && <div style={{ marginTop: 8 }}><strong>ID:</strong> {formData.marcacaoId}</div>}
                </div>
                <div className="event-modal-footer" style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-primary" onClick={() => setIsEditingFecho(true)} style={{ backgroundColor: fechoColors.primary, color: 'white' }}>Editar</button>
                  <button type="button" className="btn btn-danger" onClick={() => setShowFechoDeleteConfirm(true)} style={{ backgroundColor: '#ef4444', color: 'white' }}>Eliminar</button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowFechoEventDetailModal(false); setSelectedFechoEvent(null); }}>Fechar</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFechoDeleteConfirmModal = () => {
    if (!showFechoDeleteConfirm || !selectedFechoEvent) return null;
    const formData = selectedFechoEvent.formData || {};
    const marcacaoId = formData.marcacaoId || 'N/A';
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }} onClick={() => !deletingFecho && setShowFechoDeleteConfirm(false)}>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, maxWidth: 440, width: '90%', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: 16 }}>Eliminar Fecho</h3>
          <p style={{ marginBottom: 16 }}>Tem a certeza que deseja eliminar o Fecho <strong>{marcacaoId}</strong>? Esta ação não pode ser revertida.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button className="btn btn-secondary" onClick={() => setShowFechoDeleteConfirm(false)} disabled={deletingFecho}>Cancelar</button>
            <button className="btn btn-danger" style={{ backgroundColor: '#ef4444', color: 'white' }} onClick={handleDeleteFecho} disabled={deletingFecho}>{deletingFecho ? 'Eliminando...' : 'Eliminar'}</button>
          </div>
        </div>
      </div>
    );
  };

  const renderEventModal = () => {
    if (!showEventModal || !selectedEvent) return null;
    
    const formData = selectedEvent.formData;
    const isMarcacao = selectedEvent.type === 'marcacao';
    const isBloqueio = selectedEvent.type === 'bloqueio';
    
    return (
      <div className="event-modal">
        <div className="event-modal-content">
          <div className="event-modal-header">
            <h3>{selectedEvent.title}</h3>
            <button type="button" className="close-btn" onClick={closeEventModal}>×</button>
          </div>
          <div className="event-modal-body">
            {isMarcacao && formData ? (
              <div className="marcacao-details-compact">
                {/* Header with key info */}
                <div className="event-header-info">
                  <div className="event-time-info">
                    <span className="time-badge" title="Horário: 09:00 - 10:30">Horário: {formData.horaInicio} - {formData.horaFim}</span>
                    <span className="date-badge" title="Data: 01/10/2025">Data: {formData.marcacaoData}</span>
                  </div>
                  <div className="event-product-info">
                    {(() => {
                      // Extract produtos array (for multi-product marcações)
                      const produtosArray = formData.produtos || (formData.produto ? [formData.produto] : []);
                      const produtos = Array.isArray(produtosArray) ? produtosArray : [produtosArray].filter(Boolean);
                      
                      // Map produto values to display names
                      const produtoTitles: Record<string, string> = {
                        'bairro-solar': 'Bairro Solar',
                        'pap': 'PAP',
                        'gas': 'Gás',
                        'eletricidade': 'Eletricidade'
                      };
                      
                      if (produtos.length === 0) {
                        return (
                          <span className="product-badge" title="Produto da Marcação">
                            Produto: N/A
                          </span>
                        );
                      }
                      
                      if (produtos.length === 1) {
                        const produto = produtos[0];
                        const produtoDisplay = produtoTitles[produto] || produto;
                        return (
                          <span className={`product-badge ${produto.replace(/\s+/g, '-').toLowerCase()}`} title="Produto da Marcação">
                            Produto: {produtoDisplay}
                          </span>
                        );
                      }
                      
                      // Multiple products: render as badges
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Produtos:</span>
                          {produtos.map((produto: string, index: number) => {
                            const produtoDisplay = produtoTitles[produto] || produto;
                            return (
                              <span 
                                key={index}
                                className={`product-badge ${produto.replace(/\s+/g, '-').toLowerCase()}`}
                                title={`Produto ${index + 1}: ${produtoDisplay}`}
                              >
                                {produtoDisplay}
                              </span>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <span className="comercial-badge" title="Comercial / Comerciais Responsáveis">
                      {(() => {
                        const userIds = formData.marcacaoUsers || [];
                        const userCount = Array.isArray(userIds) ? userIds.length : 0;
                        const userNames = Array.isArray(userIds) && userIds.length > 0
                          ? userIds.map((userId: string | number) => 
                              comercialUsers.find(u => u.id === String(userId))?.name
                            ).filter(Boolean).join(', ')
                          : formData.marcacaoUser
                            ? comercialUsers.find(u => u.id === String(formData.marcacaoUser))?.name || 'N/A'
                            : 'N/A';
                        const label = userCount > 1 || (!Array.isArray(userIds) && formData.marcacaoUser && !formData.marcacaoUsers) ? 'Comerciais' : 'Comercial';
                        return `${label}: ${userNames}`;
                      })()}
                    </span>
                  </div>
                </div>

                {/* Client Info - Most Important */}
                <div className="client-info-section">
                  <h4>👤 Cliente</h4>
                  <div className="client-details">
                    <div className="client-main">
                      <strong>{formData.nomeEmpresa || 'N/A'}</strong>
                      <span className="client-contact">{formData.contactoCliente || 'N/A'}</span>
                    </div>
                    <div className="client-responsible">
                      <span className="label">Responsável:</span> {formData.nomeResponsavel || 'N/A'}
                    </div>
                    <div className="client-address">
                      <span className="label">Morada:</span> {formData.morada || 'N/A'}
                    </div>
                    {(formData.codigoPostal1 || formData.codigoPostal2) && (
                      <div className="client-postal-code">
                        <span className="label">Código Postal:</span> {formData.codigoPostal1 || '0000'}-{formData.codigoPostal2 || '000'}
                      </div>
                    )}
                    {formData.linkGoogleMaps && (
                      <div className="client-maps-link">
                        <span className="label">Google Maps:</span> 
                        <a 
                          href={formData.linkGoogleMaps} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="maps-link"
                        >
                          Ver no Google Maps
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* ID de Marcação and Creator Information */}
                <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {/* ID de Marcação */}
                  {(() => {
                    const marcacaoId = formData?.marcacaoId || formData?.marcacao_id;
                    return marcacaoId ? (
                      <span className="marcacao-id-badge" title="ID da Marcação" style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e0e7ff',
                        border: '1px solid #a5b4fc',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#4338ca'
                      }}>
                        ID: {marcacaoId}
                      </span>
                    ) : null;
                  })()}
                  
                  {/* Creator Information */}
                  <span className="creator-badge" title="Criador da Marcação" style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Criado por: {(() => {
                      const createdBy = formData?.createdBy || formData?.created_by;
                      if (createdBy) {
                        const creatorId = String(createdBy);
                        const creator = comercialUsers.find(u => u.id === creatorId);
                        return creator?.name || `ID: ${createdBy}`;
                      }
                      return 'N/A';
                    })()}
                  </span>
                </div>

                {/* CallCenter Information - Combined Technical Details and Notes */}
                {/* Only show for CallCenter marcações, not for Comercial marcações */}
                {/* Hide this section if source is 'comercial' */}
                {formData && formData.source !== 'comercial' && (
                <div className="callcenter-info-section">
                  <h4 
                    className="callcenter-header"
                    onClick={() => setIsCallCenterInfoExpanded(!isCallCenterInfoExpanded)}
                  >
                    📞 Informação do CallCenter
                    <span className="marcacao-id-tag">
                      ID: {formData.marcacaoId || 'N/A'}
                    </span>
                    <span className="toggle-icon">
                      {isCallCenterInfoExpanded ? '▼' : '▶'}
                    </span>
                  </h4>
                  {isCallCenterInfoExpanded && (
                    <div className="callcenter-details">
                    {/* Technical Details */}
                    <div className="technical-details">
                      {formData.produto === 'bairro-solar' && (
                        <>
                          <div className="callcenter-field">
                            <span className="field-label">Já tem Paineis?:</span>
                            <span className={`field-value ${formData.jaTemPaineis === 'sim' ? 'yes' : 'no'}`}>
                              {formData.jaTemPaineis === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Potência 13.80Kw?:</span>
                            <span className={`field-value ${formData.potencia1035 === 'sim' ? 'yes' : 'no'}`}>
                              {formData.potencia1035 === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Trifásico?:</span>
                            <span className={`field-value ${formData.trifasico === 'sim' ? 'yes' : 'no'}`}>
                              {formData.trifasico === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Tipo de Telhado:</span>
                            <span className="field-value">{formData.tipoTelhado || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Fatura presente no dia da visita?:</span>
                            <span className={`field-value ${formData.faturaPresenteBairro === 'sim' ? 'yes' : 'no'}`}>
                              {formData.faturaPresenteBairro === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                        </>
                      )}
                      
                      {formData.produto === 'pap' && (
                        <>
                          <div className="callcenter-field">
                            <span className="field-label">Já tem Paineis?:</span>
                            <span className={`field-value ${formData.jaTemPaineisPAP === 'sim' ? 'yes' : 'no'}`}>
                              {formData.jaTemPaineisPAP === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Potência 6.9Kw?:</span>
                            <span className={`field-value ${formData.potencia69 === 'sim' ? 'yes' : 'no'}`}>
                              {formData.potencia69 === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Trifásico?:</span>
                            <span className={`field-value ${(formData.trifasicoPAP || formData.trifasico) === 'sim' ? 'yes' : 'no'}`}>
                              {(formData.trifasicoPAP || formData.trifasico) === 'sim' ? 'Sim' : (formData.trifasicoPAP || formData.trifasico) === 'nao' ? 'Não' : (formData.trifasicoPAP || formData.trifasico) === 'nao_sabe' ? 'Não Sabe' : 'N/A'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Tipo de Telhado:</span>
                            <span className="field-value">{formData.tipoTelhadoPAP || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Paga mais que 250€?:</span>
                            <span className={`field-value ${formData.pagaMais250 === 'sim' ? 'yes' : 'no'}`}>
                              {formData.pagaMais250 === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Fatura presente no dia da visita?:</span>
                            <span className={`field-value ${formData.faturaPresentePAP === 'sim' ? 'yes' : 'no'}`}>
                              {formData.faturaPresentePAP === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                        </>
                      )}
                      
                      {formData.produto === 'gas' && (
                        <>
                          <div className="callcenter-field">
                            <span className="field-label">Gás natural e Canalizado?:</span>
                            <span className={`field-value ${formData.gasNatural ? 'yes' : 'no'}`}>
                              {formData.gasNatural ? 'Confirmo' : 'Não confirmo'}
                            </span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Comercializadora Atual:</span>
                            <span className="field-value">{formData.comercializadoraGas || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Valor da Última Fatura:</span>
                            <span className="field-value">{formData.valorFaturaGas || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Fatura Presente no Dia da Reunião?:</span>
                            <span className={`field-value ${formData.faturaPresenteGas === 'sim' ? 'yes' : 'no'}`}>
                              {formData.faturaPresenteGas === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                        </>
                      )}
                      
                      {formData.produto === 'eletricidade' && (
                        <>
                          <div className="callcenter-field">
                            <span className="field-label">Comercializadora Atual:</span>
                            <span className="field-value">{formData.comercializadoraEletricidade || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Valor da Última Fatura:</span>
                            <span className="field-value">{formData.valorFaturaEletricidade || 'N/A'}</span>
                          </div>
                          <div className="callcenter-field">
                            <span className="field-label">Fatura Presente no Dia da Reunião?:</span>
                            <span className={`field-value ${formData.faturaPresenteEletricidade === 'sim' ? 'yes' : 'no'}`}>
                              {formData.faturaPresenteEletricidade === 'sim' ? 'Sim' : 'Não'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Notes if any */}
                    {formData.marcacaoDescription && (
                      <div className="callcenter-field notes-field">
                        <span className="field-label">Observações:</span>
                        <span className="field-value">{formData.marcacaoDescription}</span>
                      </div>
                    )}
                    </div>
                  )}
                </div>
                )}
              </div>
            ) : isBloqueio && formData ? (
              <div className="bloqueio-details-compact">
                {/* Header with key info */}
                <div className="event-header-info">
                  <div className="event-time-info">
                    <span className="time-badge" title="Horário: 08:00 - 09:00">Horário: {formData.horaInicio} - {formData.horaFim}</span>
                    <span className="date-badge" title="Data: 01/10/2025">Data: {formData.marcacaoData}</span>
                  </div>
                  <div className="event-product-info">
                    <span className="bloqueio-badge" title="Tipo de Bloqueio">
                      Tipo: Bloqueio de Horário
                    </span>
                    <span className="comercial-badge" title="Comerciais Afetados">
                      {(() => {
                        const userIds = formData.marcacaoUsers || [];
                        const userNames = Array.isArray(userIds) && userIds.length > 0
                          ? userIds.map((userId: string | number) => 
                              comercialUsers.find(u => u.id === String(userId))?.name
                            ).filter(Boolean).join(', ')
                          : 'N/A';
                        return `Comerciais: ${userNames}`;
                      })()}
                    </span>
                  </div>
                </div>

                {/* Bloqueio Information */}
                <div className="bloqueio-info-section">
                  <h4>🚫 Informação do Bloqueio</h4>
                  <div className="bloqueio-details">
                    <div className="bloqueio-main">
                      <div className="bloqueio-field">
                        <span className="field-label">ID do Bloqueio:</span>
                        <span className="field-value">{formData.marcacaoId || 'N/A'}</span>
                      </div>
                      <div className="bloqueio-field">
                        <span className="field-label">Comerciais Afetados:</span>
                        <span className="field-value">
                          {(() => {
                            const userIds = formData.marcacaoUsers || [];
                            if (Array.isArray(userIds) && userIds.length > 0) {
                              const userNames = userIds.map((userId: string | number) => 
                                comercialUsers.find(u => u.id === String(userId))?.name
                              ).filter(Boolean);
                              return userNames.length > 0 ? userNames.join(', ') : 'N/A';
                            }
                            return 'N/A';
                          })()}
                        </span>
                      </div>
                      <div className="bloqueio-field">
                        <span className="field-label">Data:</span>
                        <span className="field-value">{formData.marcacaoData || 'N/A'}</span>
                      </div>
                      <div className="bloqueio-field">
                        <span className="field-label">Hora de Início:</span>
                        <span className="field-value">{formData.horaInicio || 'N/A'}</span>
                      </div>
                      <div className="bloqueio-field">
                        <span className="field-label">Hora de Fim:</span>
                        <span className="field-value">{formData.horaFim || 'N/A'}</span>
                      </div>
                      {formData.marcacaoDescription && (
                        <div className="bloqueio-field">
                          <span className="field-label">Descrição:</span>
                          <span className="field-value">{formData.marcacaoDescription}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Fallback for non-marcação events
              <div className="event-details">
                <div className="event-detail">
                  <strong>Horário:</strong> {selectedEvent.time} - {selectedEvent.duration}
                </div>
                <div className="event-detail">
                  <strong>Tipo:</strong> {selectedEvent.type}
                </div>
                {selectedEvent.description && (
                  <div className="event-detail">
                    <strong>Descrição:</strong> {selectedEvent.description}
                  </div>
                )}
                {selectedEvent.attendees && (
                  <div className="event-detail">
                    <strong>Participantes:</strong> {selectedEvent.attendees.join(', ')}
                  </div>
                )}
                {selectedEvent.location && (
                  <div className="event-detail">
                    <strong>Local:</strong> {selectedEvent.location}
                  </div>
                )}
                <div className="event-detail">
                  <strong>Estado:</strong> {selectedEvent.status}
                </div>
              </div>
            )}
            
            <div className="event-actions">
              {/* For Marcação events, only Gerente in comercial tenant can edit/delete */}
              {/* For other events, use the standard canEditEvents check */}
              {(() => {
                const canEdit = (isMarcacao && canEditMarcacaoEvents()) || (!isMarcacao && canEditEvents());
                return canEdit;
              })() && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => {
                      // TODO: Implement edit functionality
                      alert('Funcionalidade de edição em desenvolvimento');
                    }}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#8b5cf6', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                      marginRight: '10px'
                    }}
                  >
                    Editar
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => {
                      // TODO: Implement delete functionality
                      if (window.confirm('Tem a certeza que deseja eliminar este evento?')) {
                        alert('Funcionalidade de eliminação em desenvolvimento');
                      }
                    }}
                    style={{ 
                      padding: '10px 20px', 
                      backgroundColor: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                      marginRight: '10px'
                    }}
                  >
                    Eliminar
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={closeEventModal}>Fechar</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if user has access - allow all Comercial users (tenant = comercial)
  // For regular Comercial users, they can only see their own calendar
  // For Admin and Gerente, they see the full calendar with sidebar
  const hasAccess = () => {
    try {
      const tenant = localStorage.getItem('user_tenant') || '';
      const tenantLower = tenant.toLowerCase();
      
      // Allow access if tenant is comercial or admin
      return tenantLower === 'comercial' || tenantLower === 'admin';
    } catch {
      return false;
    }
  };

  // Check access before rendering
  if (!hasAccess()) {
    return (
      <div className="comercial-calendar">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#ef4444' }}>Acesso Negado</h2>
          <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
            Esta página está disponível apenas para utilizadores do tenant Comercial.
          </p>
        </div>
      </div>
    );
  }

  // When modalOnly (used by Gestão das Marcações): render only the exact same event detail modal + backdrop
  if (modalOnly && showMarcacaoEventDetailModal && selectedMarcacaoEvent) {
    return (
      <>
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => {
            if (!showPropostasMarcacaoModal) {
              setShowMarcacaoEventDetailModal(false);
              setSelectedMarcacaoEvent(null);
              onCloseModal?.();
            }
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', maxHeight: '90vh' }}>
            {renderMarcacaoEventDetailModal()}
          </div>
        </div>
        {renderPropostasMarcacaoModal()}
      </>
    );
  }

  return (
    <div className="comercial-calendar">
      {searchInProgress && (
        <div
          className="marcacao-notification"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 100001,
            padding: '12px 24px',
            borderRadius: '8px',
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          🔍 A pesquisar no calendário...
        </div>
      )}
      {/* Drawer Sidebar - show for Admin, Gerente, and Gestor users */}
      {(isAdminOrGerenteComercial() || canViewComercialUserSidebar()) && renderSidebar()}

      <div className="calendar-main">
        {renderCalendarHeader()}
        {renderSubHeader()}
        
        <div className="calendar-content">
          {(!selectedUser && isAdminOrGerenteComercial()) ? (
            <div className="empty-calendar-state">
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h3 className="empty-state-title">Selecione um utilizador</h3>
              <p className="empty-state-description">
                Escolha um utilizador comercial na barra lateral para visualizar o seu calendário
              </p>
            </div>
          ) : isLoadingEvents ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px',
              width: '100%'
            }}>
              <WonderTradeLoader message="A carregar eventos..." />
            </div>
          ) : (
            <>
              {displayMode === 'calendar' && (
                <>
                  {viewMode === 'daily' && renderDailyView()}
                  {viewMode === 'weekly' && renderWeeklyView()}
                  {viewMode === 'monthly' && renderMonthlyView()}
                </>
              )}
              {displayMode === 'table' && renderTableView()}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {renderDatePicker()}
      {renderYearPicker()}
      {renderEventModal()}
      {renderStatusEventModal()}
      {renderStatusEventDetailModal()}
      {renderRotaEventDetailModal()}
      {renderMarcacaoEventDetailModal()}
      {renderPropostasMarcacaoModal()}
      {renderMarcacaoDeleteConfirmModal()}
      {renderScenarioDeleteConfirmModal()}
      {renderRotaDeleteConfirmModal()}
      {renderEspecialidadeModal()}
      {renderBloqueioModal()}
      {renderMarcacaoModal()}
      {renderFechoModal()}
      {renderRotaModal()}
      
    </div>
  );
};

export default ComercialCalendar;
